import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { tokens } from '@fluentui/react-components';
import { Box, Html } from '@react-three/drei';
import OuterDoors from './OuterDoors';
import Measurements from './Measurements';
import { getGrainBumpTexture } from '../utils/grainTexture';
import { isNearPanelEdge, isNearStripCenter, HoverEdgeFrame, PANEL_HOVER_COLOR, PanelDepthTooltip, EDGE_MARGIN_M_STICKY, EDGE_MARGIN_M } from '../utils/edgeHover.jsx';

import {
  ClothesRod, DoubleClothesRod, VerticalDivider, Drawer,
  SideDrawerBlock, DoubleDrawer, PullOutHanger, Pantograph,
  AdjustableShelf, ShelfWithRails, DrawerBackPanel
} from './InternalComponents';

// Half-gap pulled back from a door's visible face on any edge that borders another
// SECTION (not the cabinet's outer wall) — two independent OuterDoors instances
// (e.g. one stacked above another, or a neighboring column) each extend halfway
// into their shared divider by design, so without this they meet with zero visible
// separation. 0.75mm here + 0.75mm from the neighboring door = 1.5mm total,
// matching the gap DoubleDoorGroup uses between its own two leaves (OuterDoors.jsx).
const DOOR_EDGE_GAP = 0.00075;

// innerDoorFacadeExtend (žr. žemiau) VIDINIAM kraštui grąžina lygiai arba
// safeWallThickness (daliklis "Ištraukta į priekį") arba safeWallThickness/2 (ne) —
// šis slenkstis atskiria tuos du atvejus skaičiuojant uEdgeGap/bEdgeGap (modulio
// lygmeniu, kad būtų pasiekiamas IŠ ABIEJŲ — sujungtų IR solo durelių — forEach
// callback'ų, kurie yra SESERIŠKI, ne vienas kitame įdėti, tad vietinis const
// vienoje šakoje nebūtų matomas kitoje).
const FACADE_EXTEND_EPS = 0.0001;

// 8cm — the one true minimum leaf size for ANY drag/resize/type-in interaction on a
// structure tree divider, at any nesting depth or sibling count.
const MIN_LEAF_SIZE = 0.08;

// Conservative (never-too-small) floor for an ENTIRE Vidus interior structure nested
// inside an outer leaf — sum of every interior leaf's own MIN_LEAF_SIZE plus one
// wallThickness gap between each pair, regardless of each nested split's own axis
// (same simplifying approximation getNearAdjustableMin below already makes for the
// outer tree — axis-perfect would need to track the outer drag axis through every
// nested split, this never UNDER-estimates the true minimum, which is what actually
// matters for a safety floor).
const getInteriorStructureMinSize = (node, wallThickness) => {
  if (!node || !node.split || !node.children) return MIN_LEAF_SIZE;
  const childMins = node.children.map((c) => getInteriorStructureMinSize(c, wallThickness));
  return childMins.reduce((a, b) => a + b, 0) + wallThickness * (node.children.length - 1);
};

// The TRUE minimum `node` can shrink to from its CURRENT absolute size (currentAbsSize), given
// that only the ONE leaf nearest the dragged boundary may shrink (descend via first child if
// absorbFirst, else last child, all the way down to MIN_LEAF_SIZE) while every OTHER descendant
// keeps its EXACT current size untouched. This is the real floor a drag/type-in clamp must
// respect — getSubtreeMinSize alone is not enough, because it assumes every leaf could shrink,
// but the rescale rule only ever lets ONE bordering leaf absorb a change.
//
// interiorStructure/nodePath/wallThickness (all optional, backward-compatible with existing
// callers that don't pass them) let this also respect a LEAF's own Vidus interior shelving —
// previously a leaf's floor was ALWAYS the flat MIN_LEAF_SIZE (8cm) no matter what was inside
// it, so dragging the OUTER (Išorė) boundary of a section that already had its own interior
// shelves/dividers could shrink it well below what those interior leaves actually needed,
// squeezing them into/through each other (žr. /admin/bugs "Su pelyte neturi buti galimybes
// nustumti plokstes zemiau, anksciau veike gerai").
const getNearAdjustableMin = (node, currentAbsSize, absorbFirst, interiorStructure = null, nodePath = null, wallThickness = 0.018) => {
  if (!node || !node.children || node.children.length < 2) {
    const innerNode = nodePath ? interiorStructure?.[nodePath] : null;
    if (innerNode && innerNode.split) {
      return Math.max(MIN_LEAF_SIZE, getInteriorStructureMinSize(innerNode, wallThickness));
    }
    return MIN_LEAF_SIZE;
  }
  const n = node.children.length;
  const nearIdx = absorbFirst ? 0 : n - 1;
  const nearFraction = node.sizes
    ? node.sizes[nearIdx]
    : (nearIdx === 0 ? (node.ratio ?? 0.5) : 1 - (node.ratio ?? 0.5));
  const nearCurrentAbs = nearFraction * currentAbsSize;
  const farTotalAbs = currentAbsSize - nearCurrentAbs;
  const nearPath = nodePath ? `${nodePath}-${nearIdx}` : null;
  return farTotalAbs + getNearAdjustableMin(node.children[nearIdx], nearCurrentAbs, absorbFirst, interiorStructure, nearPath, wallThickness);
};

// ── getSectionBounds ─────────────────────────────────────────────────────────
// Returns absolute {x, y, width, height} of a section within the inner cabinet
// Supports variable split ratio stored in each split node.
const getSectionBounds = (structure, path, innerWidth, innerHeight, wallThickness) => {
  const parts = path.split('-').slice(1);
  let node = structure || {};
  let x = 0, y = 0, w = innerWidth, h = innerHeight;
  for (const part of parts) {
    const i = parseInt(part);
    if (!node.split || !node.children) break;
    const isH = node.split === 'h';
    const N = node.children.length;
    if (N === 2) {
      const ratio = node.ratio ?? 0.5;
      const available = isH ? h - wallThickness : w - wallThickness;
      const child0Size = available * ratio;
      const child1Size = available * (1 - ratio);
      if (isH) {
        if (i === 0) { y += child1Size + wallThickness; h = child0Size; }
        else { h = child1Size; }
      } else {
        if (i === 1) { x += child0Size + wallThickness; w = child1Size; }
        else { w = child0Size; }
      }
    } else {
      const sizes = node.sizes || Array(N).fill(1 / N);
      const available = isH ? h - wallThickness * (N - 1) : w - wallThickness * (N - 1);
      const childAbsSizes = sizes.map(s => s * available);
      if (isH) {
        let offset = 0;
        for (let j = N - 1; j > i; j--) offset += childAbsSizes[j] + wallThickness;
        y += offset;
        h = childAbsSizes[i];
      } else {
        let offset = 0;
        for (let j = 0; j < i; j++) offset += childAbsSizes[j] + wallThickness;
        x += offset;
        w = childAbsSizes[i];
      }
    }
    node = node.children[i];
  }
  return { x, y, width: w, height: h };
};

const getLeafPaths = (node, path = '0') => {
  if (!node || !node.children) return [path];
  return node.children.flatMap((child, i) => getLeafPaths(child, `${path}-${i}`));
};

// ── findLeafResizeAncestor ───────────────────────────────────────────────────
// A leaf's own size can only trade space with a sibling that shares a DIRECT split
// with it. When sections were built by splitting different branches independently
// (e.g. top half → [A,B], bottom half → [C,D]) rather than always splitting the last
// leaf in a chain, a leaf like B has no direct sibling on the side facing its actual
// visual neighbor C — C lives one level up, on the other side of the ROOT split. This
// walks from the leaf up to the root looking for the nearest split (matching `axis`)
// where the leaf's branch isn't the very first/last child, mirroring the simple n-ary
// "i+1, else i-1" convention up through arbitrarily nested trees instead of stopping
// at the leaf's immediate parent. Preference: nearest available "next" neighbor
// anywhere in the chain (deepest first), else nearest available "previous" neighbor.
const findLeafResizeAncestor = (rootNode, leafPath, axis, rootWidth, rootHeight, wallThickness, onlyDirection = null) => {
  const parts = leafPath.split('-').slice(1).map(Number);
  const chain = [];
  let node = rootNode || {};
  let w = rootWidth, h = rootHeight;
  for (const i of parts) {
    if (!node.split || !node.children) break;
    const isH = node.split === 'h';
    const N = node.children.length;
    const available = isH ? h - wallThickness * (N - 1) : w - wallThickness * (N - 1);
    chain.push({ node, childIdx: i, available, isH });
    if (N === 2) {
      const ratio = node.ratio ?? 0.5;
      const c0 = available * ratio, c1 = available * (1 - ratio);
      if (isH) h = i === 0 ? c0 : c1; else w = i === 0 ? c0 : c1;
    } else {
      const sizes = node.sizes || Array(N).fill(1 / N);
      const abs = sizes.map(s => s * available);
      if (isH) h = abs[i]; else w = abs[i];
    }
    node = node.children[i];
  }
  const tryDirection = (wantNext) => {
    for (let level = chain.length - 1; level >= 0; level--) {
      const { node: ancestor, childIdx, available, isH } = chain[level];
      if ((isH ? 'h' : 'v') !== axis) continue;
      const N = ancestor.children.length;
      const neighborIdx = wantNext ? childIdx + 1 : childIdx - 1;
      if (neighborIdx >= 0 && neighborIdx < N) {
        return { ancestorNode: ancestor, ancestorPathParts: parts.slice(0, level), childIdx, neighborIdx, available };
      }
    }
    return null;
  };
  // onlyDirection lets a caller that cares about ONE specific physical edge (e.g. "the
  // divider to my left") ask for exactly that direction instead of the resize-handler's
  // "nearest available neighbor, prefer next" fallback (see innerDoorFacadeExtend below).
  if (onlyDirection === 'next') return tryDirection(true);
  if (onlyDirection === 'prev') return tryDirection(false);
  return tryDirection(true) || tryDirection(false);
};

// ── innerDoorFacadeExtend ───────────────────────────────────────────────────
// Bilietas '3.0 Išorė. "Ištraukti į priekį" veikia, bet durelės viską uždengia' — case 2
// (vidinės karkaso plokštės): pastūmus VIDINĘ pertvarą/daliklį į priekį ("Ištraukti į
// priekį"), abi besiribojančios durelės turi atsitraukti, kad pastumta pertvara taptų
// matoma tarp jų, o ne liktų durų uždengta lygiai taip pat, kaip prieš pastūmimą. Randa
// pertvarą/daliklį, kuris šiai lentynai/sekcijai ribojasi TIKSLIAI nurodytu kraštu
// (naudoja tą pačią su findLeafResizeAncestor bendrą medžio-eigos logiką, kad veiktų ir
// kai sekcijos sukurtos ne iš eilės), ir patikrina jo panelFrontOffsets 'facade' būseną —
// ta pati raktų formulė, kokią naudoja DividerDragHandle/NarySectionDivider patys.
//
// PATAISYTA (geometrinė klaida, rasta vartotojo tiesiogiai nurodytu principu — "jeigu du
// elementai turi plotį/gylį/ilgį, vienas negali užimti tų pačių koordinačių, kaip kitas"):
// getSectionBounds (žr. aukščiau) tarp dviejų gretimų lapų PALIEKA LYGIAI wallThickness
// pločio tarpą (child0 baigiasi ties child0Size, child1 prasideda ties child0Size+
// wallThickness) — tiksliai tiek, kad daliklis (pats wallThickness pločio) jį UŽPILDYTŲ
// VISIŠKAI, be jokios papildomos paraštės. Anksčiau ČIA (isFacade atveju) buvo grąžinama
// PILNAS wallThickness — kiekviena durelė AUGDAVO per VISĄ tarpo plotį SAVO KRYPTIMI, t.y.
// abi durelės kartu PERSIDENGDAVO per VISĄ tarpo plotį (ne tik su pačiu dalikliu, bet IR
// VIENA SU KITA — patikrinta skaičiais, žr. node -e patikrinimą prieš šį commit'ą) — vizualiai
// "durelės užlipa ant vertikalios atskyrimo plokštės", o ne tiesiog ja atsiremia. Dalikliui
// TAPUS matomam (isFacade, ta pati Z kaip ir durelių), 0 yra VIENINTELĖ reikšmė, kuri durelę
// sustabdo LYGIAI ties savo pačios sekcijos riba — t.y. TIKSLIAI ties daliklio artimiausiu
// paviršiumi, be jokio persidengimo (90° atsirėmimas, ne įsiskverbimas). Kai daliklis NĖRA
// isFacade (kitame, giliau pasislinkusiame Z), wallThickness/2 lieka teisingas — durelės
// SUSITINKA tarpo VIDURYJE skirtingame Z nei daliklis, tad 2D (X/Y) persidengimas su JUO
// (ne viena su kita) yra įprastas/numatytas — taip durelė uždengia/paslepia visa, kas yra UŽ
// jos, lygiai taip pat, kaip bet kuri uždaryta spintos durelė natūraliai dengia karkasą už savęs.
const innerDoorFacadeExtend = (rootNode, leafPath, axis, direction, rootWidth, rootHeight, wallThickness, sectionKeyPrefix, panelFrontOffsets) => {
  const found = findLeafResizeAncestor(rootNode, leafPath, axis, rootWidth, rootHeight, wallThickness, direction);
  // ClickUp "3.0 Išorė. Durelių matmenys... išorinės karkaso dalys. Durelės turi būti
  // platesnės ir aukštesnės" — `!found` reiškia ŠIS kraštas NETURI kaimyninės sekcijos/
  // daliklio ta kryptimi, t.y. tai TIKRAS išorinis korpuso kraštas (siena), NE vidinis
  // tarpas tarp dviejų durelių. wallThickness/2 (toliau žemiau, kai `found` TIESA)
  // teisingas TIK vidiniam atvejui — ten DVI durelės susitinka VIDURYJE bendro tarpo,
  // kiekviena augdama per PUSĘ. Čia durelė VIENA, o siena — PILNO wallThickness storio
  // plokštė, kurią durelė turi uždengti NUO VIDINIO IKI IŠORINIO paviršiaus (standartinė
  // "uždengiančių" — overlay — durelių konstrukcija), tad PILNAS wallThickness, ne pusė.
  if (!found) return wallThickness;
  const { ancestorNode, ancestorPathParts, childIdx, neighborIdx } = found;
  const dividerPath = ancestorPathParts.length ? `0-${ancestorPathParts.join('-')}` : '0';
  const N = ancestorNode.children ? ancestorNode.children.length : 2;
  const key = N === 2
    ? `${sectionKeyPrefix}-${dividerPath}`
    : `${sectionKeyPrefix}-${dividerPath}-nd${Math.min(childIdx, neighborIdx)}`;
  const isFacade = panelFrontOffsets?.[key] === 'facade';
  return isFacade ? 0 : wallThickness / 2;
};

// ── subtreeHasDoor / wallBordersDoor ────────────────────────────────────────
// Long-term fix for contour/door z-fighting ("kontūrai kartais persimato per dureles"):
// a door genuinely occupies the front wallThickness slice of the cabinet's depth, so any
// carcass panel/divider it covers should sit that much further back by default — not at
// the exact same depth as the door, which is what caused the z-fighting in the first place.
// These two helpers answer "does a door cover this panel" so panels can auto-recess only
// where that's actually true (see the `bordersDoor`-driven depth math in CabinetPanel and
// DividerDragHandle/NarySectionDivider below).
const subtreeHasDoor = (node, path, doorColors, keyPrefix) => {
  if (!doorColors) return false;
  return getLeafPaths(node, path).some(leafPath => !!doorColors[`${keyPrefix}${leafPath}`]);
};

const wallBordersDoor = (structure, edge, innerW, innerH, wallThickness, doorColors, keyPrefix) => {
  if (!doorColors || !structure) return false;
  const EPS = 0.001;
  return getLeafPaths(structure).some(leafPath => {
    if (!doorColors[`${keyPrefix}${leafPath}`]) return false;
    const b = getSectionBounds(structure, leafPath, innerW, innerH, wallThickness);
    if (!b) return false;
    if (edge === 'top') return b.y + b.height >= innerH - EPS;
    if (edge === 'bottom') return b.y <= EPS;
    if (edge === 'left') return b.x <= EPS;
    if (edge === 'right') return b.x + b.width >= innerW - EPS;
    return false;
  });
};

// ── commitFacadeSectionResize ───────────────────────────────────────────────
// Same "find whichever ancestor really owns the boundary, trade space only with the
// immediate visual neighbor, respecting each side's true minimum" logic as the inner
// leaf badge's onCommit — reused here so the Išorė "Fasadų aukščiai/pločiai" facade
// badges (rendered in the doors pass, outside the RecursiveSection tree) can resize
// the underlying section too, not just display a read-only number. resizeBinary/
// resizeNary are expected to already be bound to the right furnitureId/cabinetIndex.
const commitFacadeSectionResize = (cabinetStructure, path, axis, oldValMeters, newValMeters, availW, availH, wallThickness, resizeBinary, resizeNary) => {
  const leafTarget = findLeafResizeAncestor(cabinetStructure, path, axis, availW, availH, wallThickness);
  if (!leafTarget) return false;
  const { ancestorNode, ancestorPathParts, childIdx, neighborIdx, available } = leafTarget;
  const ancestorPath = '0' + (ancestorPathParts.length ? '-' + ancestorPathParts.join('-') : '');
  const N = ancestorNode.children.length;
  const sizesArr = ancestorNode.sizes || (N === 2 ? [ancestorNode.ratio ?? 0.5, 1 - (ancestorNode.ratio ?? 0.5)] : Array(N).fill(1 / N));
  const nearOldAbs = sizesArr[childIdx] * available;
  const neighborOldAbs = sizesArr[neighborIdx] * available;
  const neighborAbsorbFirst = neighborIdx > childIdx;
  const ownAbsorbFirst = !neighborAbsorbFirst;
  const nearBranchMin = getNearAdjustableMin(ancestorNode.children[childIdx], nearOldAbs, ownAbsorbFirst);
  const neighborMin = getNearAdjustableMin(ancestorNode.children[neighborIdx], neighborOldAbs, neighborAbsorbFirst);
  const desiredLeafAbs = Math.max(MIN_LEAF_SIZE, newValMeters);
  const delta = desiredLeafAbs - oldValMeters;
  let newNearAbs = nearOldAbs, newNeighborAbs = neighborOldAbs;
  if (delta > 0) {
    const room = Math.max(0, neighborOldAbs - neighborMin);
    const take = Math.min(delta, room);
    newNeighborAbs = neighborOldAbs - take;
    newNearAbs = nearOldAbs + take;
  } else if (delta < 0) {
    const room = Math.max(0, nearOldAbs - nearBranchMin);
    const take = Math.min(-delta, room);
    newNearAbs = nearOldAbs - take;
    newNeighborAbs = neighborOldAbs + take;
  }
  if (ancestorNode.sizes || N > 2) {
    const newSizes = sizesArr.map((s, idx) => {
      if (idx === childIdx) return newNearAbs / available;
      if (idx === neighborIdx) return newNeighborAbs / available;
      return s;
    });
    if (resizeNary) resizeNary(ancestorPath, newSizes);
  } else {
    const newRatioFor0 = (childIdx === 0 ? newNearAbs : newNeighborAbs) / available;
    if (resizeBinary) resizeBinary(ancestorPath, newRatioFor0);
  }
  return true;
};

// ── ClickInput ───────────────────────────────────────────────────────────────
// ── MeasureTooltip ────────────────────────────────────────────────────────────
const MeasureTooltip = ({ label }) => (
  <div style={{
    position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
    transform: 'translateX(-50%)', background: '#1e293b', color: '#f8fafc',
    fontSize: 10, fontWeight: 500, padding: '3px 8px', borderRadius: 5,
    whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 9999,
    boxShadow: '0 4px 10px rgba(0,0,0,0.25)', fontFamily: 'sans-serif',
  }}>
    {label}
    <div style={{
      position: 'absolute', top: '100%', left: '50%',
      transform: 'translateX(-50%)', width: 0, height: 0,
      borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
      borderTop: '4px solid #1e293b',
    }} />
  </div>
);

// #17: detect touch device once
const _isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

// Reusable click-to-edit input matching the Measurements component pattern.
const ClickInput = ({ value, onCommit, borderColor = '#ccc', label = '', title = '', editable = true, bgColor = '#000', textColor = '#fff' }) => {
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState('');
  // #17: on touch devices use click-toggle instead of hover
  const [hovered, setHovered] = useState(false);

  const cmVal = (value * 100).toFixed(1);

  const handleBlur = () => {
    setEditing(false);
    const parsed = parseFloat(localVal);
    if (!isNaN(parsed) && parsed > 0 && onCommit) onCommit(parsed / 100);
  };

  const hoverProps = _isTouchDevice
    ? { onClick: () => { if (!editing) setHovered(h => !h); } }
    : { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) };

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}
      {...hoverProps}
    >
    <div
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        background: bgColor,
        padding: '1px 3px',
        border: 'none',
        outline: 'none',
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: editable ? (editing ? 'text' : 'pointer') : 'default',
        pointerEvents: 'auto',
        minWidth: 35,
        height: 18,
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
      onClick={editable && !editing ? () => { setEditing(true); setLocalVal(cmVal); } : undefined}
    >
      {editing ? (
        <input
          autoFocus
          type="text"
          className="fitit-measure-input"
          value={localVal}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '' || /^\d*\.?\d*$/.test(v)) setLocalVal(v);
          }}
          onFocus={(e) => e.target.select()}
          onBlur={handleBlur}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          style={{
            width: 35,
            border: 'none',
            outline: 'none',
            textAlign: 'center',
            fontSize: 11,
            fontWeight: 500,
            color: textColor,
            background: 'transparent',
            padding: 0,
            margin: 0,
            fontFamily: 'sans-serif',
            MozAppearance: 'textfield',
            WebkitAppearance: 'none',
          }}
        />
      ) : (
        <span style={{ fontSize: 11, fontWeight: 500, color: textColor }}>{cmVal}</span>
      )}
    </div>
    {hovered && title && <MeasureTooltip label={title} />}
    </div>
  );
};

const overlayWrapStyle = { display: 'flex', gap: '6px', pointerEvents: 'auto' };

const OverlayBtn = ({ onClick, onPointerDown, title, children, grab = false }) => {
  const [hov, setHov] = React.useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerEnter={() => setHov(true)}
        onPointerLeave={() => setHov(false)}
        style={{
          border: 'none', borderRadius: tokens.borderRadiusCircular, cursor: grab ? 'ns-resize' : 'pointer',
          width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'auto', userSelect: 'none', flexShrink: 0,
          background: '#fff', color: hov ? '#3b82f6' : '#111',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          transition: 'color 0.15s',
        }}
      >
        {children}
      </button>
      {title && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 7px)', left: '50%',
          transform: hov ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(4px)',
          background: '#111', color: '#fff', padding: '2px 7px', borderRadius: 4,
          fontSize: 10, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 9999,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          opacity: hov ? 1 : 0,
          transition: 'opacity 0.18s, transform 0.18s',
        }}>
          {title}
          <div style={{
            position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
            borderTop: '4px solid #111',
          }} />
        </div>
      )}
    </div>
  );
};

// ── ShelfDragOverlay ─────────────────────────────────────────────────────────
// Each shelf board shows Move/Delete buttons on click. Move button: press-and-hold to drag.
const ShelfDragOverlay = ({ shelves, shelfSections, cabWidth, safeDepth, safeWallThickness, currentColor, activeStep, onShelfHeightChange, onShelfDelete, visualBaseHeight, hasRightWall, selectedIdx, onSelectShelf, showEdges = false }) => {
  const dragRef = useRef(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const { camera, size } = useThree();

  const isInteractive = activeStep === 'Baldas' || activeStep === 'Vidus';

  return (
    <>
      {shelves.map((shelf, sortedIdx) => {
        const sPos = Number(shelf.position[1]);
        if (isNaN(sPos)) return null;
        // .find by .index (ne pozicinis [sortedIdx]) — shelfSections PRALEIDŽIA beveik
        // nulinio aukščio sekcijas (žr. jos apibrėžimą), tad jos masyvo pozicija gali
        // NESUTAPTI su lentynos sortedIdx, jei kuri nors ANKSTESNĖ sekcija buvo praleista
        // (pvz. dvi lentynos jau arti viena kitos) — dėl to vilkimas gaudavo NETEISINGOS
        // lentynos startHeight (žr. /admin/bugs "Su pelyte neturi buti galimybes nustumti
        // plokstes zemiau, anksciau veike gerai").
        const sec = shelfSections.find((s) => s.index === sortedIdx);
        const isSelected = selectedIdx === sortedIdx;
        const isHovered = hoveredIdx === sortedIdx;
        const shelfW = hasRightWall
          ? Math.max(0.001, cabWidth - safeWallThickness * 2)
          : Math.max(0.001, cabWidth - safeWallThickness);
        const shelfX = hasRightWall ? cabWidth / 2 : (safeWallThickness + cabWidth) / 2;
        // Depth: flush with the true facade plane at the front (safeDepth), inset by
        // wallThickness only at the back (against the nugarėlė's inner face) — mirrors
        // how the carcass walls themselves now ALWAYS sit (CabinetPanel's depthDelta=0,
        // "outer frame flush default" fix). Before this, the shelf still used the OLD
        // symmetric inset (safeDepth - wallThickness*2, centered), which matched the
        // walls back when THEY also receded by wallThickness — now that the walls sit
        // flush at safeDepth while the shelf didn't follow, the shelf's front edge sat
        // exactly one wallThickness behind the wall's front contour line. Invisible
        // head-on; a real, measurable parallax gap at an angle / zoomed out (vartotojas:
        // "labai priartinus... koordinates sutampa, kodel atitolinus matosi tarpas?").
        const shelfD = Math.max(0.001, safeDepth - safeWallThickness);
        const shelfZ = (safeWallThickness + safeDepth) / 2;

        const startMoveDrag = (e) => {
          if (e.button !== 0) return; // tik kairysis mygtukas — dešinysis nevilkina lentynos
          if (!sec || !onShelfHeightChange) return;
          const fovRad = camera.fov * Math.PI / 180;
          const ppm = (size.height / 2) / (camera.position.length() * Math.tan(fovRad / 2));
          dragRef.current = { startY: e.clientY, startHeight: sec.height, idx: sortedIdx, ppm };
          document.body.style.cursor = 'grabbing';
          // Batch to at most one update per animation frame — smoother than dispatching on
          // every raw pointermove event (which can fire much faster than the screen repaints).
          let rafId = null;
          let latestY = e.clientY;
          const applyFrame = () => {
            rafId = null;
            if (!dragRef.current) return;
            const dy = (dragRef.current.startY - latestY) / dragRef.current.ppm;
            onShelfHeightChange(dragRef.current.idx, Math.max(0.05, dragRef.current.startHeight + dy));
          };
          const onMove = (me) => {
            if (!dragRef.current) return;
            latestY = me.clientY;
            if (rafId == null) rafId = requestAnimationFrame(applyFrame);
          };
          const onUp = () => {
            if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
            dragRef.current = null;
            document.body.style.cursor = '';
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
          };
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
        };

        return (
          <group key={sortedIdx}>
            <Box
              position={[shelfX, sPos, shelfZ]}
              args={[shelfW, safeWallThickness, shelfD]}
              castShadow receiveShadow
              onPointerOver={(e) => { e.stopPropagation(); if (isInteractive) { document.body.style.cursor = 'pointer'; setHoveredIdx(sortedIdx); } }}
              onPointerOut={() => { document.body.style.cursor = ''; setHoveredIdx(null); }}
              onClick={(e) => { e.stopPropagation(); if (isInteractive) onSelectShelf(isSelected ? null : sortedIdx); }}
            >
              <meshStandardMaterial
                color={currentColor}
                emissive="#3b82f6"
                emissiveIntensity={isSelected ? 0.35 : isHovered ? 0.22 : isInteractive ? 0.05 : 0}
              />
            </Box>
            {showEdges && <PanelEdges args={[shelfW, safeWallThickness, shelfD]} position={[shelfX, sPos, shelfZ]} />}

            {isSelected && isInteractive && (
              <Html position={[shelfX, sPos + safeWallThickness + 0.04, safeDepth / 2 + 0.05]} center zIndexRange={[300, 0]}>
                <div style={overlayWrapStyle}>
                  <OverlayBtn grab onPointerDown={(e) => { e.stopPropagation(); startMoveDrag(e); }} title="Perkelti (laikyti ir vilkti)">
                    <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>open_with</span>
                  </OverlayBtn>
                  <OverlayBtn onClick={(e) => { e.stopPropagation(); onSelectShelf(null); if (onShelfDelete) onShelfDelete(sortedIdx); }} title="Ištrinti">
                    <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>delete</span>
                  </OverlayBtn>
                </div>
              </Html>
            )}
          </group>
        );
      })}
    </>
  );
};

// ── CabinetRightDragHandle ────────────────────────────────────────────────────
// Thin draggable strip on the right wall — drags to resize cabinet width.
const CabinetRightDragHandle = ({ cabWidth, cabHeight, safeDepth, safeWallThickness, visualBaseHeight, onWidthChange }) => {
  const dragRef = useRef(null);
  const { camera, size } = useThree();
  const EDGE_W = 0.004;   // visible blue edge — thickness in X
  const EDGE_D = 0.004;   // visible blue edge — thickness in Z (PATAISYTA: anksčiau čia
  // nebuvo atskiro Z apribojimo, tad "visible edge" tęsėsi per VISĄ safeDepth (iki 0.6m) —
  // vizualiai ne plona linija, o pilna, per visą gylį ištemptą mėlyna permatoma PLOKŠTUMA,
  // kuri iš kampinio kameros vaizdo atrodo kaip plati "dryžuota" mėlyna juosta (grūdėtumo
  // bump tekstūra, prašvieskanti per 0.7 opacity). TA PATI "visą paviršių dažo mėlynai"
  // problema, kaip CabinetTopDragHandle anksčiau (žr. ten identišką pataisymą/komentarą —
  // vartotojas: "Aktyvaus baldo nereikia zymeti melynai") — tik ŠIS komponentas (pločio,
  // ne aukščio, rankena) liko nepataisytas. Dabar matoma linija plona ABIEM ašimis (X ir
  // Z), kaip CabinetTopDragHandle — nematomas platesnis/gilesnis "hit" Box (žemiau)
  // paliktas per visą gylį, kad liktų lengva nutaikyti/nutempti iš bet kokio kampo.
  const HIT_W = 0.022;    // invisible wider hit zone
  const carcassH = cabHeight - visualBaseHeight;
  const carcassCY = visualBaseHeight + carcassH / 2;
  const events = {
    onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = 'ew-resize'; },
    onPointerOut: () => { document.body.style.cursor = ''; },
    onPointerDown: (e) => {
      if (e.button !== 0) return; // tik kairysis mygtukas — dešinysis nevilkina pločio
      e.stopPropagation();
      const fovRad = camera.fov * Math.PI / 180;
      const ppm = (size.height / 2) / (camera.position.length() * Math.tan(fovRad / 2));
      dragRef.current = { startX: e.clientX, startW: cabWidth, ppm };
      document.body.style.cursor = 'grabbing';
      const onMove = (me) => {
        if (!dragRef.current) return;
        const dx = (me.clientX - dragRef.current.startX) / dragRef.current.ppm;
        onWidthChange(Math.max(0.1, dragRef.current.startW + dx));
      };
      const onUp = () => {
        dragRef.current = null;
        document.body.style.cursor = '';
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
  };
  return (
    <group>
      {/* Visible edge — thin line along the front-right vertical edge only (not a
          full-depth plane, žr. EDGE_D komentarą aukščiau). */}
      <Box position={[cabWidth - EDGE_W / 2, carcassCY, safeDepth - EDGE_D / 2]} args={[EDGE_W, carcassH, EDGE_D]}>
        <meshStandardMaterial color="#3b82f6" opacity={0.7} transparent />
      </Box>
      {/* Invisible wide hit zone — full depth, so dragging stays easy to grab from any angle */}
      <Box position={[cabWidth - HIT_W / 2, carcassCY, safeDepth / 2]} args={[HIT_W, carcassH, safeDepth]} {...events}>
        <meshStandardMaterial transparent opacity={0} />
      </Box>
    </group>
  );
};

// ── CabinetTopDragHandle ──────────────────────────────────────────────────────
// Draggable strip at the top of each cabinet for stepped-wardrobe height drag.
// /admin/bugs "Kai baldas yra aktyvus nereikia to melyno zymejimo" — anksčiau ŠI
// rankena buvo VIENAS pilno width×depth, tik HANDLE_H (1,2cm) storio dėžutės
// sluoksnis, visada matomas (0,6 opacity mėlynas) kol baldas aktyvus — kadangi jos
// plonas kraštas buvo Y ašyje (ne X, kaip CabinetRightDragHandle), iš priekio/kampo
// ji atrodė kaip VIENTISA mėlyna "lenta", uždengianti visą viršų, o ne plona
// rankenos linija. Pataisyta TA PAČIA kryptimi, kaip CabinetRightDragHandle
// aukščiau: plonas MATOMAS kraštas (dabar plonas GYLYJE, stovi tik ties priekiniu
// viršaus kraštu kaip siaura linija) + platesnė NEMATOMA paspaudimo zona (lengviau
// pataikyti vilkimui, bet nebesimato kaip užpildytas stačiakampis).
const CabinetTopDragHandle = ({ cabWidth, cabHeight, safeDepth, onHeightChange }) => {
  const dragRef = useRef(null);
  const { camera, size } = useThree();
  const EDGE_D = 0.004;  // plonas matomas kraštas (gylyje)
  const HIT_H = 0.03;    // platesnė nematoma paspaudimo zona (aukštyje)
  const events = {
    onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = 'ns-resize'; },
    onPointerOut: () => { document.body.style.cursor = ''; },
    onPointerDown: (e) => {
      if (e.button !== 0) return; // tik kairysis mygtukas — dešinysis nevilkina aukščio
      e.stopPropagation();
      const fovRad = camera.fov * Math.PI / 180;
      const ppm = (size.height / 2) / (camera.position.length() * Math.tan(fovRad / 2));
      dragRef.current = { startY: e.clientY, startH: cabHeight, ppm };
      document.body.style.cursor = 'grabbing';
      const onMove = (me) => {
        if (!dragRef.current) return;
        const dy = (dragRef.current.startY - me.clientY) / dragRef.current.ppm;
        onHeightChange(Math.max(0.2, dragRef.current.startH + dy));
      };
      const onUp = () => {
        dragRef.current = null;
        document.body.style.cursor = '';
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
  };
  return (
    <group>
      {/* Visible edge — plona linija ties priekiniu viršaus kraštu */}
      <Box position={[cabWidth / 2, cabHeight, safeDepth - EDGE_D / 2]} args={[cabWidth, 0.002, EDGE_D]}>
        <meshStandardMaterial color="#3b82f6" opacity={0.7} transparent />
      </Box>
      {/* Invisible wide hit zone */}
      <Box position={[cabWidth / 2, cabHeight - HIT_H / 2, safeDepth / 2]} args={[cabWidth, HIT_H, safeDepth]} {...events}>
        <meshStandardMaterial transparent opacity={0} />
      </Box>
    </group>
  );
};

// ── PanelEdges ───────────────────────────────────────────────────────────────
// Draws a front-face rectangle outline for "Rodyti kontūrus" mode.
// Front-face only avoids depth-edge crossing artifacts at panel junctions.
const PanelEdges = ({ args, position }) => {
  const obj = useMemo(() => {
    const [w, h, d] = args;
    // Proud of the panel surface — prevents z-fighting with the panel's own face.
    // Kept small (1mm): 0.003 visibly floated the line off the board in the common
    // case ("linijos kelis milimetrus iškeltos nuo plokštės" — bendras #1). The one
    // narrow case this margin doesn't fully cover — a divider explicitly pulled to
    // the facade plane ("Ištraukta į priekį") while it still borders a door, so its
    // contour lands at the exact same depth as the door's own contour — anksčiau
    // buvo laikoma retu atveju, bet vartotojas jį rado: "plokste yra istraukta i
    // fasado plokstuma... kodel nesutampa konturu linijos?" — OuterDoors.jsx
    // DoorEdgeLines naudojo KITOKĮ (0.006, ne 0.001) poslinkį būtent TAM, kad
    // IŠVENGTŲ z-fighting su šia linija — bet tai reiškė, kad abi linijos, nors
    // žymi TĄ PATĮ fizinį kraštą, realybėje stovi 5mm skirtingame gylyje: statmenai
    // nematoma, bet žiūrint kampu sukuria būtent tą "dvi beveik lygiagrečios,
    // truputį paslinktos linijos" efektą (paralaksas). depthTest=false ČIA
    // SĄMONINGAI NEnaudojamas (nors pašalintų z-fighting riziką) — jis priverstų
    // šią liniją "prasišviesti" per BET KĄ priekyje, net per TIKRAI ją uždengiantį
    // paviršių (pvz. pertvaros, paslėptos už platesnių durų, kontūras taptų
    // matomas PER duris) — platesnė, blogesnė regresija nei sprendžiama problema.
    // Vietoj to: abi linijos (čia IR DoorEdgeLines) dabar naudoja TĄ PATĮ 0.001
    // poslinkį — kai jos tikrai sutampa (facade atvejis), jos atsiduria TIKSLIAI
    // tame pačiame gylyje, tad paralaksas išnyksta; o kadangi abi VIENODOS spalvos
    // (#1a1a1a), joks z-fighting tarp jų pačių nebūtų pastebimas net jei nutiktų —
    // GPU tiesiog pakaitomis "laimėtų" ta pati spalva.
    const z = d / 2 + 0.001;
    const pts = new Float32Array([
      -w / 2, -h / 2, z,
       w / 2, -h / 2, z,
       w / 2,  h / 2, z,
      -w / 2,  h / 2, z,
    ]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
    const mat = new THREE.LineBasicMaterial({ color: '#1a1a1a' });
    const loop = new THREE.LineLoop(geo, mat);
    // Purely a visual outline — three.js's Line.raycast() would otherwise let clicks
    // "hit" these thin edge lines instead of passing through to the background, which
    // was blocking onPointerMissed (furniture deselect) whenever contours were shown,
    // especially with multiple furniture pieces (more edges = more accidental hits).
    loop.raycast = () => {};
    return loop;
  }, [args[0], args[1], args[2]]);
  useEffect(() => () => { obj.geometry.dispose(); obj.material.dispose(); }, [obj]);
  return <primitive object={obj} position={position} />;
};

// ── CabinetPanel ─────────────────────────────────────────────────────────────
// Cabinet carcass board with hover glow effect. `args`' own depth is always the baldo
// gylis (B, the niche-fit boundary — vartotojas: "maksimalus gylis visais atvejais yra
// baldo gylis"). Two diagnostic planes (žr. PanelDepthTooltip, naudojamas B/B-D
// verifikacijai): kai šis skydas RIBOJASI su durelėmis (bordersDoor) ir NĖRA pats
// ištrauktas į fasadą, jis atsitraukia per wallThickness (karkaso plokštuma = B-D) ir
// durelės (B) jį dengia iš priekio; ištrauktas į fasadą ("Ištraukta į priekį" —
// panelFrontOffsets / handleTogglePanelFront Layout.jsx), jis stovi LYGIAI B, be jokios
// paraštės (anksčiau čia buvo +0.006 "saugiai" prašokti durelių priekį — tai PRIEŠTARAVO
// niekada-neviršyti-B taisyklei ir gadino šio tooltip'o skaičius). Kai fasado skydas ir
// durelė atsiduria lygiai tame pačiame B (sekcija, kuri turi ir durelę, ir pažymėtą
// "Ištraukta į priekį" tą pačią plokštę), lygiosios sprendžiamos polygonOffset'u (žr.
// žemiau, isFacade šaka) — PVZ fasado medžiaga gauna dar neigiamesnį offset'ą nei durelės
// (DoorFaceMaterial -4/-4), tad lieka matoma/nedengiama, bet POZICIJA išlieka tiksli B.
const CabinetPanel = ({
  args, position, color, texture, activeStep, castShadow, receiveShadow, showEdges = false,
  wallThickness = 0, isFacade = false, bordersDoor = false, isSelected = false, onSelectSelf = null, onToggleFront = null,
  // edgeOnly=false: visas paviršius yra hover/click zona, kaip UnifiedTopPanel.jsx TopBox
  // jau daro Stogeliui (žr. ten komentarą prie "SKIRTINGAI nuo CabinetPanel") — tinka
  // IZOLIUOTAI plokštei, neturinčiai kaimyno tame pačiame aukštyje/plokštumoje, kurį
  // reikėtų atskirti pagal tikslų paspaudimo tašką. 'top' karkaso siena yra TOKS
  // atvejis (vienintelė plokštė tame Y aukštyje) — vartotojas negalėjo jos ĮSKRITAI
  // pažymėti ("nieko neatsitinka" paspaudus), nes edge-only apribojimas reikalauja
  // paspausti ~3cm pakraštyje plačios, horizontalios plokštės, o natūraliai spaudžiama
  // VIDURYJE. bottom/left/right PALIEKA edgeOnly=true (default) — jiems disambiguacija
  // reikalinga (žr. Pagrindo/kaimyninio kabineto konkurenciją).
  edgeOnly = true,
}) => {
  const [edgeHovered, setEdgeHovered] = useState(false);
  const [depthHovered, setDepthHovered] = useState(false);
  // onClick tikrina ŠITĄ ref'ą (paskutinio onPointerMove rezultatą), NE perskaičiuoja
  // isNearPanelEdge iš NAUJO su paties click event'o .uv — vartotojas: "kai jau
  // pasidare zalia man uzejus ir paspaudus, reiskia ir mygtukai turi pasirodyti".
  // Anksčiau kartais NESUTAPDAVO: pele TIKSLIAI toje pačioje ekrano vietoje per
  // onPointerMove ir click gali grąžinti NEŽYMIAI skirtingą uv (kelių pikselių tikslumo
  // skirtumas tarp dviejų atskirų raycast'ų), tad prie pat 3cm ribos retsykiais
  // "žalia" (hover) ir "click praėjo" duodavo skirtingą atsakymą. Dabar click ir
  // vaizdas (žalias rėmelis) VISADA sutampa, nes abu remiasi TUO PAČIU skaičiavimu.
  const edgeHoveredRef = useRef(false);
  const isIsore = activeStep === 'Išorė';
  // Žalias briaunos rėmelis TURI rodytis TIK tada, kai paspaudimas IŠ TIKRŲJŲ ką nors
  // padarys — onClick žemiau VEIKIA TIK 'Išorė' žingsnyje (isIsore && onSelectSelf), bet
  // iki šio pataisymo isHoverable (taigi ir rėmelis/pointer kursorius) ĮSIJUNGDAVO IR
  // 'Baldas'/'Vidus' žingsniuose vien dėl isInteractive (activeStep==='Baldas'||'Vidus')
  // — rodant rėmelį, kuris ŽADA paspaudimą, kurio realiai nėra (vartotojas: "kartais
  // pasizymi baldas, kartais plokste, o kartais isvis niekas" — ši plokštė viena iš
  // priežasčių, kodėl atrodė neaišku, kada paspaudimas suveiks). isInteractive pats
  // kintamasis vėliau pašalintas — jo vienintelis likęs naudojimas buvo emissiveIntensity
  // `ei` (dabar suvienodinta su durelėmis į fiksuotą 0.3, žr. žemiau).
  const isHoverable = isIsore && !!onSelectSelf;
  // PATAISYTA: anksčiau ši plokštė (kai bordersDoor=true IR neištraukta į fasadą)
  // atsitraukdavo per wallThickness nuo durelių plokštumos — SKIRTINGAI nuo vidinės
  // pertvaros atveju (ten durys, būdamos PLATESNĖS už pertvarą, ją PRIDENGIA, tad
  // atsitraukimas niekada nematomas), ŠI plokštė (karkaso viršus/šonai/apačia) yra
  // PLATESNĖ už dureles ir visada matoma KAIP RĖMAS aplink jas — tad tas pats
  // atsitraukimas čia NIEKADA nebūdavo paslėptas, o rodydavosi kaip tikras, nuolatinis
  // ~1,8cm laiptelis/šešėlis aplink visas dureles. Vartotojas: "dabar atrodo jog
  // durelės yra idubusios ir turime aplink jas šešėlį, nors is tiesu visu karkasiniu
  // ploksciu ir dureliu gylis yra 60cm" + "visos plokstes turi buti prisiglaudusios
  // viena prie kitos ir turi buti atstumas tarp ju lygus 0, mazas atstumas turi buti
  // tik tarp dureliu ir aplink jas esanciu ploksciu" — t.y. RĖMAS (karkaso plokštė)
  // turi VISADA stovėti lygiai su dureles plokštuma, nepriklausomai nuo isFacade
  // perjungiklio; tik vidinės (durimis pridengiamos) pertvaros gali prasmingai
  // atsitraukti pagal numatytąjį nustatymą.
  // PATAISYTA (vartotojo patikslinimas): aukščiau aprašyta "atstumas = 0" taisyklė
  // yra apie plokščių susiglaudimą X AŠYJE (šoninė riba su kaimynine plokšte), NE
  // apie šios plokštės PAČIOS gylį Z ašyje — tai du nesusiję dalykai. Anksčiau čia
  // (klaidingai) depthDelta buvo besąlygiškai užšaldytas į 0, todėl ši plokštė
  // VISADA stovėdavo lygiai B (60cm), nepriklausomai nuo isFacade, o "Gylis" tooltip'as
  // (PanelDepthTooltip žemiau, skaito effArgs[2]) dėl to irgi visada rodydavo 60 —
  // vartotojas: "nepriklausomai ar istraukiu ja i fasado plokstuma ar pastumiu i
  // karkaso gyli, gylio tooltip man rodo 60cm". Grąžinta TA PATI formulė, kurią
  // vidinės pertvaros (DividerDragHandle/NarySectionDivider frontZ) jau teisingai
  // naudoja: karkaso plokštumoje (ne isFacade, bet bordersDoor) plokštė atsitraukia
  // per wallThickness (B−1,8); fasado plokštumoje (isFacade) stovi lygiai B. Durų
  // dengimo/matomumo klausimas (kodėl ši plokštė PLATESNĖ už dureles ir atsitraukimas
  // čia visada matomas) išlieka, bet tai atskiras, apgalvotas vaizdinis pasirinkimas,
  // ne priežastis visai Z ašies logikai užšaldyti.
  const depthDelta = (!isFacade && bordersDoor) ? -wallThickness : 0;
  const effArgs = depthDelta ? [args[0], args[1], args[2] + depthDelta] : args;
  const effPosition = depthDelta ? [position[0], position[1], position[2] + depthDelta / 2] : position;
  const hoverHandlers = {
    // setDepthHovered TYČIA pirmoje eilutėje, prieš isHoverable patikrą — "Gylis" tooltip'as
    // (žr. PanelDepthTooltip) yra grynai informacinis, veikia ant VISO paviršiaus ir visuose
    // žingsniuose, nepriklausomai nuo to, ar ši konkreti plokštė šiuo metu paspaudžiama.
    onPointerMove: (e) => {
      setDepthHovered(true);
      if (!isHoverable) return;
      e.stopPropagation();
      const near = edgeOnly ? isNearPanelEdge(e, effArgs[0], effArgs[1], undefined, edgeHoveredRef.current) : true;
      edgeHoveredRef.current = near;
      setEdgeHovered(near);
      document.body.style.cursor = near ? 'pointer' : '';
    },
    onPointerOut: () => { setDepthHovered(false); edgeHoveredRef.current = false; setEdgeHovered(false); if (isHoverable) document.body.style.cursor = ''; },
    // e.stopPropagation() PERKELTA prieš briaunos patikrą — anksčiau paspaudimas NE
    // prie briaunos tiesiog "iškrisdavo" neperskaitytas iki FurnitureGroup.jsx viršutinio
    // <group onClick>, kuris arba pažymėdavo VISĄ baldą (jei dar neaktyvus), arba
    // nedarydavo nieko (jei jau aktyvus) — vartotojo pranešta kaip "kartais pasizymi
    // baldas, kartais plokste, o kartais isvis niekas". Dabar paspaudimas šią plokštę
    // VISADA "praryja" (nebekyla aukščiau), o ar jis ką nors pažymi, sprendžia tik
    // edgeHoveredRef patikra.
    onClick: isIsore && onSelectSelf ? (e) => { e.stopPropagation(); if (!edgeHoveredRef.current) return; onSelectSelf(); } : undefined,
  };
  // "bottom" (ir kartais "left"/"right" jų pačiu apatiniame gale) TIESIOG RIBOJASI su
  // Pagrindo UnifiedBase.jsx priekine juosta/dėže (žr. ten FrontTrimBox), kuri savo
  // ruožtu turi Z ašimi ištemptą nematomą hitbox'ą (siekia cabinetDepth+0.06 į priekį,
  // kad laimėtų konkurenciją prieš pertvarą). Kadangi ŠI plokštė (ypač "bottom", tik
  // wallThickness storio) neturėjo JOKIO analogiško ištempimo, prie pat tos bendros
  // ribos ji PRARASDAVO raycast konkurenciją prieš kaimyninę Pagrindo juostą — vartotojas:
  // "kai bandau pazymeti karkaso plokstes ir paspausti tada nepasirodo mygtukai". Sprendimas
  // TA PATI kryptimi, kaip UnifiedBase.jsx left/right vs front trim: šiai plokštei irgi
  // pridedamas truputį toliau į priekį siekiantis (ir todėl lygiais atvejais laimintis)
  // nematomas hit box.
  const hitDepthArg = effArgs[2] + 0.07;
  const hitPosZ = hitDepthArg / 2 - effArgs[2] / 2;
  return (
    <group position={effPosition}>
      <Box args={effArgs} castShadow={castShadow} receiveShadow={receiveShadow} {...hoverHandlers}>
        {/* meshStandardMaterial's `color` multiplies the texture `map` pixel-for-pixel —
            `color` here (currentColor) is a plain selection-state tone (see its
            definition below), never the real decor's own color, so multiplying it against
            a loaded texture was silently washing it out, regardless of which decor was
            actually selected.
            Before a real decor plate is chosen, the panel is a plain flat light grey
            (currentColor) — no procedural texture map, just the bumpMap for subtle
            surface relief so it doesn't read as flat plastic. */}
        <meshStandardMaterial
          color={texture ? '#ffffff' : color} map={texture || undefined}
          bumpMap={texture ? undefined : getGrainBumpTexture()} bumpScale={texture ? 0 : 0.0018}
          // roughness/metalness SUVIENODINTA su OuterDoors.jsx DoorFaceMaterial (buvo
          // 0.34/0.06 čia vs 0.30/0.07 ten) — nedidelis, bet REALUS PBR skirtumas, kuris
          // keisdavo šio paviršiaus atspindį nepriklausomai nuo bet kokio apšvietimo
          // derinimo (žr. [[project_shadow_camera_precision_2026_10]] — trys šviesos
          // lygio pataisymai nepajudino nė kiek, nes problema buvo medžiagos PARAMETRE,
          // ne pačioje šviesoje).
          roughness={0.3} metalness={0} envMapIntensity={0}
          // metalness 0.07→0, envMapIntensity 1.35→0.2 — žr. identišką OuterDoors.jsx
          // DoorFaceMaterial komentarą: vartotojas pastebėjo kameros-kampo priklausomą
          // patamsėjimą paviršiaus kraštuose (Fresnel/Environment IBL atspindys), kuris
          // dingsta priartinus/žiūrint tiesiai — metalness=0 pašalina šią priklausomybę.
          // Bendra ryškumo "grindis" (0.3), naudojanti PAČIOS plokštės spalvą (ne
          // mėlyną ei atspalvį kaip anksčiau) — vartotojas, po algebra ĮRODYTO nulinio
          // tarpo tarp pertvaros viršaus ir sienos apačios: "reikia kad neliktu tokio
          // pobudzio seseliu" (vis tiek matomas įgaubto 90° kampo patamsėjimas, kur du
          // statmeni paviršiai iš dalies užstoja vienas kitą nuo šviesos — fizikai
          // teisingas reiškinys, bet vartotojo nebepriimtinas). Skirtingai nuo ANKSTESNIO
          // (atšaukto) bandymo pertvaroms, čia NĖRA alternatyvaus geometrijos sprendimo —
          // du statmeni korpuso elementai PRIVALO susitikti 90° kampu, tad vienintelis
          // įrankis yra šviesa. 0.3 (ne 1.0, kaip buvo galutinis pertvaros sprendimas)
          // sąmoningai kuklesnis — šis paviršius yra DIDELIS, plokščias, jau gerai
          // apšviestas savo priekine puse, tad pilna "grindis" jį per daug nuplokštintų;
          // reikia tik pakelti PAČIUS tamsiausius (kampo) taškus, ne visą paviršių.
          // emissiveIntensity SUVIENODINTA su durelėmis — buvo `0.3 + ei`, t.y. KARKASAS
          // gaudavo papildomą +0.04 TIK Baldas/Vidus žingsniuose (žr. ei/isInteractive
          // aukščiau), durelės — visada fiksuotą 0.3 — ši asimetrija keitėsi priklausomai
          // nuo žingsnio, durelės/karkasas niekada nebuvo IDENTIŠKAI ryškūs vienu metu.
          emissive={color} emissiveIntensity={0.3}
          // SUSIAURINTA nuo (isFacade || bordersDoor) iki vien isFacade — žr. depthDelta
          // komentarą aukščiau: ta "visada koplanari" prielaida nebegalioja, grąžinus
          // realų atsitraukimą karkaso plokštumoje (bordersDoor && !isFacade atveju ši
          // plokštė DABAR stovi B−wallThickness, t.y. jau NEBE koplanari su durimis —
          // z-fight apsaugos tam nebereikia, lygiai kaip ir vidinėms pertvaroms, kurios
          // šią sąlygą visada ribojo vien iki isFacade).
          // -8/-8 → -2/-2 — žr. OuterDoors.jsx DoorFaceMaterial identišką komentarą: GPU gylio
          // poslinkis nėra selektyvus VIEN koplanarumui su durimis spręsti, jis veikia ŠIOS
          // plokštės gylio palyginimą prieš VISKĄ aplink (pvz. prieš ShelfDragOverlay lentyną,
          // kuri jokio offset'o neturi) — -8 buvo gerokai daugiau nei reikia paprastai lygiajai
          // išspręsti, tad kūrė matomą tarpą/persišvietimą prie TIKSLIAI sutampančių (patikrinta
          // Plokščių koordinatės įrankiu), bet NESUSIJUSIŲ su durimis ribų. -2 išlaiko tą patį
          // santykį su durelių -1 (žr. ten), bet su mažesniu šalutinio poveikio spinduliu.
          {...(isFacade ? { polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 } : null)}
        />
      </Box>
      <Box args={[effArgs[0], effArgs[1], hitDepthArg]} position={[0, 0, hitPosZ]} {...hoverHandlers}>
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </Box>
      <HoverEdgeFrame width={effArgs[0]} height={effArgs[1]} depth={effArgs[2]} visible={isHoverable && (edgeHovered || isSelected)} />
      <PanelDepthTooltip depth={effArgs[2]} position={[0, 0, effArgs[2] / 2 + 0.02]} visible={depthHovered} />
      {showEdges && <PanelEdges args={effArgs} position={[0, 0, 0]} />}
      {isIsore && isSelected && onToggleFront && (
        <Html position={[0, 0, effArgs[2] / 2 + 0.02]} center zIndexRange={[175, 0]} style={{ pointerEvents: 'none' }}>
          <div style={overlayWrapStyle}>
            <OverlayBtn
              title={isFacade ? "Grąžinti į karkaso plokštumą" : "Ištraukti į priekį (fasadų plokštuma)"}
              onClick={(e) => { e.stopPropagation(); onToggleFront(); }}
            >
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300, color: isFacade ? '#09aa89' : undefined }}>
                swap_horiz
              </span>
            </OverlayBtn>
          </div>
        </Html>
      )}
    </group>
  );
};

// ── DividerDragHandle ────────────────────────────────────────────────────────
// Renders the visible divider wall with hover emissive + Move/Delete overlay.
const DividerDragHandle = ({ isHorizontal, width, height, depth, child0Size, wallThickness, available, path, onSectionResize, onSectionDelete, activeStep, currentColor, isSelected, onSelectSelf, dividerKey, isInner = false, onGhostSelect = null, isGhostSelected = false, onGhostConvert = null, minChild0 = MIN_LEAF_SIZE, minChild1 = MIN_LEAF_SIZE, onDemote = null, showEdges = false, isFacade = false, onToggleFront = null, bordersDoor = false, extraBottomExtend = 0 }) => {
  const dragRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [depthHovered, setDepthHovered] = useState(false);
  const { controls, camera, size } = useThree();

  // Outer dividers: draggable in Išorė, clickable in Baldas; Inner dividers: interactive in Vidus only
  const isInteractive = isInner ? activeStep === 'Vidus' : (activeStep === 'Baldas' || activeStep === 'Išorė');
  const isIsore = !isInner && activeStep === 'Išorė';
  // Vidus dividers now drag directly on hover too, same as Išorė (previously Vidus required a
  // click to select first, then a separate "Perkelti" button — see 86cay2ygr).
  const dragOnHover = isIsore || isInner;
  const HIT_HALF = 0.08;
  const posX = isHorizontal ? width / 2 : child0Size + wallThickness / 2;
  // extraBottomExtend (basisType 7/8 only, žr. SingleCabinet3D "Vertical split" komentarą
  // žemiau) stumia PLONOS vertikalios pertvaros APAČIĄ žemyn iki grindų/plokštės lygio,
  // NEKEIČIANT viršaus — todėl posY (centras) pasislenka žemyn per extraBottomExtend/2,
  // o wallArgs[1] (aukštis, žr. žemiau) padidėja per tą patį kiekį.
  const posY = isHorizontal ? (available - child0Size) + wallThickness / 2 : (height - extraBottomExtend) / 2;

  const startMoveDrag = (e, from3D = false) => {
    const nativeE = e.nativeEvent || e;
    if (nativeE.button !== 0) return; // tik kairysis mygtukas — dešinysis nevilkina pertvaros
    const prevEnabled = controls?.enabled;
    if (from3D && controls) controls.enabled = false;
    const startCoord = isHorizontal ? nativeE.clientY : nativeE.clientX;
    const startClientX = nativeE.clientX;
    const startClientY = nativeE.clientY;
    const fovRad = camera.fov * Math.PI / 180;
    const ppm = (size.height / 2) / (camera.position.length() * Math.tan(fovRad / 2));
    dragRef.current = { startCoord, startRatio: child0Size / available, ppm };
    document.body.style.cursor = 'grabbing';
    let didDrag = false;
    let rafId = null;
    let latestCoord = startCoord;
    const applyFrame = () => {
      rafId = null;
      if (!dragRef.current) return;
      const delta = (latestCoord - dragRef.current.startCoord) / (dragRef.current.ppm * available);
      let minRatio = minChild0 / available;
      let maxRatio = 1 - (minChild1 / available);
      if (minRatio > maxRatio) { minRatio = maxRatio = 0.5; }
      const newRatio = Math.max(minRatio, Math.min(maxRatio, dragRef.current.startRatio + delta));
      if (onSectionResize) onSectionResize(path, newRatio);
    };
    const onMove = (me) => {
      if (!dragRef.current) return;
      if (!didDrag && (Math.abs(me.clientX - startClientX) > 5 || Math.abs(me.clientY - startClientY) > 5)) didDrag = true;
      latestCoord = isHorizontal ? me.clientY : me.clientX;
      if (rafId == null) rafId = requestAnimationFrame(applyFrame);
    };
    const onUp = () => {
      if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
      dragRef.current = null;
      document.body.style.cursor = '';
      if (!didDrag && dragOnHover) {
        // ClickUp "3.0 Išorė. Briaunų action mygtukų iššaukimas. Sunku kartais
        // iššaukti action mygtukus" — ta pati priežastis/pataisymas, kaip durelių
        // toggle-to-deselect bug'as (Builder3D.jsx handleShelfClick): hover-žalia
        // ir selected-žalia atrodo vienodai, tad paspaudus JAU pažymėtą dalyklę
        // DAR KARTĄ, ji išsijungdavo be jokio matomo pasikeitimo (žalia likdavo
        // dėl hover). Atžymėjimas tuščioje vietoje jau veikia atskirai, tad šis
        // toggle nebereikalingas — paspaudimas dabar VISADA pažymi.
        onSelectSelf?.(dividerKey);
      } else {
        onSelectSelf?.(null);
      }
      if (from3D && controls) controls.enabled = prevEnabled ?? true;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // `depth` is always baldo gylis (B). When a door covers this divider (bordersDoor) and
  // it is NOT itself pulled to facade, it recedes by wallThickness (karkaso plokštuma =
  // B-D) and the door (B) covers it from the front. Pulled to facade ("Ištraukta į
  // priekį" — panelFrontOffsets Layout.jsx), it stands at EXACTLY B, no margin (anksčiau
  // +0.006 — žr. CabinetPanel komentarą, ta pati pataisa: pozicija lieka tiksli B
  // diagnostikai, o lygiosios su durelėmis sprendžiamos polygonOffset'u žemiau).
  const frontZ = depth - ((!isFacade && bordersDoor) ? wallThickness : 0);
  // ATŠAUKTA (vartotojo sprendimu): isFacade anksčiau šią pertvarą PLONINDAVO
  // (wallThickness gylio juosta prie priekio, ne dėžė per visą gylį) — tai buvo
  // ankstesnis pataisymas dėl ilgų ±X šoninių paviršių, matytų kaip "šešėlis"/"balta
  // linija" ATVIRO (be durelių) skyriaus atveju. Vartotojas aiškiai paprašė: "plokstes
  // tureti pagrinda nuo gylio pradzio iki gylio pabaigos" — t.y. pati dėžė (ne tik jos
  // priekinis paviršius) VISADA turi siekti per visą baldo gylį, NEPRIKLAUSOMAI nuo
  // isFacade, net rizikuojant grąžinti aukščiau aprašytą šoninio paviršiaus efektą tose
  // vietose, kur pertvara neriboja su durelėmis iš abiejų pusių. frontZ (priekinio
  // paviršiaus Z pozicija — B arba B-D) šiuo pakeitimu NEKINTA, keičiasi tik GYLIS.
  const boxDepthZ = frontZ;
  const wallArgs = [isHorizontal ? width : wallThickness, isHorizontal ? wallThickness : (height + extraBottomExtend), boxDepthZ];
  const wallPos  = [posX, posY, frontZ - boxDepthZ / 2];
  // Full-depth hit box for Išorė: covers z=[0, frontZ+0.05] so it's reachable from any camera angle.
  // Ne-Išorė (Vidus/Baldas) atveju naudojamas NOMINALUS `depth` (ne galintis būti
  // sugilintas `frontZ`) + 0.08 — žr. isNearStripCenter importo komentarą: tiek
  // reikia, kad PATIKIMAI laimėtų prieš kaimyninės RecursiveSection leaf sekcijos
  // hit-box'ą (ten ištemptą iki depth+0.07), VISAIS atvejais, net kai ši pertvara
  // pati sugilinta (bordersDoor).
  // onGhostSelect (isInner, so isIsore is always false here — same +0.08 branch below):
  // unlike an already-split divider (whose two neighboring leaves each only cover HALF the
  // original width, so the divider sits at the EDGE of each one's own depth+0.07
  // click-catcher — see comment above), a ghost divider lives INSIDE a single, still-unsplit
  // outer leaf (demoted back to interiorStructure) whose OWN "click empty section" catch-all
  // (RecursiveSection leaf branch, ~2345, hitDepth=depth+0.07) spans the ENTIRE original
  // width — putting the ghost divider's hit-box deep inside that catch-all instead of at its
  // boundary (vartotojas: "Kai paverciu i vidine pertvara man jau nebeleidzia paspausti ant
  // jos ir padaryti ja isorine" — still broken after the pointerdown-drag conflict and the
  // dual-hitbox-ambiguity fixes). Confirmed live via ray-AABB timings that a NARROW hit-box
  // here never wins this specific race no matter how deep hitDepth reaches: with
  // width=HIT_HALF*2, ray-AABB entry time is dominated by the (late-satisfied) X axis, not Z,
  // so extra depth never even factors in — the actual fix is widening hitArgs below (to match
  // the catch-all's own full `width`, removing X as the limiting axis) so the EXISTING +0.08
  // margin over the catch-all's +0.07 finally gets to decide the race on Z. Deliberately NOT
  // pushing hitDepth further than +0.08 to compensate: a much larger hitDepth (tried
  // depth+0.2, combined with the widened box) reaches a front-face plane far enough forward
  // that, at an angled camera, the SAME screen ray lands on a world point perceptibly offset
  // from the true divider position (perspective/parallax, not a bug in nearCenter) — enough
  // to fail nearCenter's fixed ~3cm/7cm margin outright.
  const hitDepth = isIsore ? frontZ + 0.05 : depth + 0.08;
  // onGhostSelect: a plain ray-vs-box "which hitbox is nearer" comparison isn't decided by
  // Z-reach alone — ray-AABB entry time is the MAX of each axis's own entry t, so the leaf
  // catch-all's full-width X-range (~2345, same `width`) lets its ray-entry be dominated by
  // (and resolve as early as) its Z-range, while our normally-narrow HIT_HALF*2 X-range delays
  // OUR entry past that regardless of how deep hitDepth reaches — confirmed live via ray-AABB
  // timings (catch-all t≈1.025 vs our narrow hitbox t≈1.32 even with hitDepth=depth+0.2).
  // Matching the catch-all's own full `width` here removes X as the limiting axis, letting
  // our deeper Z-reach actually decide the race. nearCenter (UV-based, below) still restricts
  // which clicks WITHIN this now-wide box actually count — only the raycast-priority changes.
  const hitArgs  = [
    isHorizontal ? width : (onGhostSelect ? width : HIT_HALF * 2),
    isHorizontal ? (onGhostSelect ? (height + extraBottomExtend) : HIT_HALF * 2) : (height + extraBottomExtend),
    hitDepth,
  ];
  const hitPos   = [posX, posY, hitDepth / 2];
  // Pertvara jau savaime plona (ne platus paviršius kaip CabinetPanel), tad jai
  // NETAIKOMAS isNearPanelEdge (briaunos-nuo-PERIMETRO) apribojimas — vietoj to,
  // žr. isNearStripCenter: hover/vilkimas/paspaudimas galioja tik arti PLATAUS
  // hit-box'o CENTRO (kur realiai stovi pati plona pertvara), žr. `nearCenter`
  // žemiau — vartotojas: "vilkimas turi būti apribotas iki briaunos, jeigu užeinu
  // pele hover ant briaunos tada turiu galimybę arba vilkt, arba paspaust ant jos"
  // (86cay2ygr). Ghost režimas (onGhostSelect) paliktas nepakitęs — jis jau turi
  // savo, atskirą spalvų kalbą (žalia=paversta, mėlyna=hover), ir savo atskirą
  // nematomą "hit" juostą (žemiau), nesusijusią su šiuo apribojimu.
  // sticky=true (žr. edgeHover.jsx EDGE_MARGIN_M_STICKY) — kai pertvara JAU laikoma
  // "arti centro" (žalia), platesnė riba, kol vėl užgęsta, vartotojas: "labai sudetinga
  // paspausti... kazkokia papildoma atsargine vieta... ji nesiaktyvuotu kol nera
  // pataikoma tiksliai ant briaunos".
  // onGhostSelect: e.uv/e.point reflect where the ray hit the WIDENED hit-box's OWN
  // pushed-forward Z-plane (hitArgs/hitPos below, deliberately different from the thin
  // visible divider) — at an angled camera that plane sits far enough ahead of the true
  // divider position that the ray crosses it at a world (x,y) that has ALREADY visibly
  // drifted sideways from posX/posY by real meters (perspective/parallax: the ray is not
  // parallel to the Z axis, so "further forward" also means "further sideways"). Comparing
  // the raw hit point (e.point, even via worldToLocal) against posX/posY inherited this
  // drift and still failed nearCenter after hitDepth was pulled back to the usual +0.08
  // margin (vartotojas: "Kai paverciu i vidine pertvara man jau nebeleidzia paspausti ant
  // jos ir padaryti ja isorine"). The fix re-derives the ORIGINAL ray (e.ray, independent of
  // which hit-box face it actually crossed) and intersects it with the true divider's own
  // Z-plane (wallPos[2] below) instead — giving the (x,y) this click would have hit had the
  // visible, un-pushed divider itself been the thing in front of the camera.
  const nearCenter = (e, sticky = false) => {
    // onGhostSelect always gets the wider STICKY margin, not just sticky===true: the
    // isHovered React state this normally gates on is set imperatively inside
    // handleHoverMove (document.body.style.cursor) but read back as plain component state —
    // the click (mousedown+mouseup, no further pointermove) can fire before that state
    // update has actually flushed/re-rendered, so `sticky` here can still read its OLD
    // (false) value even though the cursor visibly already flipped to 'pointer'. A ghost
    // divider is a deliberate, infrequent edit action, not a dense click target competing
    // for precision against neighbors, so there is no reason to risk the narrow margin here.
    const margin = (sticky || onGhostSelect) ? EDGE_MARGIN_M_STICKY : EDGE_MARGIN_M;
    if (onGhostSelect) {
      const obj = e?.eventObject || e?.object;
      if (!e?.ray || !obj?.parent) return true;
      const invMatrix = obj.parent.matrixWorld.clone().invert();
      const localOrigin = e.ray.origin.clone().applyMatrix4(invMatrix);
      const localDir = e.ray.origin.clone().add(e.ray.direction).applyMatrix4(invMatrix).sub(localOrigin);
      if (Math.abs(localDir.z) < 1e-9) return true;
      const t = (wallPos[2] - localOrigin.z) / localDir.z;
      const hitX = localOrigin.x + localDir.x * t;
      const hitY = localOrigin.y + localDir.y * t;
      return isHorizontal ? Math.abs(hitY - posY) <= margin : Math.abs(hitX - posX) <= margin;
    }
    return isHorizontal
      ? isNearStripCenter(e?.uv?.y, hitArgs[1], sticky ? EDGE_MARGIN_M_STICKY : undefined)
      : isNearStripCenter(e?.uv?.x, hitArgs[0], sticky ? EDGE_MARGIN_M_STICKY : undefined);
  };
  const ei = isInteractive ? 0.05 : 0;
  const dragCursor = isHorizontal ? 'ns-resize' : 'ew-resize';

  return (
    <group>
      {/* Ši (plona, MATOMA) dėžė anksčiau TURĖJO savo pačios, NIEKUO neapribotą
          isInteractive hover/click logiką — lygiagrečiai su žemiau esančiu plačiu
          "hit" Box'u (isInteractive blokas), kuris TĄ PATĮ darbą jau daro TEISINGAI,
          apribotas iki nearCenter. Kadangi platus hit-box'as stovi PRIEKYJE (jo
          artimiausias paviršius arčiau kameros) ir savo onPointerOver/onPointerDown
          NESUSTABDO sklidimo, kai nearCenter=false (žr. handleHoverMove), įvykis
          KELIAUDAVO TOLIAU iki šios plonos dėžės, kurios besąlyginis `setIsHovered(true)`
          "laimėdavo" lenktynes PRIEŠ plataus hit-box'o teisingą `setIsHovered(false)` —
          dėl to žalias rėmelis/vilkimas suveikdavo VISAME plačiame plote, ne tik ties
          pertvaros viduriu (vartotojas: "Zalia hover spalva turi pasirodyti TIK kai
          pelyte uzeinu ant plokstes briaunos o ne pacios plokstes"). isInteractive
          atveju platus hit-box'as VISADA sumontuotas (žr. {isInteractive && (...)}
          žemiau), tad ši dėžė jam daugiau nebekonkuruoja — telieka onGhostSelect
          (kur plataus hit-box'o apskritai nėra) ir pačios Gylio piktogramos
          setDepthHovered, kuris TYČIA lieka besąlyginis visiems atvejams. */}
      <Box args={wallArgs} position={wallPos} castShadow receiveShadow
        // Ghost mode's own hover/click used to live HERE (on this thin, exact-size box) —
        // moved entirely to the wide hit-box below (now rendered for onGhostSelect too,
        // see {(isInteractive || onGhostSelect) && ...}), matching how every NON-ghost
        // divider already works (this thin box never had onClick for the non-ghost case —
        // only the wide hit-box did). Two separate hit targets (this thin box AND the old
        // dedicated ghost-strip further down) both claiming onClick for the SAME divider
        // meant whichever one the raycaster picked up on pointerdown didn't reliably match
        // the one picked up on the later click event (r3f only fires onClick when the
        // SAME object was also hit on pointerdown) — the click silently did nothing even
        // after the pointerdown/drag conflict above was fixed (vartotojas: "Kai paverciu i
        // vidine pertvara man jau nebeleidzia paspausti ant jos ir padaryti ja isorine").
        onPointerOver={() => setDepthHovered(true)}
        onPointerOut={() => setDepthHovered(false)}
        // !onGhostSelect guard: a ghost-candidate divider (promote-to-outer preview, Išorė
        // tab) is select-only, never draggable — but onPointerDown fired unconditionally
        // whenever dragOnHover && !isInteractive, which is ALSO true for isInner ghost
        // dividers (dragOnHover = isIsore || isInner). That pointerdown called
        // startMoveDrag, whose onUp falls back to onSelectSelf (null here, ghost mode's
        // nested RecursiveSection passes onDividerSelect={null}) instead of onClick's
        // onGhostSelect — r3f never raises onClick once onPointerDown has claimed the
        // pointer, so the click silently did nothing. A just-demoted divider became
        // permanently unclickable (vartotojas: "isorine karkasine plokste paverciant i
        // vidine, nera galimybes paspausti ant jos ir ja vel paversti isorine").
        onPointerDown={(dragOnHover && !isInteractive && !onGhostSelect) ? (e) => { e.stopPropagation(); startMoveDrag(e, true); } : undefined}
      >
        <meshStandardMaterial
          // Vartotojas: "vidine pertvara [...] turetu veikti taip pat kaip ir kitos
          // plokstes kai paspaudziu ant briaunos galiu ja redaguoti" — onGhostSelect
          // (vidinės sekcijos pertvaros kandidato žymėjimas, prieš "Paversti į
          // išorinę") anksčiau NUDAŽYDAVO VISĄ paviršių (pilka→mėlyna hover→žalia
          // select), skirtingai nuo VISŲ kitų šio baldo plokščių, kur grįžtamasis
          // ryšys yra TIK plonas krašto kontūras (HoverEdgeFrame žemiau, anksčiau
          // rodomas TIK {!onGhostSelect}), o pats paviršius visada rodo vienodą
          // spalvą. Spalva/opacity dabar FIKSUOTA (be eskalacijos) abiem atvejais —
          // hover/select grįžtamasis ryšys perkeltas į bendrą HoverEdgeFrame žemiau.
          color={onGhostSelect ? '#b0bec5' : ((isInner && activeStep === 'Išorė') ? '#b0bec5' : currentColor)}
          transparent={isInner && activeStep === 'Išorė'}
          opacity={onGhostSelect ? 0.55 : ((isInner && activeStep === 'Išorė') ? 0.55 : 1)}
          roughness={0.34} metalness={0}
          // isFadade ŠONINIAI paviršiai (žr. ankstesnę pastabą — pirmą kartą matomi,
          // beveik lygiagretūs kamerai/šviesai) — PASIRODĖ, kad vien envMapIntensity
          // (praeito karto bandymas) REALIAI beveik nieko nekeičia tokiam mažo metalness/
          // didelio roughness paviršiui: envMap čia prisideda prie SPEKULIARAUS atspindžio,
          // o ne prie difuzinio apšvietimo, tad jis NETAISĖ problemos (vartotojas
          // patvirtino: "Vis dar yra šešėlis"). `emissive` (PRIDEDAMA tiesiai prie
          // galutinės spalvos, NEPRIKLAUSOMAI nuo šviesos/kameros kampo) yra teisingas
          // įrankis TOKIAM atvejui — čia naudojama PAČIOS plokštės spalva (ne žalsva
          // "hover" spalva), kad paviršius taptų šviesesnis, NE nudažytas kita spalva.
          // onGhostSelect atveju (turi SAVO, nesusijusią spalvų kalbą) šis pakeitimas
          // NETAIKOMAS.
          // metalness 0.06→0, envMapIntensity 1.35→0.2 — NAUJAS, KITOKS simptomas nei
          // aukščiau aprašytas (tas buvo apie difuzinį kampo patamsėjimą, šis — apie
          // kameros-kampo priklausomą Fresnel/IBL atspindį, žr. OuterDoors.jsx
          // DoorFaceMaterial komentarą). Abu pataisymai saugiai sugyvena vienas su kitu.
          envMapIntensity={0}
          // RAUNDAI 4-6 (envMapIntensity boost, tada emissive 0.35, tada 1.0) buvo
          // simptomo gydymas, ne priežasties — bandė pridengti faktą, kad ši dėžė
          // (isFacade atveju) tįsodavo per VISĄ baldo gylį, tad jos ŠONINIAI (±X)
          // paviršiai (aukštis × gylis) realiai buvo dideli, pirmąkart matomi be
          // durelių. Vartotojas pastebėjo kertinį dalyką: "dvi durys viena šalia
          // kitos tokio pat šešėlio nerodo, nors principas tas pats — tik viena
          // plokštė siauresnė". Durys TIKRAI neturi šio efekto, bet NE todėl, kad
          // jų medžiaga kitokia — jų DĖŽĖ pati jau plona Z ašimi (doorThickness =
          // wallThickness, žr. OuterDoors.jsx doorZPosition), tad jų ±X briauna irgi
          // plona/nežymi, lygiai kaip bet kurios kitos durų poros tarpas. Tikrasis
          // pataisymas — žr. žemiau `boxDepthZ`: facade atveju ši dėžė DABAR IRGI
          // tampa plona (tiksliai wallThickness, kaip durys), tad jos ±X paviršius
          // tampa taip pat nežymus kaip durų — joks medžiagos/šviesos apgaudinėjimas
          // nebereikalingas. Grąžinta į TOKIĮ PATĮ emissive/envMapIntensity, kokį jau
          // turi CabinetPanel savo isFacade atveju (vienintelis skirtumas — polygonOffset,
          // kuris lieka, nes tai atskiras, pagrįstas z-fight prieš durų priekį sprendimas).
          // Bendra ryškumo "grindis" (0.3) PAČIOS pertvaros spalva — žr. identišką
          // CabinetPanel komentarą aukščiau (SingleCabinet3D.jsx ~823): tas pats įgaubto
          // 90° kampo (pertvara vs siena/lentyna) patamsėjimas, vartotojo nebepriimtas
          // net po algebra įrodyto nulinio tarpo. onGhostSelect/isInner+Išorė atvejams
          // PALIEKAMA sena, nepakitusi logika (PANEL_HOVER_COLOR/ei) — jie turi SAVO,
          // nesusijusią spalvų kalbą (žalia=paversta/pilka=peržiūra), kurios ši bendra
          // grindis NETURI paliesti.
          emissive={(onGhostSelect || (isInner && activeStep === 'Išorė')) ? PANEL_HOVER_COLOR : currentColor}
          emissiveIntensity={(onGhostSelect || (isInner && activeStep === 'Išorė')) ? ei : (0.3 + (isInteractive ? 0.05 : 0))}
          // -8/-8 → -2/-2 — žr. CabinetPanel identišką komentarą aukščiau (SingleCabinet3D.jsx
          // ~875) ir OuterDoors.jsx DoorFaceMaterial.
          {...(isFacade ? { polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 } : null)}
        />
      </Box>
      {showEdges && <PanelEdges args={wallArgs} position={wallPos} />}
      <group position={wallPos}>
        <HoverEdgeFrame width={wallArgs[0]} height={wallArgs[1]} depth={wallArgs[2]} visible={isHovered || isSelected || isGhostSelected} />
      </group>
      {/* depth={frontZ} (NE wallArgs[2]) — tooltip'as turi rodyti, KIEK PRIEKYJE
          stovi šios pertvaros priekinis paviršius (tas pats dydis, kurį rodo ir
          durelių savo tooltip'as, žr. OuterDoors.jsx doorZPosition+doorThickness/2),
          ne pačios (dabar facade atveju plonos) dėžės storį. */}
      <PanelDepthTooltip depth={frontZ} position={[posX, posY, frontZ + 0.02]} visible={depthHovered} />

      {/* onGhostSelect now shares this SAME wide, nearCenter-restricted hit-box (instead of
          the old thin/strip pair above/below) — the one proven-reliable click target every
          other divider already uses. See the stopPropagation/initialHits comment on the
          thin box above for why two competing thin targets silently ate ghost clicks. */}
      {(isInteractive || onGhostSelect) && (() => {
        const handleHoverMove = (e) => {
          setDepthHovered(true);
          const near = nearCenter(e, isHovered);
          if (near) { e.stopPropagation(); document.body.style.cursor = onGhostSelect ? 'pointer' : (dragOnHover ? dragCursor : 'pointer'); }
          else if (isHovered) document.body.style.cursor = '';
          setIsHovered(near);
        };
        return (
          <Box args={hitArgs} position={hitPos}
            onPointerOver={handleHoverMove}
            onPointerMove={handleHoverMove}
            onPointerOut={() => { setDepthHovered(false); document.body.style.cursor = ''; setIsHovered(false); }}
            onClick={(e) => {
              // stopPropagation PRIEŠ nearCenter patikrą — žr. CabinetPanel komentarą
              // (SingleCabinet3D.jsx) apie tą patį "paspaudimas iškrenta iki
              // FurnitureGroup" klasės klaidą; čia identiška. sticky=isHovered — žr.
              // handleHoverMove aukščiau/edgeHover.jsx EDGE_MARGIN_M_STICKY.
              e.stopPropagation();
              if (!nearCenter(e, isHovered)) return;
              if (onGhostSelect) { onGhostSelect(); return; }
              // Ta pati priežastis/pataisymas, kaip onUp aukščiau — žr. jo komentarą.
              if (!dragOnHover) onSelectSelf?.(dividerKey);
            }}
            onPointerDown={(e) => {
              if (onGhostSelect) return; // select-only, never draggable
              if (!dragOnHover || !nearCenter(e, isHovered)) return;
              e.stopPropagation();
              startMoveDrag(e, true);
            }}
          >
            <meshStandardMaterial transparent opacity={0} depthWrite={false} />
          </Box>
        );
      })()}


      {/* Ghost mode: move_up button when this divider is selected */}
      {onGhostSelect && isGhostSelected && onGhostConvert && (
        <Html position={[posX, posY, depth + 0.02]} center style={{ pointerEvents: 'none' }}>
          <div style={overlayWrapStyle}>
            <OverlayBtn title="Paversti į išorinę pertvarą" onClick={(e) => { e.stopPropagation(); onGhostConvert(); }}>
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>move_up</span>
            </OverlayBtn>
          </div>
        </Html>
      )}

      {/* Outer action buttons: shown when an outer divider is selected in Išorė step —
          demote-to-inner + delete, mirroring the trash icon Vidus/Baldas already have. */}
      {isIsore && isSelected && (onDemote || onSectionDelete || onToggleFront) && (
        <Html position={[posX, posY, frontZ + 0.02]} center style={{ pointerEvents: 'none' }}>
          <div style={overlayWrapStyle}>
            {onToggleFront && (
              <OverlayBtn
                title={isFacade ? "Grąžinti į karkaso plokštumą" : "Ištraukti į priekį (fasadų plokštuma)"}
                onClick={(e) => { e.stopPropagation(); onToggleFront(); }}
              >
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300, color: isFacade ? '#09aa89' : undefined }}>
                  swap_horiz
                </span>
              </OverlayBtn>
            )}
            {onDemote && (
              <OverlayBtn title="Paversti į vidinę pertvarą" onClick={(e) => { e.stopPropagation(); onSelectSelf?.(null); onDemote(); }}>
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>move_down</span>
              </OverlayBtn>
            )}
            {onSectionDelete && (
              <OverlayBtn title="Ištrinti" onClick={(e) => { e.stopPropagation(); onSelectSelf?.(null); onSectionDelete(path); }}>
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>delete</span>
              </OverlayBtn>
            )}
          </div>
        </Html>
      )}

      {/* Visible drag strip in Išorė — thin teal bar on door face so user knows where to grab */}
      {isInteractive && dragOnHover && (
        <Box
          args={[isHorizontal ? width : 0.006, isHorizontal ? 0.006 : height, 0.004]}
          position={[posX, posY, frontZ + 0.026]}
          onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = dragCursor; setIsHovered(true); }}
          onPointerOut={() => { document.body.style.cursor = ''; setIsHovered(false); }}
          onPointerDown={(e) => { e.stopPropagation(); startMoveDrag(e, true); }}
        >
          <meshStandardMaterial color={isSelected ? '#22c55e' : '#60a5fa'} transparent opacity={isSelected || isHovered ? 0.82 : 0} />
        </Box>
      )}

      {isSelected && isInteractive && !isIsore && (
        <Html position={[posX, posY + 0.04, depth + 0.08]} center zIndexRange={[300, 0]}>
          <div style={overlayWrapStyle}>
            {/* Perkelti mygtukas rodomas TIK kai dragOnHover==false (t.y. Baldas žingsnio
                IŠORINĖ pertvara, vienintelis likęs atvejis be tiesioginio vilkimo ant hover) —
                vartotojas: "Sito move mygtuko nereikia, istrink ji, nes dabar galima tiesiog
                su pelyte stumdyti" — TAI teisinga Vidus/isInner atveju (dragOnHover=true ten,
                žr. aukščiau), bet Baldas+išorinei pertvarai dragOnHover lieka false, tad ten
                mygtukas vis dar vienintelis būdas pradėti vilkimą — pašalinus visada, ten
                dingtų bet koks būdas pertvarą perkelti. */}
            {!dragOnHover && (
              <OverlayBtn grab onPointerDown={(e) => { e.stopPropagation(); startMoveDrag(e); }} title="Perkelti (laikyti ir vilkti)">
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>open_with</span>
              </OverlayBtn>
            )}
            <OverlayBtn onClick={(e) => { e.stopPropagation(); onSelectSelf?.(null); if (onSectionDelete) onSectionDelete(path); }} title="Ištrinti">
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>delete</span>
            </OverlayBtn>
          </div>
        </Html>
      )}
    </group>
  );
};

// ── NarySectionDivider ────────────────────────────────────────────────────────
// Interactive divider for N-ary flat splits. Drag to resize, click to delete.
const NarySectionDivider = ({ isHorizontal, posX, posY, wallThickness, width, height, depth, currentColor, activeStep, startRatio, available, onDrag, onDelete, isSelected, onSelectSelf, dividerKey, isInner = false, onGhostSelect = null, isGhostSelected = false, onGhostConvert = null, showEdges = false, isFacade = false, onToggleFront = null, bordersDoor = false, extraBottomExtend = 0 }) => {
  const dragRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [depthHovered, setDepthHovered] = useState(false);
  const { controls, camera, size } = useThree();
  const isInteractive = isInner ? activeStep === 'Vidus' : (activeStep === 'Baldas' || activeStep === 'Išorė');
  const isIsore = !isInner && activeStep === 'Išorė';
  // See DividerDragHandle's identical dragOnHover comment — Vidus dividers now drag directly
  // on hover too, instead of requiring a click-select + separate "Perkelti" button (86cay2ygr).
  const dragOnHover = isIsore || isInner;

  const startMoveDrag = (e, from3D = false) => {
    const nativeE = e.nativeEvent || e;
    if (nativeE.button !== 0) return; // tik kairysis mygtukas — dešinysis nevilkina pertvaros
    const prevEnabled = controls?.enabled;
    if (from3D && controls) controls.enabled = false;
    const startCoord = isHorizontal ? nativeE.clientY : nativeE.clientX;
    const startClientX = nativeE.clientX;
    const startClientY = nativeE.clientY;
    const fovRad = camera.fov * Math.PI / 180;
    const ppm = (size.height / 2) / (camera.position.length() * Math.tan(fovRad / 2));
    dragRef.current = { startCoord, startRatio, ppm };
    document.body.style.cursor = 'grabbing';
    let didDrag = false;
    // Batch to at most one update per animation frame — smoother than dispatching on every
    // raw pointermove event (which can fire much faster than the screen repaints).
    let rafId = null;
    let latestCoord = startCoord;
    const applyFrame = () => {
      rafId = null;
      if (!dragRef.current) return;
      const delta = (latestCoord - dragRef.current.startCoord) / (dragRef.current.ppm * available);
      const newRatio = dragRef.current.startRatio + delta;
      if (onDrag) onDrag(newRatio);
    };
    const onMove = (me) => {
      if (!dragRef.current) return;
      if (!didDrag && (Math.abs(me.clientX - startClientX) > 5 || Math.abs(me.clientY - startClientY) > 5)) didDrag = true;
      latestCoord = isHorizontal ? me.clientY : me.clientX;
      if (rafId == null) rafId = requestAnimationFrame(applyFrame);
    };
    const onUp = () => {
      if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
      dragRef.current = null;
      document.body.style.cursor = '';
      if (!didDrag && dragOnHover) {
        // ClickUp "3.0 Išorė. Briaunų action mygtukų iššaukimas. Sunku kartais
        // iššaukti action mygtukus" — ta pati priežastis/pataisymas, kaip durelių
        // toggle-to-deselect bug'as (Builder3D.jsx handleShelfClick): hover-žalia
        // ir selected-žalia atrodo vienodai, tad paspaudus JAU pažymėtą dalyklę
        // DAR KARTĄ, ji išsijungdavo be jokio matomo pasikeitimo (žalia likdavo
        // dėl hover). Atžymėjimas tuščioje vietoje jau veikia atskirai, tad šis
        // toggle nebereikalingas — paspaudimas dabar VISADA pažymi.
        onSelectSelf?.(dividerKey);
      } else {
        onSelectSelf?.(null);
      }
      if (from3D && controls) controls.enabled = prevEnabled ?? true;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // See DividerDragHandle's identical frontZ comment — same door-aware auto-recess to
  // karkaso plokštuma (B-D), and EXACTLY B (no margin) when pulled to facade.
  const frontZ = depth - ((!isFacade && bordersDoor) ? wallThickness : 0);
  // extraBottomExtend — see DividerDragHandle's identical comment: stretches this vertical
  // divider's bottom edge down to the basisType 7/8 floor plate, same as the 2-way case.
  // Overrides the plain `posY` prop for every use below (mirrors DividerDragHandle, which
  // computes this once and reuses it for the wall, hit box, and every overlay position).
  posY = isHorizontal ? posY : (height - extraBottomExtend) / 2;
  // ATŠAUKTA (vartotojo sprendimu) — žr. identišką DividerDragHandle boxDepthZ
  // komentarą: isFacade plonos juostos pakeista visada-pilno-gylio dėže.
  const boxDepthZ = frontZ;
  const wallArgs = [isHorizontal ? width : wallThickness, isHorizontal ? wallThickness : (height + extraBottomExtend), boxDepthZ];
  const wallPos  = [posX, posY, frontZ - boxDepthZ / 2];
  // See DividerDragHandle's identical hitDepth comment: nominal `depth`+0.08 (not the
  // possibly-recessed `frontZ`) so this reliably beats the neighboring RecursiveSection
  // leaf's depth+0.07 hit-box, in all cases.
  // onGhostSelect (isInner, so isIsore is always false here — same +0.08 branch below):
  // unlike an already-split divider (whose two neighboring leaves each only cover HALF the
  // original width, so the divider sits at the EDGE of each one's own depth+0.07
  // click-catcher — see comment above), a ghost divider lives INSIDE a single, still-unsplit
  // outer leaf (demoted back to interiorStructure) whose OWN "click empty section" catch-all
  // (RecursiveSection leaf branch, ~2345, hitDepth=depth+0.07) spans the ENTIRE original
  // width — putting the ghost divider's hit-box deep inside that catch-all instead of at its
  // boundary (vartotojas: "Kai paverciu i vidine pertvara man jau nebeleidzia paspausti ant
  // jos ir padaryti ja isorine" — still broken after the pointerdown-drag conflict and the
  // dual-hitbox-ambiguity fixes). Confirmed live via ray-AABB timings that a NARROW hit-box
  // here never wins this specific race no matter how deep hitDepth reaches: with
  // width=HIT_HALF*2, ray-AABB entry time is dominated by the (late-satisfied) X axis, not Z,
  // so extra depth never even factors in — the actual fix is widening hitArgs below (to match
  // the catch-all's own full `width`, removing X as the limiting axis) so the EXISTING +0.08
  // margin over the catch-all's +0.07 finally gets to decide the race on Z. Deliberately NOT
  // pushing hitDepth further than +0.08 to compensate: a much larger hitDepth (tried
  // depth+0.2, combined with the widened box) reaches a front-face plane far enough forward
  // that, at an angled camera, the SAME screen ray lands on a world point perceptibly offset
  // from the true divider position (perspective/parallax, not a bug in nearCenter) — enough
  // to fail nearCenter's fixed ~3cm/7cm margin outright.
  const hitDepth = isIsore ? frontZ + 0.05 : depth + 0.08;
  // BUVO 0.09 čia (tik pusė DividerDragHandle HIT_HALF*2=0.16 pločio) — atrodo kopijavimo
  // klaida iš žemiau esančios "ghost" juostos savo 0.09 konstantos (žr. ten), NE iš
  // DividerDragHandle pagrindinio hitArgs. Dėl to N-dalių (3+) pertvaros turėjo ~44%
  // siauresnę paspaudimo juostą už 2-dalių pertvarų — sunkiau pataikyti tame pačiame
  // kameros kampe. Suvienodinta su DividerDragHandle HIT_HALF*2.
  const DIVIDER_HIT_WIDTH = 0.16;
  // onGhostSelect: see the matching comment on DividerDragHandle's hitArgs — a narrow
  // ray-AABB axis delays this box's effective ray-entry regardless of hitDepth, letting the
  // leaf catch-all's full-`width`/full-height box win the raycast despite reaching less deep.
  const hitArgs  = [
    isHorizontal ? width : (onGhostSelect ? width : DIVIDER_HIT_WIDTH),
    isHorizontal ? (onGhostSelect ? (height + extraBottomExtend) : DIVIDER_HIT_WIDTH) : (height + extraBottomExtend),
    hitDepth,
  ];
  const hitPos   = [posX, posY, hitDepth / 2];
  // See DividerDragHandle's identical isNearStripCenter comment: hover/vilkimas/
  // paspaudimas galioja tik arti plataus hit-box'o CENTRO (ne isNearPanelEdge).
  // sticky=true (žr. edgeHover.jsx EDGE_MARGIN_M_STICKY) — kai pertvara JAU laikoma
  // "arti centro" (žalia), platesnė riba, kol vėl užgęsta, vartotojas: "labai sudetinga
  // paspausti... kazkokia papildoma atsargine vieta... ji nesiaktyvuotu kol nera
  // pataikoma tiksliai ant briaunos".
  // onGhostSelect: e.uv/e.point reflect where the ray hit the WIDENED hit-box's OWN
  // pushed-forward Z-plane (hitArgs/hitPos below, deliberately different from the thin
  // visible divider) — at an angled camera that plane sits far enough ahead of the true
  // divider position that the ray crosses it at a world (x,y) that has ALREADY visibly
  // drifted sideways from posX/posY by real meters (perspective/parallax: the ray is not
  // parallel to the Z axis, so "further forward" also means "further sideways"). Comparing
  // the raw hit point (e.point, even via worldToLocal) against posX/posY inherited this
  // drift and still failed nearCenter after hitDepth was pulled back to the usual +0.08
  // margin (vartotojas: "Kai paverciu i vidine pertvara man jau nebeleidzia paspausti ant
  // jos ir padaryti ja isorine"). The fix re-derives the ORIGINAL ray (e.ray, independent of
  // which hit-box face it actually crossed) and intersects it with the true divider's own
  // Z-plane (wallPos[2] below) instead — giving the (x,y) this click would have hit had the
  // visible, un-pushed divider itself been the thing in front of the camera.
  const nearCenter = (e, sticky = false) => {
    // onGhostSelect always gets the wider STICKY margin, not just sticky===true: the
    // isHovered React state this normally gates on is set imperatively inside
    // handleHoverMove (document.body.style.cursor) but read back as plain component state —
    // the click (mousedown+mouseup, no further pointermove) can fire before that state
    // update has actually flushed/re-rendered, so `sticky` here can still read its OLD
    // (false) value even though the cursor visibly already flipped to 'pointer'. A ghost
    // divider is a deliberate, infrequent edit action, not a dense click target competing
    // for precision against neighbors, so there is no reason to risk the narrow margin here.
    const margin = (sticky || onGhostSelect) ? EDGE_MARGIN_M_STICKY : EDGE_MARGIN_M;
    if (onGhostSelect) {
      const obj = e?.eventObject || e?.object;
      if (!e?.ray || !obj?.parent) return true;
      const invMatrix = obj.parent.matrixWorld.clone().invert();
      const localOrigin = e.ray.origin.clone().applyMatrix4(invMatrix);
      const localDir = e.ray.origin.clone().add(e.ray.direction).applyMatrix4(invMatrix).sub(localOrigin);
      if (Math.abs(localDir.z) < 1e-9) return true;
      const t = (wallPos[2] - localOrigin.z) / localDir.z;
      const hitX = localOrigin.x + localDir.x * t;
      const hitY = localOrigin.y + localDir.y * t;
      return isHorizontal ? Math.abs(hitY - posY) <= margin : Math.abs(hitX - posX) <= margin;
    }
    return isHorizontal
      ? isNearStripCenter(e?.uv?.y, hitArgs[1], sticky ? EDGE_MARGIN_M_STICKY : undefined)
      : isNearStripCenter(e?.uv?.x, hitArgs[0], sticky ? EDGE_MARGIN_M_STICKY : undefined);
  };
  const ei = isInteractive ? 0.05 : 0;
  const dragCursor = isHorizontal ? 'ns-resize' : 'ew-resize';

  return (
    <group>
      {/* Ši (plona, MATOMA) dėžė anksčiau TURĖJO savo pačios, NIEKUO neapribotą
          isInteractive hover/click logiką — lygiagrečiai su žemiau esančiu plačiu
          "hit" Box'u (isInteractive blokas), kuris TĄ PATĮ darbą jau daro TEISINGAI,
          apribotas iki nearCenter. Kadangi platus hit-box'as stovi PRIEKYJE (jo
          artimiausias paviršius arčiau kameros) ir savo onPointerOver/onPointerDown
          NESUSTABDO sklidimo, kai nearCenter=false (žr. handleHoverMove), įvykis
          KELIAUDAVO TOLIAU iki šios plonos dėžės, kurios besąlyginis `setIsHovered(true)`
          "laimėdavo" lenktynes PRIEŠ plataus hit-box'o teisingą `setIsHovered(false)` —
          dėl to žalias rėmelis/vilkimas suveikdavo VISAME plačiame plote, ne tik ties
          pertvaros viduriu (vartotojas: "Zalia hover spalva turi pasirodyti TIK kai
          pelyte uzeinu ant plokstes briaunos o ne pacios plokstes"). isInteractive
          atveju platus hit-box'as VISADA sumontuotas (žr. {isInteractive && (...)}
          žemiau), tad ši dėžė jam daugiau nebekonkuruoja — telieka onGhostSelect
          (kur plataus hit-box'o apskritai nėra) ir pačios Gylio piktogramos
          setDepthHovered, kuris TYČIA lieka besąlyginis visiems atvejams. */}
      <Box args={wallArgs} position={wallPos} castShadow receiveShadow
        // Ghost mode's own hover/click used to live HERE (on this thin, exact-size box) —
        // moved entirely to the wide hit-box below (now rendered for onGhostSelect too,
        // see {(isInteractive || onGhostSelect) && ...}), matching how every NON-ghost
        // divider already works (this thin box never had onClick for the non-ghost case —
        // only the wide hit-box did). Two separate hit targets (this thin box AND the old
        // dedicated ghost-strip further down) both claiming onClick for the SAME divider
        // meant whichever one the raycaster picked up on pointerdown didn't reliably match
        // the one picked up on the later click event (r3f only fires onClick when the
        // SAME object was also hit on pointerdown) — the click silently did nothing even
        // after the pointerdown/drag conflict above was fixed (vartotojas: "Kai paverciu i
        // vidine pertvara man jau nebeleidzia paspausti ant jos ir padaryti ja isorine").
        onPointerOver={() => setDepthHovered(true)}
        onPointerOut={() => setDepthHovered(false)}
        // !onGhostSelect guard: a ghost-candidate divider (promote-to-outer preview, Išorė
        // tab) is select-only, never draggable — but onPointerDown fired unconditionally
        // whenever dragOnHover && !isInteractive, which is ALSO true for isInner ghost
        // dividers (dragOnHover = isIsore || isInner). That pointerdown called
        // startMoveDrag, whose onUp falls back to onSelectSelf (null here, ghost mode's
        // nested RecursiveSection passes onDividerSelect={null}) instead of onClick's
        // onGhostSelect — r3f never raises onClick once onPointerDown has claimed the
        // pointer, so the click silently did nothing. A just-demoted divider became
        // permanently unclickable (vartotojas: "isorine karkasine plokste paverciant i
        // vidine, nera galimybes paspausti ant jos ir ja vel paversti isorine").
        onPointerDown={(dragOnHover && !isInteractive && !onGhostSelect) ? (e) => { e.stopPropagation(); startMoveDrag(e, true); } : undefined}
      >
        <meshStandardMaterial
          // Vartotojas: "vidine pertvara [...] turetu veikti taip pat kaip ir kitos
          // plokstes kai paspaudziu ant briaunos galiu ja redaguoti" — onGhostSelect
          // (vidinės sekcijos pertvaros kandidato žymėjimas, prieš "Paversti į
          // išorinę") anksčiau NUDAŽYDAVO VISĄ paviršių (pilka→mėlyna hover→žalia
          // select), skirtingai nuo VISŲ kitų šio baldo plokščių, kur grįžtamasis
          // ryšys yra TIK plonas krašto kontūras (HoverEdgeFrame žemiau, anksčiau
          // rodomas TIK {!onGhostSelect}), o pats paviršius visada rodo vienodą
          // spalvą. Spalva/opacity dabar FIKSUOTA (be eskalacijos) abiem atvejais —
          // hover/select grįžtamasis ryšys perkeltas į bendrą HoverEdgeFrame žemiau.
          color={onGhostSelect ? '#b0bec5' : ((isInner && activeStep === 'Išorė') ? '#b0bec5' : currentColor)}
          transparent={isInner && activeStep === 'Išorė'}
          opacity={onGhostSelect ? 0.55 : ((isInner && activeStep === 'Išorė') ? 0.55 : 1)}
          roughness={0.34} metalness={0}
          // isFadade ŠONINIAI paviršiai (žr. ankstesnę pastabą — pirmą kartą matomi,
          // beveik lygiagretūs kamerai/šviesai) — PASIRODĖ, kad vien envMapIntensity
          // (praeito karto bandymas) REALIAI beveik nieko nekeičia tokiam mažo metalness/
          // didelio roughness paviršiui: envMap čia prisideda prie SPEKULIARAUS atspindžio,
          // o ne prie difuzinio apšvietimo, tad jis NETAISĖ problemos (vartotojas
          // patvirtino: "Vis dar yra šešėlis"). `emissive` (PRIDEDAMA tiesiai prie
          // galutinės spalvos, NEPRIKLAUSOMAI nuo šviesos/kameros kampo) yra teisingas
          // įrankis TOKIAM atvejui — čia naudojama PAČIOS plokštės spalva (ne žalsva
          // "hover" spalva), kad paviršius taptų šviesesnis, NE nudažytas kita spalva.
          // onGhostSelect atveju (turi SAVO, nesusijusią spalvų kalbą) šis pakeitimas
          // NETAIKOMAS.
          // metalness 0.06→0, envMapIntensity 1.35→0.2 — NAUJAS, KITOKS simptomas nei
          // aukščiau aprašytas (tas buvo apie difuzinį kampo patamsėjimą, šis — apie
          // kameros-kampo priklausomą Fresnel/IBL atspindį, žr. OuterDoors.jsx
          // DoorFaceMaterial komentarą). Abu pataisymai saugiai sugyvena vienas su kitu.
          envMapIntensity={0}
          // RAUNDAI 4-6 (envMapIntensity boost, tada emissive 0.35, tada 1.0) buvo
          // simptomo gydymas, ne priežasties — bandė pridengti faktą, kad ši dėžė
          // (isFacade atveju) tįsodavo per VISĄ baldo gylį, tad jos ŠONINIAI (±X)
          // paviršiai (aukštis × gylis) realiai buvo dideli, pirmąkart matomi be
          // durelių. Vartotojas pastebėjo kertinį dalyką: "dvi durys viena šalia
          // kitos tokio pat šešėlio nerodo, nors principas tas pats — tik viena
          // plokštė siauresnė". Durys TIKRAI neturi šio efekto, bet NE todėl, kad
          // jų medžiaga kitokia — jų DĖŽĖ pati jau plona Z ašimi (doorThickness =
          // wallThickness, žr. OuterDoors.jsx doorZPosition), tad jų ±X briauna irgi
          // plona/nežymi, lygiai kaip bet kurios kitos durų poros tarpas. Tikrasis
          // pataisymas — žr. žemiau `boxDepthZ`: facade atveju ši dėžė DABAR IRGI
          // tampa plona (tiksliai wallThickness, kaip durys), tad jos ±X paviršius
          // tampa taip pat nežymus kaip durų — joks medžiagos/šviesos apgaudinėjimas
          // nebereikalingas. Grąžinta į TOKIĮ PATĮ emissive/envMapIntensity, kokį jau
          // turi CabinetPanel savo isFacade atveju (vienintelis skirtumas — polygonOffset,
          // kuris lieka, nes tai atskiras, pagrįstas z-fight prieš durų priekį sprendimas).
          // Bendra ryškumo "grindis" (0.3) PAČIOS pertvaros spalva — žr. identišką
          // CabinetPanel komentarą aukščiau (SingleCabinet3D.jsx ~823): tas pats įgaubto
          // 90° kampo (pertvara vs siena/lentyna) patamsėjimas, vartotojo nebepriimtas
          // net po algebra įrodyto nulinio tarpo. onGhostSelect/isInner+Išorė atvejams
          // PALIEKAMA sena, nepakitusi logika (PANEL_HOVER_COLOR/ei) — jie turi SAVO,
          // nesusijusią spalvų kalbą (žalia=paversta/pilka=peržiūra), kurios ši bendra
          // grindis NETURI paliesti.
          emissive={(onGhostSelect || (isInner && activeStep === 'Išorė')) ? PANEL_HOVER_COLOR : currentColor}
          emissiveIntensity={(onGhostSelect || (isInner && activeStep === 'Išorė')) ? ei : (0.3 + (isInteractive ? 0.05 : 0))}
          // -8/-8 → -2/-2 — žr. CabinetPanel identišką komentarą aukščiau (SingleCabinet3D.jsx
          // ~875) ir OuterDoors.jsx DoorFaceMaterial.
          {...(isFacade ? { polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 } : null)}
        />
      </Box>
      {showEdges && <PanelEdges args={wallArgs} position={wallPos} />}
      <group position={wallPos}>
        <HoverEdgeFrame width={wallArgs[0]} height={wallArgs[1]} depth={wallArgs[2]} visible={isHovered || isSelected || isGhostSelected} />
      </group>
      {/* depth={frontZ} (NE wallArgs[2]) — tooltip'as turi rodyti, KIEK PRIEKYJE
          stovi šios pertvaros priekinis paviršius (tas pats dydis, kurį rodo ir
          durelių savo tooltip'as, žr. OuterDoors.jsx doorZPosition+doorThickness/2),
          ne pačios (dabar facade atveju plonos) dėžės storį. */}
      <PanelDepthTooltip depth={frontZ} position={[posX, posY, frontZ + 0.02]} visible={depthHovered} />

      {/* onGhostSelect now shares this SAME wide, nearCenter-restricted hit-box (instead of
          the old thin/strip pair above/below) — the one proven-reliable click target every
          other divider already uses. See the stopPropagation/initialHits comment on the
          thin box above for why two competing thin targets silently ate ghost clicks. */}
      {(isInteractive || onGhostSelect) && (() => {
        const handleHoverMove = (e) => {
          setDepthHovered(true);
          const near = nearCenter(e, isHovered);
          if (near) { e.stopPropagation(); document.body.style.cursor = onGhostSelect ? 'pointer' : (dragOnHover ? dragCursor : 'pointer'); }
          else if (isHovered) document.body.style.cursor = '';
          setIsHovered(near);
        };
        return (
          <Box args={hitArgs} position={hitPos}
            onPointerOver={handleHoverMove}
            onPointerMove={handleHoverMove}
            onPointerOut={() => { setDepthHovered(false); document.body.style.cursor = ''; setIsHovered(false); }}
            onClick={(e) => {
              // stopPropagation PRIEŠ nearCenter patikrą — žr. CabinetPanel komentarą
              // (SingleCabinet3D.jsx) apie tą patį "paspaudimas iškrenta iki
              // FurnitureGroup" klasės klaidą; čia identiška. sticky=isHovered — žr.
              // handleHoverMove aukščiau/edgeHover.jsx EDGE_MARGIN_M_STICKY.
              e.stopPropagation();
              if (!nearCenter(e, isHovered)) return;
              if (onGhostSelect) { onGhostSelect(); return; }
              // Ta pati priežastis/pataisymas, kaip onUp aukščiau — žr. jo komentarą.
              if (!dragOnHover) onSelectSelf?.(dividerKey);
            }}
            onPointerDown={(e) => {
              if (onGhostSelect) return; // select-only, never draggable
              if (!dragOnHover || !nearCenter(e, isHovered)) return;
              e.stopPropagation();
              startMoveDrag(e, true);
            }}
          >
            <meshStandardMaterial transparent opacity={0} depthWrite={false} />
          </Box>
        );
      })()}


      {/* Ghost mode: move_up button when selected */}
      {onGhostSelect && isGhostSelected && onGhostConvert && (
        <Html position={[posX, posY, depth + 0.02]} center style={{ pointerEvents: 'none' }}>
          <div style={overlayWrapStyle}>
            <OverlayBtn title="Paversti į išorinę pertvarą" onClick={(e) => { e.stopPropagation(); onGhostConvert(); }}>
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>move_up</span>
            </OverlayBtn>
          </div>
        </Html>
      )}

      {/* Visible drag strip in Išorė — thin teal bar on door face so user knows where to grab */}
      {isInteractive && dragOnHover && (
        <Box
          args={[isHorizontal ? width : 0.006, isHorizontal ? 0.006 : height, 0.004]}
          position={[posX, posY, frontZ + 0.026]}
          onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = dragCursor; setIsHovered(true); }}
          onPointerOut={() => { document.body.style.cursor = ''; setIsHovered(false); }}
          onPointerDown={(e) => { e.stopPropagation(); startMoveDrag(e, true); }}
        >
          <meshStandardMaterial color={isSelected ? '#22c55e' : '#60a5fa'} transparent opacity={isSelected || isHovered ? 0.82 : 0} />
        </Box>
      )}

      {/* "Ištraukta į priekį" toggle — same feature as DividerDragHandle's, for N-ary splits.
          Delete is included here too (mirroring DividerDragHandle's isIsore block) — it used
          to live only in the !isIsore block below, so an Išorė-step N-ary divider showed the
          facade toggle but no way to delete it at all (see /admin/bugs "Grazinti i karkaso
          plokstuma vietoj istrinti"). */}
      {isIsore && isSelected && (onToggleFront || onDelete) && (
        <Html position={[posX, posY, frontZ + 0.02]} center style={{ pointerEvents: 'none' }}>
          <div style={overlayWrapStyle}>
            {onToggleFront && (
              <OverlayBtn
                title={isFacade ? "Grąžinti į karkaso plokštumą" : "Ištraukti į priekį (fasadų plokštuma)"}
                onClick={(e) => { e.stopPropagation(); onToggleFront(); }}
              >
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300, color: isFacade ? '#09aa89' : undefined }}>
                  swap_horiz
                </span>
              </OverlayBtn>
            )}
            {onDelete && (
              <OverlayBtn onClick={(e) => { e.stopPropagation(); onSelectSelf?.(null); onDelete(); }} title="Ištrinti pertvarą">
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>delete</span>
              </OverlayBtn>
            )}
          </div>
        </Html>
      )}

      {isSelected && isInteractive && !isIsore && (
        <Html position={[posX, posY + 0.04, depth + 0.08]} center zIndexRange={[300, 0]}>
          <div style={overlayWrapStyle}>
            {/* Perkelti mygtukas tik kai dragOnHover==false — žr. identišką DividerDragHandle
                komentarą. */}
            {!dragOnHover && (
              <OverlayBtn grab onPointerDown={(e) => { e.stopPropagation(); startMoveDrag(e); }} title="Perkelti (laikyti ir vilkti)">
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>open_with</span>
              </OverlayBtn>
            )}
            <OverlayBtn onClick={(e) => { e.stopPropagation(); onSelectSelf?.(null); if (onDelete) onDelete(); }} title="Ištrinti pertvarą">
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>delete</span>
            </OverlayBtn>
          </div>
        </Html>
      )}
    </group>
  );
};

// ── DividerStrip ─────────────────────────────────────────────────────────────
// Colored overlay on an inner panel — changes the panel's color on hover/select.
const DividerStrip = ({ px, py, aw, ah, depth, isSelected, onSelect, onConvert }) => {
  const [hovered, setHovered] = useState(false);
  const color = isSelected ? '#22c55e' : hovered ? '#3b82f6' : '#b0bec5';
  const opacity = isSelected ? 0.95 : hovered ? 0.88 : 0.55;
  return (
    <group>
      <Box
        args={[aw, ah, 0.001]}
        position={[px, py, depth + 0.003]}
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHovered(false); document.body.style.cursor = ''; }}
      >
        <meshStandardMaterial color={color} transparent opacity={opacity} depthWrite={false} />
      </Box>
      {isSelected && (
        <Html position={[px, py, depth + 0.01]} center style={{ pointerEvents: 'none' }}>
          <div style={overlayWrapStyle}>
            <OverlayBtn title="Paversti į išorinę pertvarą" onClick={(e) => { e.stopPropagation(); onConvert(); }}>
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>move_up</span>
            </OverlayBtn>
          </div>
        </Html>
      )}
    </group>
  );
};

// ── AllInnerPanelDividers ─────────────────────────────────────────────────────
// Shows clickable strips for ALL inner panel dividers at ALL nesting levels.
// onConvert(nodePathParts, dividerIdx) — path from inner root to target node.
const AllInnerPanelDividers = ({ node, width, height, depth, wallThickness, outerPath, selectedPanel, onSelectPanel, onConvert }) => {
  if (!node || !node.split) return null;

  const dividers = [];
  const collect = (n, nodePathParts, x, y, w, h) => {
    if (!n || !n.split) return;
    const isH = n.split === 'h';
    const N = (n.children || []).length;
    if (N < 2) return;
    if (N === 2) {
      const ratio = n.ratio ?? 0.5;
      const available = isH ? (h - wallThickness) : (w - wallThickness);
      const child0Size = available * ratio;
      const child1Size = available * (1 - ratio);
      // child0=TOP at y+child1Size+wt, child1=BOTTOM at y; divider center between them
      const cx = isH ? x + w / 2 : x + child0Size + wallThickness / 2;
      const cy = isH ? y + child1Size + wallThickness / 2 : y + h / 2;
      dividers.push({ nodePathParts: [...nodePathParts], dividerIdx: 0, cx, cy, dw: isH ? w : wallThickness, dh: isH ? wallThickness : h });
      if (isH) {
        collect(n.children[0], [...nodePathParts, 0], x, y + child1Size + wallThickness, w, child0Size);
        collect(n.children[1], [...nodePathParts, 1], x, y, w, child1Size);
      } else {
        collect(n.children[0], [...nodePathParts, 0], x, y, child0Size, h);
        collect(n.children[1], [...nodePathParts, 1], x + child0Size + wallThickness, y, child1Size, h);
      }
    } else {
      const available = isH ? (h - wallThickness * (N - 1)) : (w - wallThickness * (N - 1));
      const sizes = (n.sizes || Array(N).fill(1 / N)).map(s => s * available);
      if (isH) {
        const hOffsets = new Array(N);
        let off = 0;
        for (let i = N - 1; i >= 0; i--) { hOffsets[i] = off; off += sizes[i] + wallThickness; }
        for (let i = 0; i < N - 1; i++) {
          dividers.push({ nodePathParts: [...nodePathParts], dividerIdx: i, cx: x + w / 2, cy: y + hOffsets[i] - wallThickness / 2, dw: w, dh: wallThickness });
        }
        for (let i = 0; i < N; i++) collect(n.children[i], [...nodePathParts, i], x, y + hOffsets[i], w, sizes[i]);
      } else {
        let off = 0;
        for (let i = 0; i < N; i++) {
          if (i < N - 1) dividers.push({ nodePathParts: [...nodePathParts], dividerIdx: i, cx: x + off + sizes[i] + wallThickness / 2, cy: y + h / 2, dw: wallThickness, dh: h });
          collect(n.children[i], [...nodePathParts, i], x + off, y, sizes[i], h);
          off += sizes[i] + wallThickness;
        }
      }
    }
  };
  collect(node, [], 0, 0, width, height);

  return (
    <group>
      {dividers.map((div) => {
        const key = `${div.nodePathParts.join(',')}-${div.dividerIdx}`;
        const isThisSelected = selectedPanel && selectedPanel.outerPath === outerPath && selectedPanel.key === key;
        return (
          <DividerStrip
            key={key}
            px={div.cx}
            py={div.cy}
            aw={div.dw}
            ah={div.dh}
            depth={depth}
            isSelected={isThisSelected}
            onSelect={() => onSelectPanel(isThisSelected ? null : { outerPath, key, nodePathParts: div.nodePathParts, dividerIdx: div.dividerIdx })}
            onConvert={() => onConvert(div.nodePathParts, div.dividerIdx)}
          />
        );
      })}
    </group>
  );
};

// ── InternalItemGroup — hover + click ────────────────────────────────────────────
// `forcedHighlightColor`: when set (not undefined), a section-wide hover zone owns the
// highlight/click for every item — this one no longer tracks/reacts to its own hover or
// click (see VIDUS_ITEM_3D_INTERACTION_DISABLED in InternalSectionArray).
const InternalItemGroup = ({ position, renderContent, w, itemH, depth, isSelected, onItemClick, suppressHover, onStartDrag, forcedHighlightColor }) => {
  const [hovered, setHovered] = useState(false);
  const groupRef = useRef();
  const useForced = forcedHighlightColor !== undefined;

  // When item becomes selected, onPointerOver/Out are removed — clear stale hover
  // so it doesn't persist as blue after the item is deselected later
  useEffect(() => {
    if (isSelected) setHovered(false);
  }, [isSelected]);

  const highlightColor = useForced
    ? forcedHighlightColor
    : (isSelected ? '#22c55e' : (!suppressHover && hovered ? '#3b82f6' : null));

  const handleGroupClick = (e) => {
    e.stopPropagation();
    if (!isSelected && onItemClick) onItemClick();
  };

  const handleButtonMouseDown = (ev) => {
    if (!onStartDrag || !groupRef.current) return;
    ev.preventDefault();
    const worldPos = new THREE.Vector3();
    groupRef.current.getWorldPosition(worldPos);
    // Pass world Y of the grab point (button center = itemH/2 above group origin)
    // and the Z plane to intersect during drag for accurate raycasting
    onStartDrag(worldPos.y + itemH / 2, worldPos.z + depth);
  };

  return (
    <group ref={groupRef} position={position} onClick={useForced ? undefined : handleGroupClick}>
      {renderContent(highlightColor)}

      {!useForced && (
        <Box
          args={[w, itemH, depth]}
          position={[w / 2, itemH / 2, depth / 2 + 0.001]}
          onPointerOver={!isSelected ? (e) => { e.stopPropagation(); setHovered(true); } : undefined}
          onPointerOut={!isSelected ? () => setHovered(false) : undefined}
        >
          <meshStandardMaterial transparent opacity={0.001} depthWrite={false} />
        </Box>
      )}

      {isSelected && (
        <Html center position={[w / 2, itemH / 2, depth + 0.01]} zIndexRange={[200, 0]}>
          <div
            style={{
              borderRadius: tokens.borderRadiusCircular,
              cursor: onStartDrag ? 'ns-resize' : 'default',
              width: 26, height: 26,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              userSelect: 'none',
              background: '#fff', color: '#111',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              pointerEvents: onStartDrag ? 'auto' : 'none',
            }}
            onMouseDown={handleButtonMouseDown}
          >
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>open_with</span>
          </div>
        </Html>
      )}
    </group>
  );
};

// ── InternalSectionArray — items chained from the section's top boundary ─────────
// Mirrors the equivalent constants/logic in InnerSelectingDialog.jsx (the authoritative
// editor for a section's stacked items) — just enough to reproduce ITS OWN gap rules
// here so positions can be recomputed live from the CURRENT section height on every
// render, instead of trusting a stored posY that goes stale the moment the section's
// own height changes from an unrelated resize elsewhere (the shelf/divider above it).
const STACK_SHELF_TYPES = new Set([10, 11]);
const STACK_SHELF_TOP_GAP_DEFAULT_CM = { 10: 27, 11: 27 };
const STACK_ITEM_GAP_CM = 2.5; // DRAWER_GAP in the dialog — fixed gap above every non-first item
const STACK_DRAWER_MIN_TOP_GAP_CM = 2.5;
const STACK_HANGER_TYPES = new Set([1, 2, 8, 9]);
const STACK_HANGER_MIN_HEIGHT_CM = { 1: 30, 2: 30, 8: 30, 9: 91 };

const getLiveGapAboveCm = (item, idx) => {
  if (STACK_SHELF_TYPES.has(item.type)) return item.topGap ?? STACK_SHELF_TOP_GAP_DEFAULT_CM[item.type];
  if (idx === 0) return item.topGapCm ?? 0;
  return STACK_ITEM_GAP_CM;
};

// Each item's position is DERIVED, not stored: item[0] hangs `topGapCm` (or a shelf
// type's own `topGap`) below the section's top edge, and every item after it hangs a
// fixed gap below the one before — so the whole chain stays visually anchored to
// whatever's above it (shelf or ceiling) and moves together as a single block whenever
// that boundary moves, exactly like a real physical drawer stack bolted to the shelf.
// A TRAILING hanger (nothing below it) is the one exception: instead of keeping its own
// stored height, it always fills 100% of whatever space is actually left below it —
// growing or shrinking together with the shelf above, down to its own minimum — instead
// of leaving unused free space (or overflowing) whenever a neighboring resize changes
// how much room is actually available.
const withStackedPosY = (items, sectionH) => {
  let topEdge = sectionH || 0;
  return items.map((item, idx) => {
    const gapAbove = getLiveGapAboveCm(item, idx) / 100;
    const isLast = idx === items.length - 1;
    let itemH;
    if (isLast && STACK_HANGER_TYPES.has(item.type)) {
      const minH = (STACK_HANGER_MIN_HEIGHT_CM[item.type] ?? 30) / 100;
      itemH = Math.max(minH, topEdge - gapAbove);
    } else {
      itemH = (item.height || 30) / 100;
    }
    const posY = Math.max(0, topEdge - gapAbove - itemH);
    topEdge = posY;
    return { ...item, posY, height: itemH * 100 };
  });
};

// TEMPORARY: direct 3D selection/drag of "Vidus" stacked items (stalčiai/lentynos/
// kabyklos) is disabled — clicking any item just opens the configuration dialog
// straight away instead. None of the selection/drag code below was removed; flip this
// back to false to restore it.
const VIDUS_ITEM_3D_INTERACTION_DISABLED = true;

const InternalSectionArray = ({ items, w, safeDepth, sectionH, selectedItemIndex, onItemClick, onReorder, renderItemContent, showDimensions }) => {
  const { controls, camera, gl } = useThree();
  const [dragState, setDragState] = useState(null);
  const dragRef = useRef(null);
  // TEMPORARY (see VIDUS_ITEM_3D_INTERACTION_DISABLED): hover is a single zone covering
  // the whole section instead of each item having its own — matches the whole section
  // now being one click target that opens the configuration dialog.
  const [sectionHovered, setSectionHovered] = useState(false);

  // Positions recomputed live from the CURRENT section height every render (see
  // withStackedPosY) — never trust a stored posY, it goes stale the moment the
  // section's own height changes.
  const itemsWithPos = useMemo(() => withStackedPosY(items, sectionH), [items, sectionH]);

  const startDrag = useCallback((itemIdx, grabWorldY, planeZ) => {
    // The whole stack is one rigid, top-anchored block — grabbing ANY item drags the
    // ENTIRE group together (only the group's own distance from the boundary above it
    // changes; the fixed gaps between individual items never do).
    const first = itemsWithPos[0];
    const last = itemsWithPos[itemsWithPos.length - 1];
    if (!first || !last) return;
    const firstH = (first.height || 30) / 100;
    const currentTopGapM = (sectionH || 0) - (first.posY + firstH);
    const minTopGapM = (first.type === 4 ? STACK_DRAWER_MIN_TOP_GAP_CM : 0) / 100;
    const maxUp = Math.max(0, currentTopGapM - minTopGapM);
    const maxDown = Math.max(0, last.posY);

    // XY plane at the item's front face — ray intersections give world Y of the mouse
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -planeZ);
    const canvas = gl.domElement;

    dragRef.current = {
      itemIdx,
      displayItems: [...itemsWithPos],
      lowerBound: -maxDown, upperBound: maxUp,
      finalOffsetDy: 0,
      grabWorldY,
      plane,
    };
    setDragState({ displayItems: [...itemsWithPos], currentIdx: itemIdx, offsetDy: 0 });

    let rafId = null;
    let latestEvent = null;

    const applyFrame = () => {
      rafId = null;
      const ref = dragRef.current;
      if (!ref || !latestEvent) return;
      const rect = canvas.getBoundingClientRect();
      const ndcX = (latestEvent.clientX - rect.left) / rect.width * 2 - 1;
      const ndcY = -((latestEvent.clientY - rect.top) / rect.height * 2 - 1);
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
      const target = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(ref.plane, target)) return;
      // rawDy: how much the world Y under the mouse changed since drag start
      const rawDy = target.y - ref.grabWorldY;
      const clampedDy = Math.max(ref.lowerBound, Math.min(ref.upperBound, rawDy));
      ref.finalOffsetDy = clampedDy;
      setDragState({ displayItems: ref.displayItems, currentIdx: ref.itemIdx, offsetDy: clampedDy });
    };

    const onMouseMove = (ev) => {
      if (!dragRef.current) return;
      latestEvent = ev;
      if (rafId == null) rafId = requestAnimationFrame(applyFrame);
    };

    const onMouseUp = () => {
      if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
      if (dragRef.current) {
        const { displayItems, itemIdx: draggedIdx, finalOffsetDy } = dragRef.current;
        if (onReorder && Math.abs(finalOffsetDy || 0) > 0.001) {
          // Positive offsetDy = shifted up = the group's own gap above it shrank.
          const deltaCm = finalOffsetDy * 100;
          const firstItem = displayItems[0];
          const newFirst = STACK_SHELF_TYPES.has(firstItem.type)
            ? { ...firstItem, topGap: Math.max(0, (firstItem.topGap ?? STACK_SHELF_TOP_GAP_DEFAULT_CM[firstItem.type]) - deltaCm) }
            : { ...firstItem, topGapCm: Math.max(firstItem.type === 4 ? STACK_DRAWER_MIN_TOP_GAP_CM : 0, (firstItem.topGapCm ?? 0) - deltaCm) };
          const updatedItems = displayItems.map((item, i) => i === 0 ? newFirst : item);
          onReorder(updatedItems, draggedIdx);
        }
        dragRef.current = null;
      }
      setDragState(null);
      document.body.style.cursor = '';
      if (controls) controls.enabled = true;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.body.style.cursor = 'grabbing';
    if (controls) controls.enabled = false;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [itemsWithPos, onReorder, sectionH, controls, camera, gl]);

  const dragging = dragState !== null;
  const displayItems = dragging ? dragState.displayItems : itemsWithPos;
  const sectionHasSelection = selectedItemIndex !== -1;

  const forcedItemColor = VIDUS_ITEM_3D_INTERACTION_DISABLED
    ? (sectionHovered ? '#3b82f6' : null)
    : undefined;

  return (
    <>
      {displayItems.map((item, i) => {
        const itemH = (item.height || 30) / 100;
        const itemIsSelected = dragging ? (i === dragState.currentIdx) : (selectedItemIndex === i);
        // The whole block moves together, so every item — not just the grabbed one —
        // shifts by the same drag offset.
        const visualPosY = (item.posY || 0) + (dragging ? dragState.offsetDy : 0);
        return (
          <InternalItemGroup
            key={i}
            position={[0, visualPosY, 0]}
            w={w}
            itemH={itemH}
            depth={safeDepth}
            isSelected={itemIsSelected}
            onItemClick={!dragging && onItemClick ? () => onItemClick(i) : null}
            suppressHover={sectionHasSelection && !itemIsSelected}
            onStartDrag={itemIsSelected && onReorder ? (gwy, pz) => startDrag(i, gwy, pz) : null}
            renderContent={(colorOverride) => renderItemContent(i, item, itemH, colorOverride)}
            forcedHighlightColor={forcedItemColor}
          />
        );
      })}
      {VIDUS_ITEM_3D_INTERACTION_DISABLED && (
        <Box
          args={[w, sectionH || 0, safeDepth]}
          position={[w / 2, (sectionH || 0) / 2, safeDepth / 2 + 0.0015]}
          onPointerOver={(e) => { e.stopPropagation(); setSectionHovered(true); }}
          onPointerOut={() => setSectionHovered(false)}
          onClick={(e) => { e.stopPropagation(); if (onItemClick) onItemClick(0); }}
        >
          <meshStandardMaterial transparent opacity={0.001} depthWrite={false} />
        </Box>
      )}
      {showDimensions && (() => {
        const ITEM_GAP_CM = 2.5;
        const HANGER_TYPES = new Set([1, 2, 8, 9]);
        const dimBadge = (key, cy, label) => (
          <Html key={key} center position={[w / 2, cy, safeDepth / 2 + 0.25]} zIndexRange={[100, 0]}>
            <div style={{ background: '#000', color: '#fff', padding: '1px 5px', borderRadius: 3, fontSize: 11, fontFamily: 'sans-serif', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
              {label}
            </div>
          </Html>
        );
        // Sort by visual posY descending (topmost item first)
        const withVisual = displayItems.map((item, i) => ({
          item, i,
          itemH: (item.height || 30) / 100,
          visualPosY: (item.posY || 0) + (dragging ? dragState.offsetDy : 0),
        })).sort((a, b) => b.visualPosY - a.visualPosY);

        const badges = [];
        withVisual.forEach(({ item, i, itemH, visualPosY }, si) => {
          // Real gap above this item (from its top to the section ceiling or bottom of item above)
          const prevEdge = si === 0 ? (sectionH || 0) : withVisual[si - 1].visualPosY;
          const gapH = prevEdge - (visualPosY + itemH);
          const gapCm = Math.round(gapH * 1000) / 10;
          // Show gap only when it's larger than the standard gap (drawer moved away from default position,
          // or a shelf type's own "aukštis virš lentynos" gap, which is always well above this threshold)
          if (gapCm > ITEM_GAP_CM + 0.5) {
            const gapCy = visualPosY + itemH + gapH / 2;
            badges.push(dimBadge(`gap-${i}`, gapCy, gapCm));
          }
          // Badge on the item itself:
          // - hangers/pantographs: item.height IS the single total height the user entered
          //   directly in the dialog (no separate hardware/gap split anymore) — show as-is.
          // - rail shelf (11): its own fixed plate thickness, called out on the shelf itself.
          // - plain shelf (10): no on-item badge — its "height above" is already shown by the gap badge.
          // - everything else (drawer, ...): gap + item height (e.g. 2.5 + 20 = 22.5).
          if (HANGER_TYPES.has(item.type)) {
            badges.push(dimBadge(`item-${i}`, visualPosY + itemH / 2, Math.round((item.height || 0) * 10) / 10));
          } else if (item.type === 11) {
            badges.push(dimBadge(`item-${i}`, visualPosY + itemH / 2, Math.round((item.height || 0) * 10) / 10));
          } else if (item.type === 10) {
            // no on-item badge
          } else {
            const slotCm = Math.round(((item.height || 30) + ITEM_GAP_CM) * 10) / 10;
            badges.push(dimBadge(`item-${i}`, visualPosY + itemH / 2, slotCm));
          }
        });
        // Remaining free space below the lowest item — real distance from item bottom to section bottom
        const lowestVisualPosY = withVisual.length > 0
          ? withVisual[withVisual.length - 1].visualPosY
          : 0;
        const remainCm = Math.round(lowestVisualPosY * 1000) / 10;
        if (remainCm >= 0.5) badges.push(dimBadge('remain', lowestVisualPosY / 2, remainCm));
        return badges;
      })()}
    </>
  );
};

// ── RecursiveSection ─────────────────────────────────────────────────────────
const RecursiveSection = ({
  node, width, height, depth, x, y,
  wallThickness, currentColor, onShelfClick, isSelected,
  activeStep, splitSectionMode, onHorizontalSplit, onVerticalSplit,
  renderInternalComponent, path, sectionOptions, sectionKeyPrefix,
  parentSplitType, parentChildIndex, parentAvailableSize, parentSizes, parentMins, parentMinsAsLeft, parentMinsAsRight,
  onSectionResize, onSectionDelete, onLeafSectionDelete, onNarySectionResize,
  onRootDimensionChange,
  selectedDividerKey, onDividerSelect,
  siblingPath, onOpenOuterDialog, onOpenInnerDialog, onRemoveDoor, onMergeSection, onUnmergeSection,
  mergedSectionsForCab, mergeMode, onToggleMergeMode, selectedSectionPath,
  doorColors,
  doorGroupPlusPaths, doorGroupMinusPaths,
  isInner = false,
  interiorStructure,
  isInnerSelected, onInnerShelfClick,
  onInteriorHSplit, onInteriorVSplit,
  onInteriorSectionResize, onInteriorSectionDelete, onInteriorLeafSectionDelete, onInteriorNarySectionResize,
  onPromoteAtPanel,
  onDemoteAtPanel,
  panelFrontOffsets,
  onTogglePanelFront,
  selectedGhostInner,
  onGhostInnerSelect,
  createInnerRenderFn,
  ghostOuterPath = null,
  measureMode,
  onClearSection = null,
  onClearSectionAtPath = null,
  showEdges = false,
  rootNode, rootWidth, rootHeight,
  // basisType 7/8 ("Plokštė Tarp šonų"/"Per Visą Plotį") — grupės lygio plokštė
  // (UnifiedBase.jsx) ir kabineto IŠORINIAI šonai (žr. leftFloorReach/rightFloorReach
  // SingleCabinet3D.jsx) dabar siekia TIKRAS grindis (Y=0), NE tik visualBaseHeight
  // (kur šis RecursiveSection medis prasideda pagal savo lokalias koordinates). Bet
  // VIDINĖ vertikali pertvara (vartotojo atliktas "vertikalus skaidymas" tiesiog šio
  // kabineto struktūroje, ne naujas kabinetas) TOLIAU prasidėdavo tik nuo
  // visualBaseHeight — "kabojo ore" virš plokštės, su akivaizdžiu tarpu iki jos (žr.
  // ClickUp — vartotojas: "Atlikus vertikalų skaidymą... ji kabo ore, ir dabar
  // akivaizdžiai plokštes kontūrai 'lipa' vienas ant kito"). rootFloorExtend (metrais,
  // paduodamas TIK ŠAKNINIAM RecursiveSection kvietimui iš SingleCabinet3D.jsx) stumia
  // TIK ŠAKNINIO ("path==='0'") VERTIKALAUS skaidymo pertvaros APAČIĄ žemyn iki tos
  // pačios Y=0 ribos — žr. panaudojimą žemiau ir DividerDragHandle/extraBottomExtend.
  rootFloorExtend = 0,
  // touchesBottom: ar ŠIO mazgo (ne tik šakninio path==='0') apatinis kraštas iš tikrųjų
  // sutampa su VISOS kabineto struktūros apačia (Y=0), o ne tik su savo tėvinio boxo apačia.
  // Anksčiau extraBottomExtend buvo taikomas TIK path==='0' pertvarai — jei vartotojas
  // pirmiausia padaro HORIZONTALŲ skaidymą, o tada VERTIKALŲ skaidymą apatinėje dalyje,
  // ta vidinė (jau ne path==='0') vertikali pertvara negaudavo extraBottomExtend ir
  // "kabodavo ore" virš plokštės (basisType 7/8) — žr. ClickUp "vidinės pertvaros turi
  // atsiremti į pagrindo plokštę, dabar kabo ore". Skaičiuojama rekursyviai: vertikalus
  // (side-by-side) skaidymas abiem vaikams palieka tą pačią reikšmę (nei vienas
  // nepasislenka aukštyn), o horizontalus (stack) skaidymas apatiniam vaikui (tam, kuris
  // gauna childY/hOffsets===0) palieka reikšmę, viršutiniam — visada false.
  touchesBottom = true,
  // Kai ši (vidinė) medžio šaka yra vidinė pertvara TOJE PAČIOJE išorinėje sekcijoje,
  // kuri jau turi dureles — durelės uždengia visą sekciją, taigi ir jos vidines
  // pertvaras/lentynas, ne tik pačią sekcijos sieną. `isInner` medis savo paties
  // doorColors visada gauna tuščią {} (durelės priskiriamos tik IŠORINĖMS sekcijoms),
  // tad subtreeHasDoor jame niekada negrąžina true — be šio flago vidinės pertvaros
  // niekada nepasitrauktų per wallThickness, net kai jas dengia durelės (žr. bug'ą
  // "Vidinių pertvarų lentynų gylis nesumažėja"). Įrašoma vieną kartą ten, kur
  // patenkame į vidinę medžio šaką (žr. žemiau), tada tiesiog perduodama nepakitusi.
  forceBordersDoor = false,
}) => {

  if (!node) return null;
  // Captured once at the top of whichever tree this is (outer cabinet structure, or an
  // inner/interior structure's own root) and forwarded unchanged to every descendant —
  // lets any leaf, however deeply nested, walk back up the FULL tree it belongs to
  // (see findLeafResizeAncestor) without needing the whole structure re-passed explicitly.
  const effRootNode = rootNode ?? node;
  const effRootWidth = rootWidth ?? width;
  const effRootHeight = rootHeight ?? height;

  // Bilietas "4. Vidus. Kai uždedi duris... Bet Vidinių pertvarų lentynų gylis
  // nesumažėja" — anksčiau tik TIESIOGIAI su durimis besiliečiančios plokštės/
  // pertvaros sugildavo (subtreeHasDoor/wallBordersDoor, tikrina tik konkretaus
  // lapo/sienos kaimynystę). Reikalavimas platesnis: uždėjus BENT VIENAS dureles
  // BET KURIOJE šios spintos sekcijoje, VISOS karkaso plokštės ir vidinės
  // pertvaros/lentynos turi sugilėti, ne tik durims besiliečiančios (vartotojas
  // vis tiek gali individualiai ištraukti bet kurią į priekį per esamą "Ištraukti
  // į priekį" perjungiklį — tai nepakinta). doorColors yra BENDRAS visos spintos
  // (ne vienos sekcijos) žemėlapis, raktai su `${sectionKeyPrefix}-` priešdėliu
  // priklauso ŠIAI spintai — pakanka patikrinti, ar tarp jų yra bent viena reikšmė.
  const cabinetDoorKeyPrefix = (!isInner && sectionKeyPrefix) ? `${sectionKeyPrefix}-` : null;
  const cabinetHasAnyDoor = !!(cabinetDoorKeyPrefix && doorColors &&
    Object.keys(doorColors).some((k) => k.startsWith(cabinetDoorKeyPrefix) && doorColors[k]));

  if (node.split) {
    const isHorizontal = node.split === 'h';
    const N = node.children ? node.children.length : 2;
    const sharedProps = { depth, wallThickness, currentColor, onShelfClick, isSelected, activeStep, splitSectionMode, onHorizontalSplit, onVerticalSplit, renderInternalComponent, sectionOptions, sectionKeyPrefix, onSectionResize, onSectionDelete, onLeafSectionDelete, onNarySectionResize, onRootDimensionChange, selectedDividerKey, onDividerSelect, onOpenOuterDialog, onOpenInnerDialog, onRemoveDoor, onMergeSection, onUnmergeSection, mergedSectionsForCab, mergeMode, onToggleMergeMode, selectedSectionPath, doorColors, doorGroupPlusPaths, doorGroupMinusPaths, isInner, interiorStructure, isInnerSelected, onInnerShelfClick, onInteriorHSplit, onInteriorVSplit, onInteriorSectionResize, onInteriorSectionDelete, onInteriorLeafSectionDelete, onInteriorNarySectionResize, onPromoteAtPanel, onDemoteAtPanel, panelFrontOffsets, onTogglePanelFront, selectedGhostInner, onGhostInnerSelect, createInnerRenderFn, ghostOuterPath, measureMode, onClearSection, onClearSectionAtPath, showEdges, rootNode: effRootNode, rootWidth: effRootWidth, rootHeight: effRootHeight, forceBordersDoor };

    if (N === 2) {
      const ratio = node.ratio ?? 0.5;
      const available = isHorizontal ? height - wallThickness : width - wallThickness;
      const child0Size = available * ratio;
      const child1Size = available * (1 - ratio);
      const minChild0 = getNearAdjustableMin(node.children[0], child0Size, false, interiorStructure, `${path}-0`, wallThickness);
      const minChild1 = getNearAdjustableMin(node.children[1], child1Size, true, interiorStructure, `${path}-1`, wallThickness);
      return (
        <group position={[x, y, 0]}>
          {node.children.map((child, i) => {
            const childW = isHorizontal ? width : (i === 0 ? child0Size : child1Size);
            const childH = isHorizontal ? (i === 0 ? child0Size : child1Size) : height;
            const childX = !isHorizontal && i === 1 ? child0Size + wallThickness : 0;
            const childY = isHorizontal && i === 0 ? child1Size + wallThickness : 0;
            const childTouchesBottom = isHorizontal ? (i === 1 && touchesBottom) : touchesBottom;
            return (
              <RecursiveSection key={i} path={`${path}-${i}`} node={child} width={childW} height={childH} x={childX} y={childY} parentSplitType={node.split} parentChildIndex={i} parentAvailableSize={available} parentSizes={[ratio, 1 - ratio]} parentMins={[minChild0, minChild1]} siblingPath={`${path}-${1 - i}`} touchesBottom={childTouchesBottom} {...sharedProps} />
            );
          })}
          {/* DividerDragHandle renders the visible wall + hover + buttons */}
          {(() => {
            const dk = `divider:${sectionKeyPrefix}:${path}`;
            // ClickUp "3.0 Išorė. Nerodo durelių action mygtukų, kai įdėtos vidinės
            // pertvaros/lentynos" — ghost (per-dureles persišviečiančios vidinės
            // pertvaros) "paversti į išorinę" paspaudimo taikinys TYČIA platus (visas
            // `width`) IR giliai siekiantis (depth+0.08, žr. DividerDragHandle/
            // NarySectionDivider hitDepth komentarą), kad laimėtų raycast prieš TUŠČIOS
            // sekcijos catch-all'ą (depth+0.07) — bet TAS PATS platus/gilus hitbox'as
            // TAIP PAT laimėdavo prieš PAČIŲ DURELIŲ paspaudimo sluoksnį (OuterDoors.jsx,
            // ~depth+0.02), kai sekcija TURI vidinę pertvarą IR dureles vienu metu —
            // paspaudimas ant matomo durelių paviršiaus tada "nuvogamas" šio neaiškaus
            // ghost hitbox'o (dažniausiai net nepatenkantis į nearCenter ribą, tad
            // tiesiog NIEKO neatlieka), o ne pasiekia dureles. Kai sekcija jau turi
            // dureles (forceBordersDoor — šis ghost gauna jį lygų hasDoorAssigned iš
            // tėvinio iškvietimo, žr. aukščiau), "paversti į išorinę" bet kuriuo atveju
            // nelabai prasminga (durelės jau dengia tą vietą) — tad ghost tampa GRYNAI
            // pasyvus (onGhostSelect=null), ir platus/gilus hitbox'as apskritai
            // nebepiešiamas, leidžiant paspaudimui pasiekti dureles.
            const gKey = (isInner && activeStep === 'Išorė' && ghostOuterPath != null && !forceBordersDoor) ? `${ghostOuterPath}:${path}:0` : null;
            const iParts = gKey ? (path === '0' ? [] : path.split('-').slice(1).map(Number)) : null;
            const onGhostSel = (gKey && onGhostInnerSelect) ? () => onGhostInnerSelect(selectedGhostInner?.key === gKey ? null : { outerPath: ghostOuterPath, key: gKey, nodePathParts: iParts, dividerIdx: 0 }) : null;
            const isGhostSel = !!(gKey && selectedGhostInner?.key === gKey);
            const onGhostConv = (isGhostSel && onPromoteAtPanel && ghostOuterPath != null) ? () => { onPromoteAtPanel(ghostOuterPath, [...iParts.map(String), '0']); onGhostInnerSelect(null); } : null;
            const onDem = (!isInner && activeStep === 'Išorė' && onDemoteAtPanel) ? () => onDemoteAtPanel(path) : null;
            const panelKey = (!isInner && sectionKeyPrefix) ? `${sectionKeyPrefix}-${path}` : null;
            const isFacade = panelKey ? panelFrontOffsets?.[panelKey] === 'facade' : false;
            const onToggleFront = (!isInner && activeStep === 'Išorė' && onTogglePanelFront) ? () => onTogglePanelFront(path) : null;
            const bordersDoor = forceBordersDoor || cabinetHasAnyDoor || ((!isInner && sectionKeyPrefix)
              ? (subtreeHasDoor(node.children[0], `${path}-0`, doorColors, `${sectionKeyPrefix}-`) || subtreeHasDoor(node.children[1], `${path}-1`, doorColors, `${sectionKeyPrefix}-`))
              : false);
            // IŠORINIO medžio (!isInner) VERTIKALI (!isHorizontal) pertvara, kurios apatinis
            // kraštas REALIAI liečia visos kabineto struktūros apačią (touchesBottom) — žr.
            // touchesBottom/rootFloorExtend komentarus aukščiau.
            const extraBottomExtend = (!isInner && touchesBottom && !isHorizontal) ? rootFloorExtend : 0;
            return (
              <DividerDragHandle isHorizontal={isHorizontal} width={width} height={height} depth={depth} child0Size={child0Size} wallThickness={wallThickness} available={available} path={path} onSectionResize={onSectionResize} onSectionDelete={onSectionDelete} activeStep={activeStep} currentColor={currentColor} isSelected={selectedDividerKey === dk} onSelectSelf={onDividerSelect} dividerKey={dk} isInner={isInner} onGhostSelect={onGhostSel} isGhostSelected={isGhostSel} onGhostConvert={onGhostConv} minChild0={minChild0} minChild1={minChild1} onDemote={onDem} showEdges={showEdges} isFacade={isFacade} onToggleFront={onToggleFront} bordersDoor={bordersDoor} extraBottomExtend={extraBottomExtend} />
            );
          })()}
        </group>
      );
    }

    // N-ary flat split (N > 2): proportional sizes, draggable dividers
    const available = isHorizontal ? height - wallThickness * (N - 1) : width - wallThickness * (N - 1);
    const sizes = node.sizes || Array(N).fill(1 / N);
    const childAbsSizes = sizes.map(s => s * available);
    // Each child may need two different floors depending on which side of which divider is
    // being dragged: as the LEFT side of its right-hand divider (absorbFirst=false, near=its
    // last descendant) or as the RIGHT side of its left-hand divider (absorbFirst=true,
    // near=its first descendant).
    const childMinsAsLeft = node.children.map((c, idx) => getNearAdjustableMin(c, childAbsSizes[idx], false, interiorStructure, `${path}-${idx}`, wallThickness));
    const childMinsAsRight = node.children.map((c, idx) => getNearAdjustableMin(c, childAbsSizes[idx], true, interiorStructure, `${path}-${idx}`, wallThickness));

    // Compute cumulative offsets for v-split (left→right) and h-split (bottom→top)
    const vOffsets = (() => { let off = 0; return childAbsSizes.map(s => { const o = off; off += s + wallThickness; return o; }); })();
    const hOffsets = (() => { const arr = new Array(N); let off = 0; for (let i = N - 1; i >= 0; i--) { arr[i] = off; off += childAbsSizes[i] + wallThickness; } return arr; })();

    return (
      <group position={[x, y, 0]}>
        {node.children.map((child, i) => {
          const childW = isHorizontal ? width : childAbsSizes[i];
          const childH = isHorizontal ? childAbsSizes[i] : height;
          const childX = isHorizontal ? 0 : vOffsets[i];
          const childY = isHorizontal ? hOffsets[i] : 0;
          const childTouchesBottom = isHorizontal ? (i === N - 1 && touchesBottom) : touchesBottom;
          return (
            <RecursiveSection key={i} path={`${path}-${i}`} node={child} width={childW} height={childH} x={childX} y={childY} parentSplitType={node.split} parentChildIndex={i} parentAvailableSize={available} parentSizes={sizes} parentMinsAsLeft={childMinsAsLeft} parentMinsAsRight={childMinsAsRight} siblingPath={null} touchesBottom={childTouchesBottom} {...sharedProps} />
          );
        })}
        {/* N-1 interactive dividers */}
        {node.children.slice(0, -1).map((_, i) => {
          const divPosX = isHorizontal ? width / 2 : vOffsets[i] + childAbsSizes[i] + wallThickness / 2;
          const divPosY = isHorizontal ? hOffsets[i] - wallThickness / 2 : height / 2;
          const combined = sizes[i] + sizes[i + 1];
          const startRatio = sizes[i] / combined;
          const ndk = `ndivider:${sectionKeyPrefix}:${path}:${i}`;
          // Ta pati "3.0 Išorė... durelių action mygtukų" korekcija, kaip `gKey`
          // aukščiau (2-dalių atveju) — veidrodinė N-arių (3+) pertvarų šakai.
          const ngKey = (isInner && activeStep === 'Išorė' && ghostOuterPath != null && !forceBordersDoor) ? `${ghostOuterPath}:${path}:${i}` : null;
          const niParts = ngKey ? (path === '0' ? [] : path.split('-').slice(1).map(Number)) : null;
          const nOnGhostSel = (ngKey && onGhostInnerSelect) ? () => onGhostInnerSelect(selectedGhostInner?.key === ngKey ? null : { outerPath: ghostOuterPath, key: ngKey, nodePathParts: niParts, dividerIdx: i }) : null;
          const nIsGhostSel = !!(ngKey && selectedGhostInner?.key === ngKey);
          const nOnGhostConv = (nIsGhostSel && onPromoteAtPanel && ghostOuterPath != null) ? () => { onPromoteAtPanel(ghostOuterPath, [...niParts.map(String), String(i)]); onGhostInnerSelect(null); } : null;
          const nPanelKey = (!isInner && sectionKeyPrefix) ? `${sectionKeyPrefix}-${path}-nd${i}` : null;
          const nIsFacade = nPanelKey ? panelFrontOffsets?.[nPanelKey] === 'facade' : false;
          const nOnToggleFront = (!isInner && activeStep === 'Išorė' && onTogglePanelFront) ? () => onTogglePanelFront(`${path}-nd${i}`) : null;
          const nBordersDoor = forceBordersDoor || cabinetHasAnyDoor || ((!isInner && sectionKeyPrefix)
            ? (subtreeHasDoor(node.children[i], `${path}-${i}`, doorColors, `${sectionKeyPrefix}-`) || subtreeHasDoor(node.children[i + 1], `${path}-${i + 1}`, doorColors, `${sectionKeyPrefix}-`))
            : false);
          // Ta pati touchesBottom/rootFloorExtend logika kaip 2-way skaidymo pertvarai
          // aukščiau — žr. RecursiveSection touchesBottom komentarą.
          const nExtraBottomExtend = (!isInner && touchesBottom && !isHorizontal) ? rootFloorExtend : 0;
          return (
            <NarySectionDivider
              key={`nd-${i}`}
              isHorizontal={isHorizontal}
              posX={divPosX} posY={divPosY}
              wallThickness={wallThickness} width={width} height={height} depth={depth}
              currentColor={currentColor} activeStep={activeStep}
              startRatio={startRatio} available={available}
              isSelected={selectedDividerKey === ndk}
              onSelectSelf={onDividerSelect}
              dividerKey={ndk}
              isInner={isInner}
              onGhostSelect={nOnGhostSel}
              isGhostSelected={nIsGhostSel}
              onGhostConvert={nOnGhostConv}
              showEdges={showEdges}
              isFacade={nIsFacade}
              onToggleFront={nOnToggleFront}
              bordersDoor={nBordersDoor}
              extraBottomExtend={nExtraBottomExtend}
              onDrag={(newRatio) => {
                const absSizes = sizes.map(s => s * available);
                const oldPivotAbs = absSizes[i];
                const newPivotAbs = newRatio * combined * available;
                const delta = newPivotAbs - oldPivotAbs;
                const oppositeIdx = i + 1;
                // Each side's true minimum may be larger than 8cm if it's itself a nested
                // subtree — and must account for the OTHER (non-bordering) descendants staying
                // at their current size, not just shrinking everything to a flat 8cm.
                const minI = childMinsAsLeft[i];
                const minOpposite = childMinsAsRight[oppositeIdx];

                // Vilkant tik VIENĄ pertvarą keičiamos tik dvi gretimos sekcijos (i ir i+1) —
                // visos kitos (tolimesni vaikai/pertvaros) lieka fiksuotos, jokio kaskadinio stūmimo.
                if (delta > 0) {
                  const room = Math.max(0, absSizes[oppositeIdx] - minOpposite);
                  const take = Math.min(delta, room);
                  absSizes[oppositeIdx] -= take;
                  absSizes[i] = oldPivotAbs + take;
                } else if (delta < 0) {
                  const room = Math.max(0, absSizes[i] - minI);
                  const take = Math.min(-delta, room);
                  absSizes[i] = oldPivotAbs - take;
                  absSizes[oppositeIdx] += take;
                }

                const newSizes = absSizes.map(s => s / available);
                if (onNarySectionResize) onNarySectionResize(path, newSizes);
              }}
              onDelete={() => {
                if (onLeafSectionDelete) onLeafSectionDelete(`${path}-${i + 1}`);
              }}
            />
          );
        })}
      </group>
    );
  }

  // ── Leaf node ──────────────────────────────────────────────────────────────
  const isThisSelected = isSelected(path);
  const isSiblingOfSelected = siblingPath && isSelected(siblingPath);
  const parentPath = path.includes('-') ? path.substring(0, path.lastIndexOf('-')) : null;
  const resizeDim = parentSplitType === 'h' ? 'H' : parentSplitType === 'v' ? 'W' : null;
  const resizeVal = parentSplitType === 'h' ? height : width;
  const myGroupId = mergedSectionsForCab
    ? mergedSectionsForCab.find(s => s.sectionIndex === path || s.targetSectionIndex === path)?.groupId
    : null;

  const showPlusButton  = doorGroupPlusPaths?.has(path) ?? false;
  const showMinusButton = doorGroupMinusPaths?.has(path) ?? false;

  // Sekcija be durelių — jos vidus realybėje ir taip visada matomas (nėra kas
  // uždengia), tad Baldas/Išorė žingsniuose jį rodyti tuščią (be lentynų/
  // pertvarų) buvo klaidinanti — atrodydavo, lyg komponentai tiesiog dingę.
  // `myGroupId` jau apima ir sujungtų (kelių sekcijų) durelių atvejį — jei ši
  // sekcija priklauso grupei, laikoma, kad ji "su durelėmis" (saugu, neleidžia
  // per klaidą pro dureles matyti vidaus).
  const hasDoorAssigned = !!myGroupId || !!(sectionKeyPrefix && doorColors?.[`${sectionKeyPrefix}-${path}`]);

  // Outer leaf: show inner structure inside this section kai esame Vidus žingsnyje
  // ARBA Baldas žingsnyje, kai sekcija apskritai neturi durelių (žr. 86cb9y0vd —
  // "Baldas be durelių turi rodyti Vidų"). NEBEtaikoma Išorei: šis anksti
  // grąžinamas blokas visiškai apeina žemiau esantį showToolbar/onOpenOuterDialog
  // (durelių kūrimo/keitimo mygtuką) — jei taikytume ir Išorei, nedurintos
  // sekcijos apskritai nerodytų būdo pridėti dureles, o vietoj to visada rodytų
  // vidinius komponentus (žr. bug'ą: "Išorėje negaliu pridėti durelių").
  //
  // SVARBU: žemiau <RecursiveSection> gauna TIKRĄ `activeStep` (ne hardcodintą "Vidus"),
  // nors šis blokas piešiamas ir Baldas žingsnyje (sekcijoms be durelių) — priešingu
  // atveju vidus tapdavo PILNAI interaktyvus (atidarydavo "Pasirinkite vidinius
  // komponentus" dialogą) net būnant Baldas žingsnyje, kai jis turi būti tik pasyviai
  // matomas ten; interaktyvus lieka TIK realiai esant Vidaus žingsnyje.
  if (!isInner && (activeStep === 'Vidus' || (activeStep === 'Baldas' && !hasDoorAssigned))) {
    const innerNode = (interiorStructure || {})[path] || {};
    const innerSectionKeyPrefix = sectionKeyPrefix ? `${sectionKeyPrefix}-INNER__${path}` : null;
    return (
      <group position={[x, y, 0]}>
        <Box args={[width, height, 0.001]} position={[width / 2, height / 2, 0.002]}>
          <meshStandardMaterial color="#3b82f6" transparent opacity={0.07} depthWrite={false} />
        </Box>
        <RecursiveSection
          isInner={true}
          node={innerNode}
          width={width} height={height} depth={depth}
          x={0} y={0} path="0"
          sectionKeyPrefix={innerSectionKeyPrefix}
          forceBordersDoor={hasDoorAssigned || cabinetHasAnyDoor}
          isSelected={(innerPath) => isInnerSelected ? isInnerSelected(path, innerPath) : false}
          onShelfClick={(innerPath) => onInnerShelfClick && onInnerShelfClick(path, innerPath)}
          activeStep={activeStep}
          wallThickness={wallThickness}
          currentColor={currentColor}
          sectionOptions={sectionOptions}
          onHorizontalSplit={(innerPath) => onInteriorHSplit && onInteriorHSplit(path, innerPath)}
          onVerticalSplit={(innerPath) => onInteriorVSplit && onInteriorVSplit(path, innerPath)}
          onSectionResize={(innerPath, ratio) => onInteriorSectionResize && onInteriorSectionResize(path, innerPath, ratio)}
          onSectionDelete={(innerSplitPath) => onInteriorSectionDelete && onInteriorSectionDelete(path, innerSplitPath)}
          onLeafSectionDelete={(innerPath) => onInteriorLeafSectionDelete && onInteriorLeafSectionDelete(path, innerPath)}
          onNarySectionResize={(innerPath, newSizes) => onInteriorNarySectionResize && onInteriorNarySectionResize(path, innerPath, newSizes)}
          onClearSection={onClearSectionAtPath ? (innerPath) => onClearSectionAtPath(path, innerPath) : null}
          onClearSectionAtPath={null}
          renderInternalComponent={createInnerRenderFn ? createInnerRenderFn(path) : () => null}
          onOpenInnerDialog={onOpenInnerDialog}
          measureMode={measureMode}
          selectedDividerKey={selectedDividerKey}
          onDividerSelect={onDividerSelect}
          splitSectionMode={splitSectionMode}
          parentSplitType={null} parentChildIndex={null} parentAvailableSize={null}
          siblingPath={null}
          onOpenOuterDialog={null}
          onRemoveDoor={null} onMergeSection={null} onUnmergeSection={null}
          mergedSectionsForCab={[]}
          mergeMode={false} onToggleMergeMode={null} selectedSectionPath={null}
          doorColors={{}}
          doorGroupPlusPaths={null} doorGroupMinusPaths={null}
          interiorStructure={null}
          isInnerSelected={null} onInnerShelfClick={null}
          onInteriorHSplit={null} onInteriorVSplit={null}
          onInteriorSectionResize={null} onInteriorLeafSectionDelete={null} onInteriorNarySectionResize={null}
          createInnerRenderFn={null}
          showEdges={showEdges}
        />
      </group>
    );
  }

  // Outer click: Išorė only; Inner click: Vidus only
  // `&& !hasDoorAssigned` PRIDĖTA (outer atvejui) — šis bendras, VISO ploto click-box'as
  // (žr. žemiau, hitDepth=depth+0.07) anksčiau rodydavosi Išorėje NEPRIKLAUSOMAI nuo to,
  // ar sekcijai jau priskirtos durelės. Jo priekinis paviršius (depth+0.07) stovi ARČIAU
  // kameros už paties OuterDoors.jsx durelių paspaudimo sluoksnio (doorZPosition+0.02,
  // ~depth+0.01-0.02) — tad kai durelės JAU yra, šis bendras box'as VISADA laimėdavo
  // raycast lenktynes, ir OuterDoors savas (dabar briaunos-only) paspaudimo kodas
  // niekada realiai nesuveikdavo. Kai sekcija TUŠČIA (nėra durelių), OuterDoors
  // apskritai nerenderinamas, tad šis box'as lieka vienintelis ir TEISINGAS būdas ją
  // paspausti (durelėms pridėti) — čia elgesys nesikeičia.
  const showClickBox = isInner
    ? (activeStep === 'Vidus' || activeStep === 'Baldas')
    : (activeStep === 'Išorė' && !hasDoorAssigned);
  // ClickUp "Baldo kūrimas (Išorė/Vidus). Pertvarų/Lentynų vs. skyriaus žymėjimas" —
  // BANDYTA (ir ATŠAUKTA) — "jei [pertvara/lentyna] pažaliavę, tai net nerodome
  // vidinį skyrių action mygtukų" iš pradžių įgyvendinta kaip BESĄLYGIŠKAS patikrinimas
  // (!selectedDividerKey) — bet tai PASIRODĖ GLOBALUS, ne tik gretimo daliklio
  // atveju: selectedDividerKey yra VIENA, VISAM baldui bendra (ne po-sekciją) būsena,
  // kuri NEIŠSIVALO automatiškai, kai vartotojas paspaudžia KITĄ sekciją/dureles
  // (tik aiškus paspaudimas tuščioje vietoje tai padaro) — tad kartą pažymėjus BET
  // KOKĮ daliklį BET KUR baldo medyje, VISŲ kitų sekcijų (įskaitant kitų, visai
  // nesusijusių korpusų dureles) action mygtukai likdavo nerodomi likusiai seanso
  // daliai. ClickUp "3.0 Išorė. Durelių action mygtukai. Sunkiai iššaukiu durelių
  // Action mygtukus" (vartotojo screenshot, atskiras 2.2 sekcijos korpusas) rodė
  // tiksliai šį simptomą. Grąžinta sena elgsena — jei norėsime šios savybės
  // ateityje, reikės TIKSLIAI apriboto patikrinimo (tik TIESIOGINIAM, šią sekciją
  // ribojančiam dalikliui), ne bendros vėliavėlės.
  const showToolbar = isThisSelected && (isInner
    ? activeStep === 'Vidus'
    : activeStep === 'Išorė');

  return (
    <group position={[x, y, 0]}>
      {showClickBox && (() => {
        // ClickUp "Baldo kūrimas (Išorė/Vidus). Kai uždedi baldą šalia su padu - neleidžia
        // pasirinkti padalinimų." — kai kaimyninis kabinetas (BET KURIS, net ir ne tas pats,
        // kurio sekciją bandai pažymėti) turi basisType Padas/Cokolis (1/2), UnifiedBase.jsx
        // FrontTrimBox'o priekinės juostos nematomas "hit" Box'as (žr. ten showHit/hitDepthArg
        // komentarą) tyčia ištemptas iki cabinetDepth+0.06 — TIKRO galo link TOLIAU nei ŠIS
        // skydas, kad laimėtų konkurenciją prieš pertvaras (DividerDragHandle). Bet kadangi tas
        // ištemptas hitbox'as driekiasi per BEVEIK VISĄ Pagrindo pločio zoną (ne tik ten, kur
        // realiai stovi pertvaros), status kameros kampu jis PRADĖDAVO "pavogti" paspaudimus,
        // skirtus KAIMYNINIO kabineto atviros (be durų) sekcijos paspaudimui — patikrinta gyvai
        // per TIKSLIŲ pasaulio koordinačių (camera.project) scenos matavimą su realiu įrašytu
        // baldu (FIT267691): Padas priekinės juostos hitbox'as z=[0.3,0.96] (t.y. iki tikro galo
        // + 0.06) persidengia su kaimyninio kabineto sekcijos click-box'u status spindulio kampu,
        // ir visada LAIMI, nes jo priekinis paviršius ARČIAU kameros. Sprendimas TA PATI kryptimi,
        // kaip CabinetPanel (SingleCabinet3D.jsx) jau naudoja prieš TĄ PATĮ priekinės juostos
        // hitbox'ą: šiam skydui irgi pridedamas truputį toliau į priekį siekiantis (+0.07, t.y.
        // 1cm daugiau nei UnifiedBase +0.06, kad laimėtų lygiais atvejais) nematomas hit box.
        const hitDepth = depth + 0.07;
        return (
          <Box
            args={[width, height, hitDepth]}
            position={[width / 2, height / 2, hitDepth / 2]}
            onClick={(e) => { e.stopPropagation(); onShelfClick(path); if (onGhostInnerSelect) onGhostInnerSelect(null); }}
          >
            <meshStandardMaterial colorWrite={false} depthWrite={false} />
          </Box>
        );
      })()}

      {/* Sekcijos vidiniai komponentai (lentyna/strypas/pertvara/stalčius ir pan.,
          priskirti TIESIOGIAI šiai išorinei sekcijai per sectionOptions, be papildomo
          Vidus skaidymo) anksčiau būdavo piešiami BE SĄLYGOS visuose žingsniuose —
          net kai sekcijai jau priskirtos durelės. Kadangi šie komponentai (lentynos,
          pertvaros ir pan.) yra plonos plokštės beveik tame pačiame gylyje kaip pačios
          durelės, jų kraštai "prasimušdavo" per durelių paviršių (Z-fighting) —
          matomi kaip vidinių lentynų/pertvarų kontūrai per uždarytas dureles. */}
      {(!isInner && (activeStep === 'Vidus' || !hasDoorAssigned) && renderInternalComponent) && (
        <group onClick={(e) => { e.stopPropagation(); onShelfClick(path); if (onGhostInnerSelect) onGhostInnerSelect(null); }}>
          {renderInternalComponent(path, width, height, forceBordersDoor || hasDoorAssigned)}
        </group>
      )}
      {(isInner && renderInternalComponent) && (
        <group onClick={(e) => { e.stopPropagation(); onShelfClick(path); }}>
          {renderInternalComponent(path, width, height, forceBordersDoor || hasDoorAssigned)}
        </group>
      )}

      {!isInner && showMinusButton && (
        <Html position={[width / 2, height / 2, depth / 2 + 0.06]} center zIndexRange={[150, 0]}>
          <div style={{ pointerEvents: 'auto' }}>
            <OverlayBtn onClick={(e) => { e.stopPropagation(); if (onUnmergeSection) onUnmergeSection(path, myGroupId); }} title="Atskirti nuo durelių">−</OverlayBtn>
          </div>
        </Html>
      )}

      {showToolbar && (() => {
        const doorColorKey = sectionKeyPrefix ? `${sectionKeyPrefix}-${path}` : null;
        const sectionHasDoor = !!(doorColorKey && doorColors?.[doorColorKey]) ||
          mergedSectionsForCab?.some(s => s.sectionIndex === path || s.targetSectionIndex === path);
        return (
          <Html position={[width / 2, height - 0.04, depth / 2 + 0.05]} center zIndexRange={[200, 0]}>
            <div style={overlayWrapStyle}>
              {/* Paprasti horizontalus/vertikalus brūkšniai vietoj "horizontal_split"/
                  "vertical_split" dviejų-langelių piktogramų — pastarosios per neaiškiai
                  atrodė kaip pats skaidymo veiksmas (žr. /admin/bugs "Pakeisti siuos icons
                  i vertikalu ir horizontalu bruksnius"). Tas pats "horizontal_rule" ženklas,
                  pasuktas 90°, garantuoja vizualiai identišką brūkšnio storį/ilgį abiem. */}
              <OverlayBtn onClick={(e) => { e.stopPropagation(); onHorizontalSplit(path); }} title="Skaidyti horizontaliai">
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>horizontal_rule</span>
              </OverlayBtn>
              <OverlayBtn onClick={(e) => { e.stopPropagation(); onVerticalSplit(path); }} title="Skaidyti vertikaliai">
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300, display: 'inline-block', transform: 'rotate(90deg)' }}>horizontal_rule</span>
              </OverlayBtn>
              {isInner && onClearSection && !!(sectionOptions && sectionKeyPrefix && sectionOptions[`${sectionKeyPrefix}__${path}`]) && (
                <OverlayBtn onClick={(e) => { e.stopPropagation(); onClearSection(path); }} title="Išvalyti sekciją">
                  <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>delete_sweep</span>
                </OverlayBtn>
              )}
              {!isInner && activeStep === 'Išorė' && sectionHasDoor && onRemoveDoor && (
                <OverlayBtn onClick={(e) => { e.stopPropagation(); onRemoveDoor(path); }} title="Pašalinti dureles">
                  <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>door_open</span>
                </OverlayBtn>
              )}
              {!isInner && activeStep === 'Išorė' && onOpenOuterDialog && (
                <OverlayBtn onClick={(e) => { e.stopPropagation(); onOpenOuterDialog(); }} title="Pasirinkti / keisti dureles">
                  <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>edit</span>
                </OverlayBtn>
              )}
              {isInner && onOpenInnerDialog && (
                <OverlayBtn onClick={(e) => { e.stopPropagation(); onOpenInnerDialog(); }} title="Pasirinkti vidinį komponentą">
                  <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>edit</span>
                </OverlayBtn>
              )}
            </div>
          </Html>
        );
      })()}

      {(() => {
        // Vidus: which dimension to display is driven by the "Skyrių aukščiai/pločiai"
        // toggle, not by this leaf's own resize axis — so the same input slot can show
        // either H or W for every leaf. Baldas/Išorė keep the old always-on H/W resize axis.
        // Outer sections also show read-only dimensions in Vidus when toggle is active (e.g. no shelves/dividers).
        // Also covers Išorė's own "Fasadų aukščiai/pločiai" toggle keys — measureMode is
        // fully replaced (not merged) on every step change, so only one of these pairs is
        // ever populated at a time regardless of which step is active.
        const vidusDim = measureMode?.sectionHeights ? 'H' : measureMode?.sectionWidths ? 'W'
          : measureMode?.facadeHeights ? 'H' : measureMode?.facadeWidths ? 'W' : null;
        // Root leaf = this section has no parent split (the whole cabinet interior is one section).
        // Show a read-only dimension so there's always something visible when the toggle is on,
        // even before the user has added any dividers.
        const isRootLeaf = !parentPath && path === '0';
        // Suppress section height badge when inner section has stacked items — those items
        // already display their own dimension badges (gaps + heights + remaining space).
        const innerItemsKey = isInner && sectionKeyPrefix ? `${sectionKeyPrefix}__${path}` : null;
        const sectionHasArrayItems = innerItemsKey && vidusDim === 'H'
          && Array.isArray(sectionOptions?.[innerItemsKey]) && sectionOptions[innerItemsKey].length > 0;
        // Išorė step shows ONLY facade (door) dimensions — see the facadeWidths/
        // facadeHeights ClickInputs further down — not internal panel/section
        // width-height badges. Those used to also render here for every root-leaf
        // or resizable outer leaf, duplicating (and slightly disagreeing with,
        // due to reveal/edge-banding offsets) the facade dimension shown right
        // next to it. Restricted to Vidus, where there's no facade badge to
        // duplicate and this is the only way to see/edit internal section sizes.
        const showInput = isInner
          ? (activeStep === 'Vidus' && vidusDim && !sectionHasArrayItems && (
              (parentAvailableSize && parentPath && (onSectionResize || onNarySectionResize))
              || isRootLeaf
            ))
          : (activeStep === 'Vidus' && vidusDim);
        if (!showInput) return null;
        const dim = isRootLeaf
          ? vidusDim
          : (activeStep === 'Vidus' && !isInner) ? vidusDim : (isInner ? vidusDim : resizeDim);
        const dimVal = dim === 'H' ? height : width;
        // Inner (Vidus) leaves: don't limit resizing to this leaf's own immediate parent —
        // sections built by splitting different branches separately (top→[A,B], bottom→[C,D])
        // put visually-adjacent leaves like B/C under DIFFERENT parents, so trading space only
        // with the immediate sibling silently ignores the boundary the user actually clicked on.
        // Walk up to whichever ancestor really owns that boundary instead (see findLeafResizeAncestor).
        const leafTarget = (isInner && !isRootLeaf && dim)
          ? findLeafResizeAncestor(effRootNode, path, dim === 'H' ? 'h' : 'v', effRootWidth, effRootHeight, wallThickness)
          : null;
        // A root leaf (whole cabinet is one undivided section) has no sibling to trade
        // space with, so it can't go through the normal section-resize path — but in
        // Baldas/Išorė it still has a perfectly good target: the cabinet's own overall
        // width/height (onRootDimensionChange), so it doesn't need to be read-only either.
        const editable = isInner
          ? (isRootLeaf ? false : !!leafTarget)
          : activeStep !== 'Vidus';
        const isVidusWidth = dim === 'W' && (isInner || activeStep === 'Vidus' || activeStep === 'Išorė');
        return (
        <group position={[width / 2, height / 2, depth / 2 + 0.25]}>
          {/* drei's Html mounts each instance as an independent DOM sibling of the canvas,
              stacked purely by CSS z-index — this badge sits at the exact same screen
              anchor ([width/2, height/2]) as the "unmerge door" minus-button Html
              (zIndexRange [150,0]) whenever that button is shown, which was silently
              swallowing every click meant for this badge. Ranked above it (but still
              below the selected-leaf toolbar's [200,0]) so the badge is always reachable. */}
          <Html center zIndexRange={[175, 0]}>
            <ClickInput
              value={dimVal}
              label={dim}
              editable={editable}
              borderColor="#3b82f6"
              bgColor={isVidusWidth ? '#ffffff' : '#000'}
              textColor={isVidusWidth ? '#374151' : '#fff'}
              onCommit={(newValMeters) => {
                if (isRootLeaf && !isInner) {
                  if (onRootDimensionChange) onRootDimensionChange(dim, newValMeters);
                  return;
                }
                if (isInner) {
                  // See leafTarget above — resize whichever ancestor actually owns the
                  // boundary between this leaf and its visually-next (else previous) neighbor,
                  // not necessarily this leaf's own immediate parent.
                  if (!leafTarget) return;
                  const { ancestorNode, ancestorPathParts, childIdx, neighborIdx, available } = leafTarget;
                  const ancestorPath = '0' + (ancestorPathParts.length ? '-' + ancestorPathParts.join('-') : '');
                  const N = ancestorNode.children.length;
                  const sizesArr = ancestorNode.sizes || (N === 2 ? [ancestorNode.ratio ?? 0.5, 1 - (ancestorNode.ratio ?? 0.5)] : Array(N).fill(1 / N));
                  const nearOldAbs = sizesArr[childIdx] * available;
                  const neighborOldAbs = sizesArr[neighborIdx] * available;
                  // Which side of each branch actually touches the boundary being moved —
                  // needed so getNearAdjustableMin descends toward the right leaf on both sides.
                  const neighborAbsorbFirst = neighborIdx > childIdx;
                  const ownAbsorbFirst = !neighborAbsorbFirst;
                  const nearBranchMin = getNearAdjustableMin(ancestorNode.children[childIdx], nearOldAbs, ownAbsorbFirst);
                  const neighborMin = getNearAdjustableMin(ancestorNode.children[neighborIdx], neighborOldAbs, neighborAbsorbFirst);
                  const desiredLeafAbs = Math.max(MIN_LEAF_SIZE, newValMeters);
                  const delta = desiredLeafAbs - dimVal;
                  let newNearAbs = nearOldAbs, newNeighborAbs = neighborOldAbs;
                  if (delta > 0) {
                    const room = Math.max(0, neighborOldAbs - neighborMin);
                    const take = Math.min(delta, room);
                    newNeighborAbs = neighborOldAbs - take;
                    newNearAbs = nearOldAbs + take;
                  } else if (delta < 0) {
                    const room = Math.max(0, nearOldAbs - nearBranchMin);
                    const take = Math.min(-delta, room);
                    newNearAbs = nearOldAbs - take;
                    newNeighborAbs = neighborOldAbs + take;
                  }
                  if (ancestorNode.sizes || N > 2) {
                    const newSizes = sizesArr.map((s, idx) => {
                      if (idx === childIdx) return newNearAbs / available;
                      if (idx === neighborIdx) return newNeighborAbs / available;
                      return s;
                    });
                    if (onNarySectionResize) onNarySectionResize(ancestorPath, newSizes);
                  } else {
                    const newRatioFor0 = (childIdx === 0 ? newNearAbs : newNeighborAbs) / available;
                    if (onSectionResize) onSectionResize(ancestorPath, newRatioFor0);
                  }
                  return;
                }
                const i = parentChildIndex;
                // The leaf itself is never a nested subtree, so its own floor is always the
                // flat 8cm; it's the NEIGHBOR (which may itself contain several leaves) that
                // needs the direction-aware "preserve current size, only nearest leaf yields" floor.
                const ownMin = MIN_LEAF_SIZE;
                if (parentSizes && parentSizes.length > 2 && onNarySectionResize) {
                  // N-ary parent: adjust this child + ONE adjacent sibling only, same
                  // no-cascade / true-minimum-floor rule as dragging — never touches other siblings.
                  const neighborIdx = i < parentSizes.length - 1 ? i + 1 : i - 1;
                  const neighborIsRight = neighborIdx > i;
                  const neighborMin = neighborIsRight
                    ? (parentMinsAsRight ? parentMinsAsRight[neighborIdx] : MIN_LEAF_SIZE)
                    : (parentMinsAsLeft ? parentMinsAsLeft[neighborIdx] : MIN_LEAF_SIZE);
                  const oldAbs = parentSizes[i] * parentAvailableSize;
                  const neighborOldAbs = parentSizes[neighborIdx] * parentAvailableSize;
                  const desiredAbs = Math.max(ownMin, newValMeters);
                  const delta = desiredAbs - oldAbs;
                  let newOwnAbs = oldAbs;
                  let newNeighborAbs = neighborOldAbs;
                  if (delta > 0) {
                    const room = Math.max(0, neighborOldAbs - neighborMin);
                    const take = Math.min(delta, room);
                    newNeighborAbs = neighborOldAbs - take;
                    newOwnAbs = oldAbs + take;
                  } else if (delta < 0) {
                    const room = Math.max(0, oldAbs - ownMin);
                    const take = Math.min(-delta, room);
                    newOwnAbs = oldAbs - take;
                    newNeighborAbs = neighborOldAbs + take;
                  }
                  const newSizes = parentSizes.map((s, idx) => {
                    if (idx === i) return newOwnAbs / parentAvailableSize;
                    if (idx === neighborIdx) return newNeighborAbs / parentAvailableSize;
                    return s;
                  });
                  onNarySectionResize(parentPath, newSizes);
                } else if (onSectionResize) {
                  const min0 = parentMins ? parentMins[0] : MIN_LEAF_SIZE;
                  const min1 = parentMins ? parentMins[1] : MIN_LEAF_SIZE;
                  const newRatio = i === 0
                    ? newValMeters / parentAvailableSize
                    : 1 - newValMeters / parentAvailableSize;
                  let minRatio = min0 / parentAvailableSize;
                  let maxRatio = 1 - (min1 / parentAvailableSize);
                  if (minRatio > maxRatio) { minRatio = maxRatio = 0.5; }
                  onSectionResize(parentPath, Math.max(minRatio, Math.min(maxRatio, newRatio)));
                }
              }}
            />
          </Html>
        </group>
        );
      })()}

      {/* Ghost inner structure in Išorė — root panel dividers allow one-level promote to outer */}
      {!isInner && activeStep === 'Išorė' && (() => {
        const innerNode = (interiorStructure || {})[path] || {};
        if (!innerNode || !innerNode.split) return null;
        const innerSectionKeyPrefix = sectionKeyPrefix ? `${sectionKeyPrefix}-INNER__${path}` : null;
        return (
          <group>
            <RecursiveSection
              isInner={true}
              node={innerNode}
              width={width} height={height} depth={depth}
              x={0} y={0} path="0"
              sectionKeyPrefix={innerSectionKeyPrefix}
              forceBordersDoor={hasDoorAssigned}
              isSelected={() => false}
              onShelfClick={() => {}}
              activeStep="Išorė"
              wallThickness={wallThickness}
              currentColor={currentColor}
              sectionOptions={sectionOptions}
              onHorizontalSplit={null} onVerticalSplit={null}
              onSectionResize={null} onSectionDelete={null}
              onLeafSectionDelete={null} onNarySectionResize={null}
              renderInternalComponent={() => null}
              onOpenInnerDialog={null}
              selectedDividerKey={null}
              onDividerSelect={null}
              splitSectionMode={false}
              parentSplitType={null} parentChildIndex={null} parentAvailableSize={null}
              siblingPath={null}
              onOpenOuterDialog={null}
              onRemoveDoor={null} onMergeSection={null} onUnmergeSection={null}
              mergedSectionsForCab={[]}
              mergeMode={false} onToggleMergeMode={null} selectedSectionPath={null}
              doorColors={{}}
              doorGroupPlusPaths={null} doorGroupMinusPaths={null}
              interiorStructure={null}
              isInnerSelected={null} onInnerShelfClick={null}
              onInteriorHSplit={null} onInteriorVSplit={null}
              onInteriorSectionResize={null} onInteriorLeafSectionDelete={null} onInteriorNarySectionResize={null}
              onPromoteAtPanel={onPromoteAtPanel}
              selectedGhostInner={selectedGhostInner}
              onGhostInnerSelect={onGhostInnerSelect}
              ghostOuterPath={path}
              createInnerRenderFn={null}
              showEdges={showEdges}
            />
          </group>
        );
      })()}
    </group>
  );
};


// ── InterCabinetDragHandle ────────────────────────────────────────────────────
// Drag strip on the left wall of a non-first cabinet. Resizes this and prev cabinet widths.
const InterCabinetDragHandle = ({ prevCabinet, cabinet, cabHeight, safeDepth, safeWallThickness, visualBaseHeight, onInterCabinetResize }) => {
  const dragRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const { camera, size } = useThree();
  const HANDLE_W = safeWallThickness * 2.5;
  const carcassH = cabHeight - visualBaseHeight;
  const carcassCY = visualBaseHeight + carcassH / 2;
  return (
    <Box
      position={[safeWallThickness / 2, carcassCY, safeDepth / 2]}
      args={[HANDLE_W, carcassH, safeDepth]}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'ew-resize'; }}
      onPointerOut={() => { setHovered(false); document.body.style.cursor = ''; }}
      onPointerDown={(e) => {
        if (e.button !== 0) return; // tik kairysis mygtukas — dešinysis nevilkina kabinetų ribos
        e.stopPropagation();
        const fovRad = camera.fov * Math.PI / 180;
        const ppm = (size.height / 2) / (camera.position.length() * Math.tan(fovRad / 2));
        dragRef.current = { startX: e.clientX, leftW: Number(prevCabinet.width), rightW: Number(cabinet.width), ppm };
        document.body.style.cursor = 'grabbing';
        const onMove = (me) => {
          if (!dragRef.current) return;
          const dx = (me.clientX - dragRef.current.startX) / dragRef.current.ppm;
          const newLeftW = Math.max(0.1, dragRef.current.leftW + dx);
          const newRightW = Math.max(0.1, dragRef.current.rightW - dx);
          if (onInterCabinetResize) onInterCabinetResize(prevCabinet.uniqueId, cabinet.uniqueId, newLeftW, newRightW);
        };
        const onUp = () => { dragRef.current = null; document.body.style.cursor = ''; window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      }}
    >
      <meshStandardMaterial color="#3b82f6" opacity={hovered ? 0.35 : 0} transparent />
    </Box>
  );
};

// ── SingleCabinet3D ───────────────────────────────────────────────────────────
const SingleCabinet3D = ({
  cabinet, index, basisHeight = 0.1, wallThickness = 0.018, backWall,
  cabinetDepth = 0.6, cabinets = [], selectedShelf, onShelfClick, mergedSections = [],
  onCreateDoors, onRemoveDoors, onDetachSection, doorColors, sectionOptions, doorHandles,
  sectionComponentOffsets, onSectionComponentOffsetChange,
  basisType: rawBasisType = 0, isFirstInGroup, isLastInGroup, isSelected, furnitureActive, hideTopPanel = false,
  topPanelType = 0, topPanelHeight = 0.1,
  leftSideElementWidth = 0, rightSideElementWidth = 0,
  onShelfHeightChange, onShelfDelete, activeStep, onSelect, onCabinetWidthChange, onCabinetHeightChange,
  splitSectionMode, onHorizontalSplit, onVerticalSplit, onSectionRatioChange, onSectionDelete, onLeafSectionDelete, onNarySectionResize,
  selectedDividerKey, onDividerSelect,
  onOpenOuterDialog, onOpenInnerDialog, mergeMode, onToggleMergeMode,
  prevCabinet, onInterCabinetResize,
  plateMaterial,
  lowDetail = false,
  measureMode = 'furniture',
  selectedShelfIdx = null,
  onSelectShelf = () => {},
  onInteriorHSplit, onInteriorVSplit,
  onInteriorSectionResize, onInteriorSectionDelete, onInteriorLeafSectionDelete, onInteriorNarySectionResize,
  onPromoteAtPanel,
  onDemoteAtPanel,
  panelFrontOffsets,
  onTogglePanelFront,
  doorGroupLabels,
  onSectionOptionSelect,
  showDoors = true,
  showDoorEdges = false,
  readOnly = false,
}) => {
  // Public "Žiūrėti 3D" product viewer: hover-glow/cursor feedback on panels and
  // dividers is driven purely by `activeStep` inside CabinetPanel/DividerDragHandle/
  // NarySectionDivider (isInteractive = activeStep === '...'), independent of
  // whether the click callbacks those components call actually do anything —
  // the earlier readOnly guard on handleShelfClick/handleDividerSelect (Builder3D.jsx)
  // stopped the EDITING, not this hover feedback. Passing a neutral activeStep only
  // to those specific components (never matching 'Baldas'/'Vidus'/'Išorė') turns the
  // hover off without touching this file's OWN activeStep-driven geometry decisions
  // (e.g. door visibility, which only checks "not Vidus" and stays correct either way).
  const hoverActiveStep = readOnly ? null : activeStep;
  const nominalCabWidth = Math.max(0.001, Number(cabinet.width) || 0);
  const cabHeight = Math.max(0.001, Number(cabinet.height) || 0);
  // /admin/bugs "Kai stogelis yra nuleistas, tai visos vidines pertvaros turi eiti
  // iki stogelio o ne kirtsti ji" — tipai 5/6/7 (Nuleistas / Nuleistas kairys/
  // dešinys, žr. UnifiedTopPanel.jsx) nuleidžia PAČIĄ plokštę topPanelHeight žemiau
  // nominalaus baldo viršaus (cabHeight), bet vidinė RecursiveSection medžio aukšto
  // riba (žemiau) apie tai nieko nežinojo — pertvaros (ir visas Vidaus turinys) ir
  // toliau siekdavo TIKRĄ cabHeight, vizualiai "pereidamos pro" nuleistą plokštę.
  // Tipai 8/9 (vienpusis overhang, BE nuleidimo) ir 4 (karūna, kyla AUKŠTYN, ne
  // žemyn) šios korekcijos nereikia — jų plokštė tebėra lygi su tikru viršumi.
  // Tipams 6/7 (asimetriškas nuleidimas) PATI Stogelio plokštė — vartotojas
  // patvirtino — IŠLIEKA VIENA ištisinė lenta VIENAME (nuleistame) lygyje PER VISĄ
  // ilgį (žr. UnifiedTopPanel.jsx — jokio laiptelio viduryje), tad topDrop TAIP PAT
  // pritaikomas visiems kabinetams vienodai, lygiai kaip tipui 5 (anksčiau čia
  // trumpam buvo bandyta taikyti topDrop TIK vienos pusės kabinetui, manant, kad
  // PATI plokštė yra sulaužyta į du skirtingus lygius — tai pasirodė neteisinga
  // interpretacija, žr. vartotojo paaiškinimą ir toliau esantį leftOuterFlushDrop/
  // rightOuterFlushDrop: asimetrija kyla iš TIKROS IŠORINĖS sienelės aukščio, ne iš
  // pačios plokštės ar vidinio turinio).
  const topDrop = (topPanelType === 5 || topPanelType === 6 || topPanelType === 7)
    ? Math.max(0, Number(topPanelHeight) || 0)
    : 0;
  // Tipams 6/7 VIENOS (flush) pusės TIKRA IŠORINĖ sienelė (korpuso kampo stulpelis,
  // ne vidinė pertvara tarp sujungtų kabinetų — tai jau tvarko leftWallInternalDrop
  // žemiau) sutrumpėja TIEK PAT, kiek nuleista plokštė, kad susilietų su ja LYGIAI,
  // be jokio tarpo/iškyšos — kita (notched) pusė lieka PILNO aukščio, ir būtent TAI
  // sukuria matomą "iškyšos" efektą ten (žr. UnifiedTopPanel.jsx tipų 6/7 komentarą).
  // 6 = "Kairys šonas": KAIRĖ pusė flush (jos sienelė trumpėja), DEŠINĖ notched
  // (sienelė lieka pilna). 7: veidrodis. Taikoma TIK TIKRAM korpuso kraštui
  // (isFirstInGroup / isLastInGroup||vienas kabinetas), NE vidinei sujungimo ribai —
  // vartotojas: "kaire arba desine sonine baldo plokste lieka aukstesne o kita
  // trumpesne sulyg dureliu auksciu".
  const leftOuterFlushDrop = (isFirstInGroup || cabinets.length === 1) && topPanelType === 6
    ? Math.max(0, Number(topPanelHeight) || 0) : 0;
  const rightOuterFlushDrop = (isLastInGroup || cabinets.length === 1) && topPanelType === 7
    ? Math.max(0, Number(topPanelHeight) || 0) : 0;
  // Anksčiau čia buvo naudojamas `cabinetDepth` prop, kurį FurnitureGroup.jsx gauna
  // KAIP VIENĄ REIKŠMĘ visai grupei (Builder3D.jsx: cabinetDepth={group.cabinets[0]?.depth}
  // — TIK PIRMO kabineto gylis) ir per {...cabinetProps} paskleidžia VIENODAI VISIEMS
  // grupės kabinetams — skirtingai nuo width/height aukščiau, kurie visada skaitomi iš
  // PAČIO `cabinet` objekto. Jei grupėje kabinetų gyliai skiriasi (arba tiesiog ne
  // pirmas kabinetas turi kitokį gylį), kiekvienas kabinetas klaidingai piešdavosi
  // pirmojo kabineto gyliu, o UnifiedBase.jsx plokštė (žr. FurnitureGroup.jsx totalDepth
  // — Math.max per VISUS group.cabinets) teisingai naudoja GILIAUSIO kabineto gylį —
  // tad plokštė galėjo atrodyti "issikišusi" giliau už kabinetą, kurio gylis buvo
  // klaidingai sutrumpintas iki pirmojo kabineto reikšmės (žr. "2.0 Baldas. Pagrindas...
  // Plokštė turėtų būti tokio paties gylio kaip ir baldas"). Pataisyta: kiekvienas
  // kabinetas dabar visada naudoja SAVO `cabinet.depth`, kaip ir width/height; senasis
  // `cabinetDepth` prop paliktas TIK kaip atsarginė reikšmė, jei `cabinet.depth` kada
  // nors nebūtų nustatytas.
  const safeDepth = Math.max(0.001, Number(cabinet.depth) || Number(cabinetDepth) || 0);
  const safeBasisHeight = Math.max(0.001, Number(basisHeight) || 0);
  const safeWallThickness = Math.max(0.001, Number(wallThickness) || 0.018);

  // Falšjuosta/falšfasadas/papildomas šonas (leftSideElementWidth/rightSideElementWidth,
  // tik isFirstInGroup/isLastInGroup kraštiniams korpusams) anksčiau siaurindavo TIK
  // dureles — pats korpusas (šoninės sienelės, vidinė struktūra) likdavo pilno pločio,
  // todėl "Rodyti dureles" išjungus persidengdavo su netikru (false) skydeliu, kurį
  // piešia FurnitureGroup.jsx renderSideElements. Dabar korpusas realiai susiaurėja
  // per šio elemento plotį, o visas turinys (sienelės, vidus, durelės) pastumiamas per
  // carcassOffsetL — false skydelis tada užpildo TIKRAI atlaisvintą, ne uždengtą tarpą.
  // totalWidth (FurnitureGroup.jsx) nesikeičia, nes ji skaičiuojama iš nominalaus
  // cabinet.width, kurio čia neliečiame — keičiasi tik šio komponento vidinis piešimas.
  const carcassOffsetL = isFirstInGroup ? leftSideElementWidth : 0;
  const carcassOffsetR = isLastInGroup ? rightSideElementWidth : 0;
  const cabWidth = Math.max(0.001, nominalCabWidth - carcassOffsetL - carcassOffsetR);

  // ANKSČIAU čia buvo BASIS_TYPE_SWAP_1234 = {1:2,2:1,3:4,4:3}, pridėtas kai vartotojas
  // pastebėjo, kad 3D rodo atvirkščiai pasirinkimui. TAČIAU nuo to laiko type===1/2/3/4
  // geometrijos blokai žemiau (visualBaseHeight, "Bottom wall", UnifiedBase.jsx front/
  // side dėžės ir t.t.) patys buvo perrašyti/patikslinti TIESIOGIAI pagal savo pačių
  // numerio referencinę ikoną (assets/img/icons/pagrindas/{1,2,3,4}.jpg — žr. ten esančius
  // "PERDARYTA"/"FIXED: confirmed against reference icon" komentarus prie kiekvieno
  // type===N bloko). Patikrinta tiesiogiai lyginant ikonas su kodu: icon 1.jpg (Padas)
  // rodo VIENĄ ištisą šoną iki grindų + priekinę juostą — TIKSLIAI tai, ką piešia
  // type===1 blokas; icon 2.jpg (Cokolis) rodo ATSKIRĄ dėžę (priekis+šonai), ant kurios
  // stovi korpusas, nesiekiantis grindų — TIKSLIAI tai, ką piešia type===2 blokas. Su
  // SWAP'u UI pasirinkimas "Cokolis" (raw=2) buvo nukreipiamas į type===1 turinį — vėl
  // RODĖ ATVIRKŠČIAI, tik priešinga kryptimi, nei originalus bug'as (vartotojas: "Cokolis
  // dabar blogai issivaizduoja... turi buti apatine dėžė ant kurios stovi korpusas").
  // SWAP dabar pašalintas — kiekvienas raw ID tiesiogiai atitinka savo NUMERIO turinį,
  // kuris jau savarankiškai patikrintas prieš savo ikoną.
  const basisType = rawBasisType;

  // basisType 5 ("Be pagrindo") — PAKEISTA (ClickUp "2.0 Baldas. Pagrindas. Be
  // pagrindo. Panaikinti apatinę lentyną. Ir leisti uždėti duris iki grindų"):
  // ankstesnis dizainas (žr. git istoriją) piešė pakeltą "Bottom wall" lentyną su
  // tarpu iki grindų apačioje, atkartojant senąją pagrindas/5.jpg ikoną — bet
  // vartotojas gyvai patikrino su durimis (Vidus žingsnyje pro permatomas duris
  // matėsi ta lentyna) ir aiškiai pažymėjo screenshot'e "Šitos lentynos nereikia",
  // paaiškindamas: "Abu šonai vientisi nuo grindų. Dugno nėra." — t.y. jokios
  // horizontalios plokštės apačioje, IR durys turi kabėti žemyn iki beveik grindų
  // ("tiesiog 1,5cm nuo grindų"), ne iki senojo 10cm tarpo viršaus. Tai jau atitinka
  // KITAS šio failo vietas, kurios NIEKADA nekeitė savo prielaidos, kad tipas 5
  // neturi jokios dugno plokštės (ConfigPanel.jsx "neededCount = basisType===5 ? 3
  // : 4", Plates.jsx praleidžia 'bottom' plokštės pasirinkimą, calculatePrice.js
  // niekada nekainuoja jokios plokštės šiam tipui) — 3D vaizdas dabar pagaliau
  // sutampa su tuo, kas jau buvo TIKRA visur kitur. 0.015 = vien tik durų
  // tarpo grindims (kabinama duris, ne konstrukcinė plokštė).
  // basisType 7/8 ("Plokštė Tarp šonų"/"Per Visą Plotį") — anksčiau visualBaseHeight čia
  // buvo TAS PATS pilnas vartotojo įvestas "Pagrindo aukštis" (safeBasisHeight), kaip ir
  // pjedestalo tipams (1-4), NORS fiziškai rezervuota tik PLONA plokštė (safeWallThickness,
  // žr. UnifiedBase.jsx type 7/8 — jos viršus visada tiksliai ties safeWallThickness).
  // Likusi dalis tarp plokštės viršaus ir visualBaseHeight likdavo TUŠČIA/nepanaudota —
  // durys (ir visa vidinė struktūra) prasidėdavo tik nuo visualBaseHeight, per aukštai,
  // paliekant nereikalingą tarpą virš plokštės. Vartotojas: "kai pasirenku plokste tarp
  // sonu arba per visa ploti, tada ir durys turi atitinkamai padideti, nes pagrindo
  // aukstis tampa tik pacios plokstes auksciu". Pataisyta: šiems dviem tipams
  // visualBaseHeight = safeWallThickness (tiksliai plokštės storis, ne pilnas įvestas
  // aukštis) — durys/vidinė struktūra dabar prasideda tiksliai nuo plokštės viršaus,
  // be tarpo, todėl automatiškai tampa aukštesnės (visos priklausomos formulės žemiau —
  // carcassHeight, bottomOffsetFor, sideHeight/sideCenterY, rootFloorExtend — jau
  // parametrizuotos per visualBaseHeight, tad korektiškai perskaičiuoja be papildomų
  // pakeitimų).
  const visualBaseHeight = basisType === 0 ? 0 : (basisType === 5 ? 0.015
    : ((basisType === 7 || basisType === 8) ? safeWallThickness : safeBasisHeight));
  const carcassHeight = Math.max(0.001, cabHeight - visualBaseHeight);
  // /admin/bugs "Pertvara pakilusi nuo grindų" / "Nugarėlė pakilusi nuo grindų" —
  // basisType 5 ("Be pagrindo") neturi JOKIO dugno (bottomPanelThickness=0, žr.
  // žemiau), o šonai jau TIKRAI siekia grindis (Y=0, žr. leftFloorReach/
  // rightFloorReach + jų -visualBaseHeight kompensaciją žemiau) — bet vidinis
  // turinys (pertvaros, RecursiveSection) ir nugarėlė VIS DAR naudojo
  // visualBaseHeight (1,5cm) kaip savo apatinę ribą, nors po jais NIEKO nėra, kas
  // tą tarpą pateisintų — kabėjo ore lygiai tiek, kiek matyti screenshot'uose.
  // DURYS TYČIA PALIEKAMOS nepaliestos — jos naudoja visualBaseHeight TIESIOGIAI
  // (žr. absY prie OuterDoors žemiau, ne šį kintamąjį ar RecursiveSection grupę),
  // nes anksčiau EXPLICITAI paprašyta durims kaboti iki 1,5cm tarpo (žr. "Bottom
  // wall" komentarą žemiau, ClickUp "leisti uždėti duris iki grindų").
  const contentFloorY = basisType === 5 ? 0 : visualBaseHeight;
  // basisType 5 ("Be pagrindo") DABAR turi normalų "Bottom wall" panelį (kabineto
  // spalvos, žr. aukščiau) — anksčiau jo neturėjo visai (žr. ClickUp "2.0 Baldas.
  // Pagrindas. Be pagrindo. Vidinės pertvaros lieka pakibusios ore" — ta klaida
  // liko svarbi TIK istoriškai, dabar bottomPanelThickness šiam tipui vėl normalus).
  // basisType 7 ("Plokštė Tarp šonų") ALSO omits the normal "Bottom wall" panel —
  // its own inset plate (žr. žemiau, "Bottom wall" komentaras) IS the cabinet's
  // bottom, just recessed into the base zone with its TOP SURFACE exactly at
  // visualBaseHeight (position = visualBaseHeight - wallThickness/2, thickness =
  // wallThickness ⇒ top = visualBaseHeight). Keeping the normal Bottom wall too
  // produced TWO stacked panels right on top of each other (žr. ClickUp — vartotojas
  // po pirmo fix'o: "pridėjai kažkokias papildomas plokštes apačioje" — pastebėjo
  // šį dubliavimą tiksliai tarp y=0.082–0.100 (naujoji plokštė) ir y=0.100–0.118
  // (senoji "Bottom wall"), patikrinta scenos matavimais).
  // basisType 8 ("Plokštė Per Visą Plotį") turi TĄ PATĮ dubliavimo bug'ą —
  // UnifiedBase.jsx type=8 dabar piešia savo PLONĄ (safeWallThickness) plokštę
  // TIESIOG ANT grindų (y=[0,wallThickness]), o šis "Bottom wall" toliau piešė
  // ANTRĄ, atskirą plokštę aukščiau (y≈[0.1,0.118]) — vartotojas pastebėjo per
  // "Vidus" žingsnį (permatomos durys atskleidžia vidų): "kodėl šiuo atveju
  // 'Plokštė (per visą plotį)' yra piešiamos DVI plokštės". Ta pati logika kaip
  // type 7: grupės lygio plokštė PATI yra kabineto dugnas, antro "Bottom wall"
  // nereikia.
  const bottomPanelThickness = (basisType === 5 || basisType === 7 || basisType === 8) ? 0 : safeWallThickness;

  // Side walls sit between the horizontal top and bottom plates — no corner overlap.
  // All basis types use safeWallThickness at both ends so no panel occupies the same
  // corner region as the top/bottom plates.
  // basisType 5 ("Be pagrindo") omits the bottom plate entirely (see "Bottom wall"
  // below) — so the side walls must reach all the way down to the real floor (y=0)
  // instead of leaving the usual wallThickness gap for a bottom plate that isn't
  // there. Without this, the cabinet visually "floats" by ~1.8cm even though its
  // floor clearance value correctly reads 0 (that value comes from the group's own
  // y position, not from where the side-wall geometry actually starts).
  // basisType 1 ("Padas") — REDESIGNED: sides now ALSO reach the real floor (y=0),
  // same as type 5, while visualBaseHeight/carcassHeight (and the "dugno plokštė"
  // bottom-wall panel below, which stays at visualBaseHeight, exactly like type 7's
  // between-sides plate) are left untouched — only the visible SIDE PANELS extend
  // further down, past the base zone. -visualBaseHeight cancels the "+visualBaseHeight"
  // term below, landing the side's bottom exactly at y=0 while its top stays where
  // it always was (see ClickUp "2.0 Baldas. Pagrindas. Padas. Abu šonai iki grindų.
  // Cokolis ir dugnas tarp šonų"). The matching front cokolis trim board (replacing
  // the old 4-wall pedestal box) is in UnifiedBase.jsx type 1.
  // basisType 3 ("Kairė iki grindų") / 4 ("Dešinė iki grindų") — only ONE side
  // reaches the floor, the other rests on the base like Cokolis (see reference
  // icons assets/img/icons/pagrindas/{3,4}.jpg: type 3 = LEFT open to the floor,
  // RIGHT on a recessed plinth board; type 4 = mirrored — see ClickUp "2.0 Baldas.
  // Pagrindas. Kairė/Dešinė iki grindų. Vienas šonas iki grindų. Kitas ant dugno.
  // Cokolis įgilintas (1-2cm)", and the matching UnifiedBase.jsx type 3/4 fix,
  // which had the same left/right swapped before this). "Left wall" below is the
  // true external left edge only for the first cabinet in a merged group — every
  // other cabinet's "left wall" is an internal divider between merged cabinets,
  // which must stay a normal shared partition regardless of basisType (a
  // floor-reaching gap in the middle of a merged run would look broken). "Right
  // wall" is already only rendered for the last cabinet in the group (see
  // isLastInGroup below), so it needs no extra guard here.
  // basisType 7 ("Plokštė Tarp šonų") — pagal referencinę ikoną
  // (assets/img/icons/pagrindas/7.jpg), TIK TIKRI IŠORINIAI grupės šonai (kraštinis
  // kairys/dešinys, žr. isFirstInGroup/isLastInGroup) turi siekti grindis (kaip Pado
  // basisType 1); plokštė (žr. UnifiedBase.jsx type 7 — piešiama VIENĄ KARTĄ visai
  // grupei, ne po kabinetą) yra ĮGILINTA tarp jų, pakelta virš grindų su matomu
  // tarpu — išoriniai šonai ją "apglėbia" iš abiejų pusių, o ne stovi ANT jos.
  // VIDINĖS sekcijų pertvaros (tarp sujungtų kabinetų šioje pačioje FurnitureGroup)
  // NETURI siekti grindų — jos turi būti trumpesnės, atsiremiančios lygiai į
  // plokštės viršų (žr. bottomOffsetFor žemiau, 0 vietoj safeWallThickness), kad
  // NEKIRSTŲ vientisos plokštės per pusę. (žr. ClickUp "2.0 Baldas. Pagrindas.
  // Plokštė (Tarp šonų). Padaryti dugno plokštę tarp šonų" — po dviejų ankstesnių
  // fix'ų vartotojas paaiškino: "apatinė plokštė neturėtų dalintis ir turi eiti
  // vienoje linijoje su šoninėmis plokštėmis... dabar labiausiai trukdo skyriaus
  // vidinės pertvaros, tai jos tiesiog turėtų būti truputį trumpesnės").
  // basisType 8 ("Plokštė Per Visą Plotį") — plokštė (UnifiedBase.jsx type 8) yra
  // PILNO PLOČIO, per VISĄ grupę, tad SKIRTINGAI nuo type 7, VISI šonai (tiek
  // išoriniai, tiek vidinės pertvaros tarp sujungtų kabinetų) turi ją pasiekti —
  // po visu grupės pločiu plokštė yra vienodai, nėra "vidinės"/"išorinės" zonos
  // skirtumo. Be šito šonai kabodavo ~10cm virš plonos (dabar safeWallThickness
  // storio) plokštės su tuščiu tarpu (žr. UnifiedBase.jsx type 8 komentarą apie
  // plokštės storio pataisymą).
  // basisType 5 ("Be pagrindo") — iš pradžių padaryta simetriška (kaip 1/8, floor-
  // reach VISIEMS kabinetams), bet vartotojas GYVAI patikrino su keliomis
  // sekcijomis ir paprašė: "vidinės pertvaros plokštės turi sutrumpėti ir eiti
  // tik iki tos apatinės pagrindo plokštės" — t.y. TA PATI asimetrija kaip type 7:
  // TIK TIKRI IŠORINIAI šonai (isFirstInGroup/isLastInGroup) siekia grindis,
  // VIDINĖS pertvaros tarp sujungtų kabinetų liktos trumpesnės, atsiremiančios
  // į pakeltos lentynos viršų (žr. bottomOffsetFor žemiau — type 5 KRENTA į
  // numatytąją safeWallThickness šaką, nes (skirtingai nuo 7/8) čia IŠLIEKA
  // normalus "Bottom wall" panelis kiekvienam kabinetui, tad pertvara paprasčiausiai
  // atsiremia ant JO viršaus, kaip ir kituose "normaliuose" basisType).
  // basisType 1 ("Padas") — PATAISYTA: anksčiau čia buvo besąlygiškai true
  // VISIEMS kabinetams, o klaidingas komentarAs (žr. git istoriją) teigė, kad
  // "Padas/Dešinė/Plokštė Per Visą Plotį 'Right wall' jau tik paskutiniam
  // kabinetui rodoma, papildomo guard'o nereikia" — bet, kaip ir type 7 atveju
  // žemiau, leftFloorReach/rightFloorReach VERTĖ naudojama NE TIK "Left/Right
  // wall" JSX sąlygai, bet IR sideHeight/leftWallHeight skaičiavimui KIEKVIENAM
  // kabinetui (žr. žemiau). Sujungtoje (keli kabinetai) grupėje tai reiškė, kad
  // VIDINIS dalijamasis pertvaros šonas (ne tikras grupės kraštas) TAIP PAT
  // "siekdavo grindis" kaip tikras karkaso šonas — pilno gylio, nerecessed, per
  // VISĄ pagrindo zoną — kai NAUJAS, įgilintas priekinis cokolio skydas
  // (UnifiedBase.jsx) jau NEBEBUVO tame pačiame Z plokštumoje, tad ta vidinė
  // pertvara vizualiai "išlįsdavo" priekyje kaip atskiras, nerecessed elementas
  // (žr. ClickUp "2.0 Baldas. Pagrindas. Padas/Cokolis/Kairė/Dešinė. Priekinės
  // lentos įgilinimas" — vartotojas: "vidurinė plokštė eina iki dugno plokštės,
  // Padas pagrindas šio atskyrimo neturėtų būti"). Dabar TA PATI isFirstInGroup/
  // cabinets.length===1 sąlyga, kaip jau teisingai taikoma 3/5/7 tipams.
  // basisType 5 ("Be pagrindo") PATAISYTA į besąlygišką (kaip 8) — žr. /admin/bugs
  // "Pertvara pakilusi nuo grindų": su senąja isFirstInGroup/cabinets.length===1
  // sąlyga, SUJUNGTO (kelių kabinetų) baldo VIDINĖ "Left wall" riba (ne tikras
  // korpuso kraštas) NEBŪDAVO floor-reaching, tad liko kaboti ant visualBaseHeight
  // (1,5cm) — patikrinta tiesiogiai scenos matavimu. "Be pagrindo" reiškia JOKIO
  // dugno NIEKUR šiame balde, ne tik prie tikrų išorinių kraštų — ta pati logika,
  // kuri jau pritaikyta basisType 8 (žr. jo komentarą žemiau).
  const leftFloorReach = (basisType === 1 && (isFirstInGroup || cabinets.length === 1)) || basisType === 8 || basisType === 5
    || (basisType === 7 && (isFirstInGroup || cabinets.length === 1))
    || (basisType === 3 && (isFirstInGroup || cabinets.length === 1));
  // rightFloorReach naudojamas NE TIK "Right wall" JSX sąlygai, bet IR kaip
  // sideHeight (žr. žemiau) — kuris savo ruožtu naudojamas KAIP MINIMUMAS
  // leftWallHeight (Math.max) skaičiavime KIEKVIENAM kabinetui, nepriklausomai,
  // ar jis paskutinis. basisType 1 ir 4 PATAISYTA ta pačia logika, kaip
  // leftFloorReach aukščiau (žr. ten komentarą) — basisType 8 LIEKA
  // besąlygiškas TYČIA (jo plokštė tikrai turi pasiekti VISUS šonus, tiek
  // išorinius, tiek vidines pertvaras, žr. komentarą prie UnifiedBase.jsx type 8).
  // basisType 5 besąlygiškas — žr. leftFloorReach komentarą aukščiau.
  const rightFloorReach = (basisType === 1 && (isLastInGroup || cabinets.length === 1)) || basisType === 8 || basisType === 5
    || (basisType === 4 && (isLastInGroup || cabinets.length === 1))
    || (basisType === 7 && (isLastInGroup || cabinets.length === 1));
  const bottomOffsetFor = (floorReach) => {
    if (floorReach) {
      // basisType 8: UnifiedBase.jsx grupės lygio plokštė yra PILNO PLOČIO
      // (safeTotalWidth, žr. UnifiedBase.jsx type 8) — jos X pločio zona FIZIŠKAI
      // apima IR tą vietą, kur stovi IŠORINIAI šonai (plokštė eina PO jais), tad
      // šonas, siekdamas tikrų grindų (y=0), PRASISKVERBIA per visą plokštės storį
      // — kontūrų peržiūroje matomas akivaizdus susikirtimas (žr. ClickUp "Baldo
      // plokštes niekada negali persikirsti viena su kita, tai yra jos turi
      // prisiglausti viena prie kitos"). Bandyta ir su tyčiniu persidengimu į
      // plokštės viršutinę pusę (safeWallThickness/2) — bet vartotojas GYVAI
      // patikrino su screenshot'u ir paaiškino, kad taip šonas vizualiai "užlipa
      // ant" plokštės (matomas laiptelis), o jis turi TIK "atsiremti" — t.y.
      // lygiai liestis, BE persidengimo. Pataisyta (galutinis variantas): šonas
      // sustoja LYGIAI ties plokštės viršumi (extend = visualBaseHeight -
      // safeWallThickness, nulinis persidengimas, nulinis tarpas).
      // basisType 7 (SKIRTINGAI nuo 8): UnifiedBase.jsx plokštė yra ĮGILINTA
      // (safeTotalWidth - 2×safeWallThickness) — jos X plotis NESIEKIA ten, kur
      // stovi IŠORINIAI šonai, plokštė yra TIK TARP jų. Todėl išoriniams šonams
      // ČIA NĖRA jokios plokštės po jais, į kurią atsiremti/su kuria susikirsti —
      // jie turi siekti TIKRAS grindis (y=0), kaip ir basisType 1/3/5. Klaidingai
      // pritaikius tą pačią "sustok ties plokštės viršumi" logiką TIPUI 7,
      // šonai sustodavo virš tikrų grindų BE JOKIOS PRIEŽASTIES (ten nėra plokštės,
      // su kuria susikirsti) — visas baldas vizualiai "kabojo ore" (žr. vartotojo
      // "Plokštė (tarp šonų)... baldas kabo ore, nes šoninės plokštės neliečia
      // žemės"). VIDINĖ pertvara (rootFloorExtend žemiau) IR toliau naudoja šią
      // "sustok ties plokštės viršumi" logiką abiem tipams (7 ir 8) — pertvara
      // VISADA stovi TARP šonų, ten, kur plokštė (įgilinta ar pilno pločio) TIKRAI
      // yra po ja.
      return basisType === 8
        ? -Math.max(0, visualBaseHeight - safeWallThickness)
        : -visualBaseHeight;
    }
    // basisType 5/7/8: nėra atskiro per-kabinetą "Bottom wall" (žr. bottomPanelThickness
    // aukščiau, = 0 šiems tipams) — pertvara (šis kabinetas sujungtoje grupėje NĖRA
    // nei pirmas, nei paskutinis, tad "floorReach"=false) turi atsiremti TIESIAI į
    // visualBaseHeight, be papildomo safeWallThickness tarpo, kurį kitiems tipams
    // užpildo jų pačių "Bottom wall" panelė.
    return (basisType === 5 || basisType === 7 || basisType === 8) ? 0 : safeWallThickness;
  };
  const leftBottomOffset = bottomOffsetFor(leftFloorReach);
  const rightBottomOffset = bottomOffsetFor(rightFloorReach);
  // ClickUp "2.1 Baldai. Stogelis (nuleistas). Abu šonai per visą baldo aukštį" —
  // `- safeWallThickness` čia normaliai palieka vietos horizontaliam viršaus skydui,
  // kuris SĖDI ANT šoninės sienelės (standartinis tipas 0 konstrukcijos būdas). Bet
  // "nuleisto" stogelio (5/6/7) atveju TIKRAS išorinis dešinys kraštas (ne vidinė
  // dviejų kabinetų riba — tą ir toliau trumpina topDrop/rightOuterFlushDrop žemiau)
  // TURI siekti PILNĄ carcassHeight, nes stogelis šiuo atveju yra ATSKIRAS, žemiau
  // pritvirtintas elementas, ne "dangtis", ant kurio sienelė atsiremia. Patvirtinta
  // vartotojo "Plokščių koordinatės" įrankiu: dešinio šono Y max = 248.2 (250-1.8),
  // nors norima 250.0 (TIKSLUS carcassHeight). ClickUp "2.1 Baldas. Stogelis (tarp
  // šonų). Šonines plokštes reikia paaukštinti 1.8cm" — TAS PATS trūkumas galioja
  // ir tipui 1 ("Tarp šonų"): jo danga irgi ĮLEISTA tarp šonų (ne sėdi ANT jų, kaip
  // tipas 2/8/9), tad šonai ten irgi turi siekti PILNĄ carcassHeight, ne sustoti
  // safeWallThickness anksčiau.
  const skipTopWallThicknessRight = (isLastInGroup || cabinets.length === 1)
    && (topPanelType === 1 || topPanelType === 5 || topPanelType === 6 || topPanelType === 7);
  const sideHeight = Math.max(0.001, carcassHeight - (skipTopWallThicknessRight ? 0 : safeWallThickness) - rightBottomOffset);

  // Šviesiai pilka pora — spalva, kurią mato vartotojas KOL kas nepasirinkta jokia
  // dekoro plokštė (taip pat naudojama Pado spalvai FurnitureGroup.jsx ir durelių
  // atsarginei spalvai OuterDoors.jsx — visos trys turi likti suderintos).
  // Tamsesnis atspalvis lieka aktyviam/pažymėtam baldui, šviesesnis — foniniam.
  // Atspalvių skirtumas padidintas (#bdbdbd → #9e9e9e) — ankstesnis buvo per silpnas,
  // kad pastebimai skirtųsi nuo neaktyvaus, tad vieninteliu aiškiai matomu "aktyvus
  // baldas" požymiu likdavo atsitiktinis mėlynas atskiro pertvaros paspaudimo
  // paryškinimas (žr. vartotojo "Aktyvus baldas dabar pasizymi melyna spalva ir tik
  // dalis jo, o reikia kad absoliuciai visos jo plokstes pakeistu spalva").
  const activeMat = cabinet.plateMaterial || plateMaterial;
  // PATAISYTA: karkaso plokštės VĖL rodo TIKRĄ pasirinktos dekoro plokštės spalvą
  // (activeMat.color) — anksčiau visada rodydavo bendrinę pilką placeholder spalvą
  // (žr. žemiau plateTexture komentarą), nepriklausomai nuo to, kokią spalvą/dekorą
  // vartotojas pasirinko durelėms. Tai sukurdavo nuolatinį, aiškiai matomą spalvos
  // kontrastą tarp karkaso ir tikra spalva rodomų durelių — vartotojas ilgai tyrė
  // šį kontrastą kaip tariamą "gylio šešėlį" (geometrija realiai buvo teisinga), kol
  // galiausiai aiškiai pasakė: "Karkasas, durelės ir visos kitos plokštes turi buti
  // vienodos spalvos". OuterDoors.jsx PATS savo durelių spalvą jau seniai skaičiuoja
  // IŠ TO PATIES activeMat.color lauko (žr. ten rawPlateColor/darkenColor) — ŠIS
  // pataisymas tiesiog priverčia karkasą naudoti TĄ PATĮ šaltinį, vietoj to, kad būtų
  // sukurta antra, nesutampanti "placeholder" spalvų sistema. isSelected (konkretaus
  // atskiro skydo pažymėjimas) IR TOLIAU rodomas pilka paryškinimo spalva — tai
  // momentinis, siauros apimties redagavimo signalas, ne spalvos pasirinkimo dalis.
  // furnitureActive (visas baldas yra "aktyvus" kelių baldų patalpoje) DAUGIAU
  // NEBEUŽDENGIA tikros spalvos — anksčiau tai reiškė, kad VISĄ redagavimo laiką
  // (praktiškai visada, redaguojant TĄ patį baldą) karkasas likdavo pilkas, kas ir
  // buvo faktinė šio pranešimo priežastis. Pilka "nepasirinkta dekoro plokštė"
  // atsarginė spalva lieka TIK tada, kai activeMat.color iš viso nėra tinkamas.
  const rawPlateColor = activeMat?.color;
  const validPlateColor = (rawPlateColor && typeof rawPlateColor === 'string' && (rawPlateColor.startsWith('#') || rawPlateColor.startsWith('rgb'))) ? rawPlateColor : null;
  const currentColor = isSelected ? '#9e9e9e' : (validPlateColor || (furnitureActive ? '#9e9e9e' : '#d6d6d6'));

  // Baldo plokštėms REALAUS tekstūros PAVEIKSLĖLIO (bitmap) nebetaikoma — tai lieka
  // nepakitę (žr. vartotojo "Baldo plokstems nereikia taikyti teksturu"), kad
  // išliktų apsauga nuo trūkstamų/klaidingų tekstūrų (404) ir sulėtėjimo — pakito
  // TIK bazinė SPALVA (aukščiau), ne tai, ar keliamas tikras tekstūros paveikslėlis.
  // CabinetPanel/kt. jau turi tvarkingą atsarginį kelią plateTexture===null atveju
  // (paprasta spalva + procedūrinis bumpMap paviršiaus reljefui).
  const plateTexture = null;
  const [selectedGhostInner, setSelectedGhostInner] = useState(null);

  useEffect(() => { setSelectedGhostInner(null); }, [cabinet.structureVersion]);

  const isLmdp = activeMat?.panelType !== 1;

  const prevCab = index > 0 ? cabinets[index - 1] : null;
  const prevCabHeight = prevCab ? Math.max(0.001, Number(prevCab.height) || 0) : cabHeight;
  // "cover taller neighbor" apatinė riba anksčiau čia buvo tiesiog `sideHeight` (šio
  // kabineto DEŠINĖS sienelės aukštis, pastatytas ant rightBottomOffset inkaro). Kol
  // leftBottomOffset === rightBottomOffset (visi kiti basisType, ir basisType 7/8
  // TIKRAI iškrantiniams kabinetams), abu inkarai sutampa ir tai buvo nekenksminga.
  // BET basisType 7 VIDINIAM kabinetui (ne pirmam, ne paskutiniam grupėje)
  // leftBottomOffset=0 (vidinė pertvara, atsiremia į plokštės viršų), o
  // rightBottomOffset=-visualBaseHeight (nes ŠIS kabinetas GALI būti paskutinis —
  // rightFloorReach skaičiuojamas neatsižvelgiant į tai) — DU SKIRTINGI inkarai. Math.max
  // tiesiog paėmus `sideHeight` (dešinės sienelės, ilgesnės dėl floor-reach) kaip apatinę
  // ribą KAIRĖS sienelės (trumpesnės, ne floor-reach) aukščiui, viršus pasislinkdavo
  // AUKŠČIAU už cabinet.height (patikrinta scenos matavimais: 2.532 vietoj 2.450 —
  // vartotojas: "kažkas susigadino su baldo ir pagrindo aukščiais"). Pataisyta:
  // savas, KAIRĖS PUSĖS inkaru (leftBottomOffset) paremtas "natūralus" aukštis, ta
  // pati formulė kaip sideHeight, bet su leftBottomOffset vietoj rightBottomOffset.
  // ClickUp "2.1 Baldai. Stogelis (nuleistas). Abu šonai per visą baldo aukštį" —
  // ta pati korekcija, kaip sideHeight (dešinė) aukščiau, veidrodinė kairei. Math.max
  // žemiau (leftWallHeightRaw) automatiškai perima šią didesnę reikšmę vidinės
  // (prevCabHeight) alternatyvos atžvilgiu, papildomai keisti nereikia.
  const skipTopWallThicknessLeft = (isFirstInGroup || cabinets.length === 1)
    && (topPanelType === 1 || topPanelType === 5 || topPanelType === 6 || topPanelType === 7);
  const leftSideNaturalHeight = Math.max(0.001, carcassHeight - (skipTopWallThicknessLeft ? 0 : safeWallThickness) - leftBottomOffset);
  const leftWallHeightRaw = Math.max(leftSideNaturalHeight, Math.max(0.001, prevCabHeight - visualBaseHeight - safeWallThickness - leftBottomOffset));
  // /admin/bugs "Stogelis nuleistas nesuveike, baldo vidiniu ploksciu aukstis turi
  // buti iki stogelio" — ankstesnis topDrop pataisymas sutrumpino TIK RecursiveSection
  // medžio (vidinių, VIENO kabineto pertvarų) aukštį, bet "Left wall" (žr. žemiau)
  // yra ATSKIRA CabinetPanel, naudojama kaip RIBA TARP DVIEJŲ SUJUNGTŲ kabinetų (ne
  // tikras korpuso kraštas — tikras kraštas yra Left wall TIK kai isFirstInGroup).
  // Tokia VIDINĖ riba elgėsi kaip tikras kraštas (visad pilno aukščio, su "ausimis"),
  // nors tikrasis kraštas ("ausys") turi likti TIK ant TIKRŲ korpuso šonų. Pataisyta:
  // vidinė (ne pirma grupėje) Left wall irgi trumpinama topDrop, lygiai kaip vidinės
  // pertvaros — "ausys" lieka tik ant tikro (isFirstInGroup) kairio korpuso krašto.
  const leftWallInternalDrop = (isFirstInGroup || cabinets.length === 1) ? 0 : topDrop;
  // leftOuterFlushDrop (žr. aukščiau) — TIK tipo 6 TIKRAM kairiam korpuso kraštui;
  // leftWallInternalDrop TIK vidinei (ne pirmai grupėje) ribai — abu niekada
  // netaikomi tam pačiam kabinetui vienu metu, saugu sudėti.
  const leftWallHeight = Math.max(0.001, leftWallHeightRaw - leftWallInternalDrop - leftOuterFlushDrop);
  const leftWallCenterY = visualBaseHeight + leftBottomOffset + leftWallHeight / 2;
  // rightOuterFlushDrop (žr. aukščiau) — TIK tipo 7 TIKRAM dešiniam korpuso kraštui
  // (Right wall JSX žemiau, piešiama TIK kai isLastInGroup||vienas kabinetas, t.y.
  // visada tikras kraštas — vidinės "Right wall" sampratos nėra, žr. "Left wall"
  // komentarą apie ribą tarp sujungtų kabinetų piešiamą KITO kabineto Left wall).
  const rightWallHeight = Math.max(0.001, sideHeight - rightOuterFlushDrop);
  const rightWallCenterY = visualBaseHeight + rightBottomOffset + rightWallHeight / 2;

  // Section heights derived from cabinet.shelves (used for ShelfInput overlays)
  const shelfSections = (() => {
    const innerBottom = visualBaseHeight + safeWallThickness;
    const innerTop = cabHeight - safeWallThickness;
    const sortedShelves = [...(cabinet.shelves || [])].sort((a, b) => Number(a.position[1]) - Number(b.position[1]));
    const sections = [];
    let prevY = innerBottom;
    sortedShelves.forEach((shelf, i) => {
      const shelfCenter = Number(shelf.position[1]);
      const secHeight = shelfCenter - safeWallThickness / 2 - prevY;
      if (secHeight > 0.001) sections.push({ index: i, centerY: prevY + secHeight / 2, height: secHeight });
      prevY = shelfCenter + safeWallThickness / 2;
    });
    const lastH = innerTop - prevY;
    if (lastH > 0.001) sections.push({ index: sortedShelves.length, centerY: prevY + lastH / 2, height: lastH });
    return sections;
  })();

  const sortedShelvesForScroll = [...(cabinet.shelves || [])].sort((a, b) => Number(a.position[1]) - Number(b.position[1]));

  // bordersDoor: true when this section/leaf has a door assigned — its depth
  // must recede by one wallThickness, exactly like CabinetPanel's own
  // bordersDoor/depthDelta handling above, so the shelf/rod/drawer doesn't
  // poke through the door's own plane. Previously this always used the raw,
  // un-reduced safeDepth regardless of doors — correct for the 4 carcass
  // boards and outer/inner dividers (which DO get reduced elsewhere), but NOT
  // for the actual internal components rendered here, which stayed at full
  // depth even once a door made the usable space shallower (žr. ClickUp "4.
  // Vidus. Kai uždedi duris... Vidinių pertvarų lentynų gylis nesumažėja").
  const renderInternalSectionByOpt = (opt, w, h, sectionIndexStr, isInner = false, colorOverride = null, isSelected = false, forceHeight = false, bordersDoor = false) => {
    if (!opt) return null;
    const color = colorOverride || currentColor;
    const effDepth = bordersDoor ? Math.max(0.001, safeDepth - safeWallThickness) : safeDepth;
    const props = { width: w, height: h, depth: effDepth, color, wallThickness: safeWallThickness, hasBackWall: !!backWall, highlightColor: colorOverride };
    if (activeStep === 'Vidus' && !isInner) {
      if ([4, 5, 6, 7].includes(opt)) {
        return (
          <group position={[w / 2, h / 2, effDepth / 2]}>
            <DrawerBackPanel {...props} />
          </group>
        );
      }
      return null;
    }
    const offsetKey = sectionIndexStr ? `${cabinet.furnitureId}-${index}-${sectionIndexStr}` : null;
    const offset = offsetKey ? (sectionComponentOffsets?.[offsetKey] || 0) : 0;
    const posYKey = sectionIndexStr ? `${cabinet.furnitureId}-${index}-${sectionIndexStr}-posY` : null;
    const positionOffset = posYKey ? (sectionComponentOffsets?.[posYKey] || 0) : 0;
    const onOffsetChange = (offsetKey && onSectionComponentOffsetChange)
      ? (newOffset) => onSectionComponentOffsetChange(cabinet.furnitureId, index, sectionIndexStr, newOffset)
      : null;
    const onPositionOffsetChange = (posYKey && onSectionComponentOffsetChange)
      ? (newOffset) => onSectionComponentOffsetChange(cabinet.furnitureId, index, sectionIndexStr + '-posY', newOffset)
      : null;
    // forceHeight=true means h already is the desired drawer height (set by user in array context)
    const drawerCustomHeight = forceHeight ? h : (offset > 0 ? offset : undefined);
    return (
      <group position={[w / 2, h / 2, effDepth / 2]}>
        {/* Green selection overlay — also acts as a front-face click target for thin geometry (hangers) */}
        <Box args={[w - 0.002, h - 0.002, 0.002]} position={[0, 0, effDepth / 2 - 0.005]}>
          <meshStandardMaterial color={isSelected ? '#3b82f6' : '#000'} transparent opacity={isSelected ? 0.15 : 0} depthWrite={false} />
        </Box>
        {opt === 1 && <ClothesRod {...props} />}
        {opt === 2 && <DoubleClothesRod {...props} />}
        {opt === 3 && <VerticalDivider {...props} />}
        {opt === 4 && <Drawer {...props} customHeight={drawerCustomHeight} positionOffsetY={positionOffset} onPositionOffsetChange={onPositionOffsetChange} onHeightChange={onOffsetChange} isSelected={isSelected} />}
        {opt === 5 && <SideDrawerBlock side="right" {...props} />}
        {opt === 6 && <SideDrawerBlock side="left" {...props} />}
        {opt === 7 && <DoubleDrawer {...props} />}
        {opt === 8 && <PullOutHanger {...props} />}
        {opt === 9 && <Pantograph {...props} />}
        {opt === 10 && <AdjustableShelf {...props} positionOffsetY={positionOffset} onPositionOffsetChange={forceHeight ? null : onPositionOffsetChange} isSelected={isSelected} />}
        {opt === 11 && <ShelfWithRails {...props} offset={offset} onOffsetChange={forceHeight ? null : onOffsetChange} isSelected={isSelected} />}
      </group>
    );
  };

  const renderInternalSectionArray = (items, w, sectionIndexStr, isInner, selectedItemIndex, onItemClick, sectionH, bordersDoor = false) => {
    // Stack items top-to-bottom: first item in array = top of section
    const totalH = items.reduce((s, it) => s + (it.height || 30) / 100, 0);
    let yTop = sectionH !== undefined ? sectionH : totalH;
    const sectionHasSelection = selectedItemIndex !== -1;
    const effDepth = bordersDoor ? Math.max(0.001, safeDepth - safeWallThickness) : safeDepth;
    return (
      <>
        {items.map((item, i) => {
          const itemH = (item.height || 30) / 100;
          yTop -= itemH;
          const yBase = yTop;
          const itemIsSelected = selectedItemIndex === i;
          return (
            <InternalItemGroup
              key={i}
              position={[0, yBase, 0]}
              w={w}
              itemH={itemH}
              depth={effDepth}
              isSelected={itemIsSelected}
              onItemClick={onItemClick ? () => onItemClick(i) : null}
              suppressHover={sectionHasSelection && !itemIsSelected}
              renderContent={(colorOverride) =>
                renderInternalSectionByOpt(item.type, w, itemH, `${sectionIndexStr}__item${i}`, isInner, colorOverride, false, true, bordersDoor)
              }
            />
          );
        })}
      </>
    );
  };

  const renderInternalSection = (path, w, h, bordersDoor = false) => {
    const optionKey = `${cabinet.furnitureId}-${index}-${path}`;
    const raw = sectionOptions[optionKey];
    if (Array.isArray(raw) && raw.length > 0) {
      return renderInternalSectionArray(raw, w, path, false, -1, null, h, bordersDoor);
    }
    return renderInternalSectionByOpt(raw || 0, w, h, path, false, null, false, false, bordersDoor);
  };

  const createInnerRenderFn = (outerPath) => (innerPath, w, h, bordersDoor = false) => {
    const sectionIndexStr = `INNER__${outerPath}__${innerPath}`;
    const key = `${cabinet.furnitureId}-${index}-${sectionIndexStr}`;
    const rawShelfIndex = selectedShelf?.shelfIndex || '';
    const itemPrefix = `${sectionIndexStr}__item`;
    const isItemPath = rawShelfIndex.startsWith(itemPrefix)
      && selectedShelf?.cabinetIndex === index
      && selectedShelf?.furnitureId === cabinet.furnitureId;
    const isComponentSelected = !isItemPath
      && rawShelfIndex === sectionIndexStr
      && selectedShelf?.cabinetIndex === index
      && selectedShelf?.furnitureId === cabinet.furnitureId;
    const selectedItemIndex = isItemPath
      ? parseInt(rawShelfIndex.slice(itemPrefix.length), 10)
      : -1;
    const raw = sectionOptions[key];
    if (Array.isArray(raw) && raw.length > 0) {
      const onItemClick = VIDUS_ITEM_3D_INTERACTION_DISABLED
        ? () => {
            // Mirror the established furnitureActive gating used elsewhere in this file
            // (e.g. the outer structure's onShelfClick below): the FIRST click on an
            // inactive cabinet only activates it — it must NOT also open the dialog in
            // the same click, since selection state hasn't settled yet at that point,
            // which was causing the dialog to sometimes open against stale/empty state
            // ("Sekcija tuščia") instead of the section that was actually clicked.
            if (!furnitureActive) {
              if (onSelect) onSelect(cabinet.furnitureId);
              return;
            }
            onShelfClick(cabinet.furnitureId, index, sectionIndexStr);
            if (onOpenInnerDialog) onOpenInnerDialog();
          }
        : (itemIdx) => {
            if (!furnitureActive && onSelect) onSelect(cabinet.furnitureId);
            onShelfClick(cabinet.furnitureId, index, `${sectionIndexStr}__item${itemIdx}`);
          };
      const onReorder = (VIDUS_ITEM_3D_INTERACTION_DISABLED || !onSectionOptionSelect) ? null : (newItems, newSelectedIdx) => {
        onSectionOptionSelect(cabinet.furnitureId, index, sectionIndexStr, newItems);
        onShelfClick(cabinet.furnitureId, index, `${sectionIndexStr}__item${newSelectedIdx}`);
      };
      return (
        <InternalSectionArray
          items={raw}
          w={w}
          safeDepth={bordersDoor ? Math.max(0.001, safeDepth - safeWallThickness) : safeDepth}
          sectionH={h}
          selectedItemIndex={selectedItemIndex}
          onItemClick={onItemClick}
          onReorder={onReorder}
          showDimensions={!!measureMode?.sectionHeights}
          renderItemContent={(i, item, itemH, colorOverride) =>
            renderInternalSectionByOpt(item.type, w, itemH, `${sectionIndexStr}__item${i}`, true, colorOverride, false, true, bordersDoor)
          }
        />
      );
    }
    return renderInternalSectionByOpt(raw || 0, w, h, sectionIndexStr, true, isComponentSelected ? '#3b82f6' : null, isComponentSelected, false, bordersDoor);
  };

  const rawColor = activeMat?.color;
  const plateColor = (rawColor && typeof rawColor === 'string' && (rawColor.startsWith('#') || rawColor.startsWith('rgb'))) ? rawColor : currentColor;
  const plateMat = <meshStandardMaterial color={currentColor} map={plateTexture || undefined} />;

  // ── Adjacency sets for Išorė +/− buttons ─────────────────────────────────
  // Only boundary sections of the group show − ; only sections touching the
  // group's outer edge show +. Pre-computed here to avoid traversing the tree
  // inside every recursive leaf.
  let doorGroupPlusPaths = null;
  let doorGroupMinusPaths = null;
  if (activeStep === 'Išorė' && selectedShelf && selectedShelf.cabinetIndex === index && selectedShelf.furnitureId === cabinet.furnitureId) {
    const fid = cabinet.furnitureId;
    const innerW = cabWidth - safeWallThickness * 2;
    // innerH TYČIA lieka topDrop-sutrumpintas (ta pati reikšmė, kaip RecursiveSection
    // šaknies height) — jis apibrėžia BOUNDS/ratio erdvę, kurioje tikrai stovi medžio
    // sekcijos; durų AUKŠTIS ties tikru viršumi dabar pratęsiamas ATSKIRAI (žr.
    // bExtendT/uExtendT topDrop papildymą žemiau), ne keičiant šį pagrindą — kitaip
    // visų (ne tik viršutinių) sekcijų ribos/proporcijos pasislinktų nuo tikros
    // medžio struktūros.
    const innerH = cabHeight - visualBaseHeight - safeWallThickness * 2 - topDrop;
    const selectedPath = selectedShelf.shelfIndex;
    const cabMerged = (mergedSections || []).filter(s => s.furnitureId === fid && s.cabinetIndex === index);
    const selectedEntry = cabMerged.find(s => s.sectionIndex === selectedPath || s.targetSectionIndex === selectedPath);
    const directKey = `${fid}-${index}-${selectedPath}`;
    const hasDoor = !!(doorColors?.[directKey]) || !!selectedEntry;
    if (hasDoor && selectedEntry) {
      const gid = selectedEntry.groupId;
      const groupPaths = new Set();
      cabMerged.filter(s => s.groupId === gid).forEach(s => { groupPaths.add(s.sectionIndex); groupPaths.add(s.targetSectionIndex); });
      let gMinX = Infinity, gMinY = Infinity, gMaxX = -Infinity, gMaxY = -Infinity;
      for (const p of groupPaths) {
        const b = getSectionBounds(cabinet.structure, p, innerW, innerH, safeWallThickness);
        if (!b) continue;
        if (b.x < gMinX) gMinX = b.x;
        if (b.y < gMinY) gMinY = b.y;
        if (b.x + b.width > gMaxX) gMaxX = b.x + b.width;
        if (b.y + b.height > gMaxY) gMaxY = b.y + b.height;
      }
      if (isFinite(gMinX)) {
        const EPS = 0.001;
        const WT = safeWallThickness;
        doorGroupPlusPaths = new Set();
        const boundaryMinusCandidates = new Set();
        for (const lp of getLeafPaths(cabinet.structure)) {
          if (lp === selectedPath) continue;
          const b = getSectionBounds(cabinet.structure, lp, innerW, innerH, safeWallThickness);
          if (!b) continue;
          if (groupPaths.has(lp)) {
            if (Math.abs(b.y - gMinY) < EPS || Math.abs(b.y + b.height - gMaxY) < EPS ||
                Math.abs(b.x - gMinX) < EPS || Math.abs(b.x + b.width - gMaxX) < EPS)
              boundaryMinusCandidates.add(lp);
          } else {
            if (Math.abs(b.y + b.height + WT - gMinY) < EPS ||
                Math.abs(b.y - gMaxY - WT) < EPS ||
                Math.abs(b.x + b.width + WT - gMinX) < EPS ||
                Math.abs(b.x - gMaxX - WT) < EPS)
              doorGroupPlusPaths.add(lp);
          }
        }
        // Keep only the boundary member farthest from the primary section
        // so the door shrinks one step at a time from the far end
        const pb = getSectionBounds(cabinet.structure, selectedPath, innerW, innerH, safeWallThickness);
        const pcx = pb ? pb.x + pb.width / 2 : 0;
        const pcy = pb ? pb.y + pb.height / 2 : 0;
        let maxDist = -1, farthestLp = null;
        for (const lp of boundaryMinusCandidates) {
          const b = getSectionBounds(cabinet.structure, lp, innerW, innerH, safeWallThickness);
          if (!b) continue;
          const dist = Math.hypot(b.x + b.width / 2 - pcx, b.y + b.height / 2 - pcy);
          if (dist > maxDist) { maxDist = dist; farthestLp = lp; }
        }
        doorGroupMinusPaths = farthestLp ? new Set([farthestLp]) : new Set();
      }
    }
  }

  // Main carcass boards (top/bottom/left/right) get the same "Ištraukta į priekį" toggle as
  // divider panels — keyed distinctly (`panel-*` / `outerpanel:...`) so they never collide
  // with structure-path-based divider keys. bordersDoor drives the auto-recess long-term fix
  // for contour/door z-fighting — see subtreeHasDoor/wallBordersDoor and CabinetPanel above.
  const panelInnerW = cabWidth - safeWallThickness * 2;
  const panelInnerH = cabHeight - visualBaseHeight - safeWallThickness * 2 - topDrop;
  const doorKeyPrefix = `${cabinet.furnitureId}-${index}-`;
  // Ta pati "bent viena šios spintos dureles → visos plokštės sugilėja" taisyklė,
  // kaip RecursiveSection cabinetHasAnyDoor aukščiau — čia taikoma pačioms karkaso
  // sienoms (viršus/apačia/šonai), ne tik toms, prie kurių durys tiesiogiai liečiasi.
  const cabinetHasAnyDoor = Object.keys(doorColors || {}).some((k) => k.startsWith(doorKeyPrefix) && doorColors[k]);
  const carcassPanelToggleProps = (id) => {
    const key = `${cabinet.furnitureId}-${index}-panel-${id}`;
    const dkey = `outerpanel:${cabinet.furnitureId}-${index}:${id}`;
    // "Bottom/left/right wall" (id !== 'top') vizualiai priklauso Pagrindo zonai — apatinis
    // skydas stovi tiesiogiai virš UnifiedBase.jsx priekinės juostos, o šoniniai skydai patys
    // NUSILEIDŽIA iki Pagrindo/grindų ribos (žr. leftFloorReach/rightFloorReach/bottomOffsetFor
    // aukščiau), tad jų apatinė dalis IR YRA matoma Pagrindo sritis. Vartotojas: "pridėjus arba
    // atėmus išorines dureles, kažkodėl keičiasi pagrindo išvaizdavimas, o jis turi būti visada
    // ir visais atvejais toks pats priklausomai kuris tipas pasirinktas". Pirmas bandymas fiksavo
    // TIK 'bottom' — nepakako, nes 'left'/'right' toliau įgilindavosi/negilindavosi priklausomai
    // nuo cabinetHasAnyDoor, todėl ties Pagrindo riba (kur šoninis skydas susiliečia su
    // UnifiedBase skirting dėže) atsirasdavo/dingdavo pastebimas Z-laiptelis, kai durys
    // pridedamos/pašalinamos BET KURIOJE spintos sekcijoje (net jei jos toli nuo tos vietos).
    // Tik 'top' (spintos viršus, ne Pagrindo zonoje) paliktas priklausyti nuo TIKRO durų
    // buvimo — jis niekada nesiliečia su Pagrindu, tad jo Z judėjimas nekeičia Pagrindo vaizdo.
    // PATAISYTA (apversta): anksčiau bottom/left/right bordersDoor buvo visada TRUE
    // (visada įgilinti), nepriklausomai, ar spinta turi dureles — tai stabilizavo
    // Pagrindo vaizdą (žr. aukščiau), bet ClickUp "2.0/3.0 Baldas. Karkaso gylis.
    // Pagrindai/Šonai turi būti 60cm (be durų)/Durų uždėjimo taisyklė" patvirtino,
    // kad vartotojas vis tiek norėjo SĄLYGIŠKO elgesio visoms keturioms pusėms (kaip
    // 'top'), TIK VIENAM KORPUSUI (ne visai grupei vienu metu), priėmus riziką, kad
    // grąžins Z-laiptelį. Šis bug'as dabar išvengiamas KITAIP: UnifiedBase.jsx
    // Cokolio juosta (FRONT_RECESS) sinchronizuota su `anyDoorInGroup` signalu (žr.
    // FurnitureGroup.jsx), tad judasi TA PATI linkme, kai durys pridedamos/nuimamos.
    const bordersDoor = cabinetHasAnyDoor || wallBordersDoor(cabinet.structure, id, panelInnerW, panelInnerH, safeWallThickness, doorColors, doorKeyPrefix);
    return {
      wallThickness: safeWallThickness,
      isFacade: panelFrontOffsets?.[key] === 'facade',
      bordersDoor,
      isSelected: selectedDividerKey === dkey,
      onSelectSelf: onDividerSelect ? () => onDividerSelect(selectedDividerKey === dkey ? null : dkey) : null,
      // ClickUp "3.0 Išorė. Karkaso gylis. Taisyklė, kai nusi'reset'ina žymėjimas
      // 'Ištraukti į priekį'" — "Nuėmus paskutines duris... NERODO pasirinkimo
      // 'ištraukti į priekį'" — mygtukas prasmingas TIK kai yra bent viena durelė
      // (bordersDoor=false → depthDelta visada 0, nepriklausomai nuo isFacade, tad
      // mygtukas neturėtų nieko veikti/rodytis).
      onToggleFront: (onTogglePanelFront && bordersDoor) ? () => onTogglePanelFront(cabinet.furnitureId, index, `panel-${id}`) : null,
      // 'top' neturi kaimyno tame pačiame aukštyje (žr. CabinetPanel edgeOnly komentarą) —
      // vartotojas: "nepavyksta pazymeti baldo virsutines plokstes" / paspaudus "nieko
      // neatsitinka". 'bottom' ("Apačia") DABAR IRGI edgeOnly=false — vartotojas norėjo,
      // kad BŪTENT Apačia (ne UnifiedBase.jsx Cokolis) būtų pagrindinis, visame paviršiuje
      // paspaudžiamas Pagrindo valdymo taškas ("turi buti pazymima ta plokste kur matosi
      // briauna... ne tik ta apatine plokste", žr. UnifiedBase.jsx frontElem komentarą —
      // Cokolis dabar tik seka ŠITĄ isFacade būseną, savo mygtuko/pažymėjimo nebeturi).
      // left/right palieka edgeOnly=true (default) — jie GALI turėti tikrą kaimyną
      // (gretimą kabinetą grupėje) toje pačioje vietoje.
      edgeOnly: id !== 'top' && id !== 'bottom',
    };
  };

  return (
    <group position={[carcassOffsetL, 0, 0]}>
      {/* Bottom wall — omitted basisType 5 ("Be pagrindo"), 7 ("Plokštė Tarp
          šonų") IR 8 ("Plokštė Per Visą Plotį"). 7/8 dugną piešia UnifiedBase.jsx
          VIENĄ KARTĄ visai FurnitureGroup (ne po vieną kiekvienam kabinetui) —
          žr. jo komentarą; laikyti šį "Bottom wall" ĮJUNGTĄ basisType 7/8 atveju
          reikštų antrą, persidengiantį dugną (žr. ClickUp "kodėl... 'Plokštė (per
          visą plotį)' yra piešiamos DVI plokštės"). basisType 5 anksčiau ČIA VĖL
          naudojo šį normalų Bottom wall (kabineto spalvos, atkartojant pasenusią
          pagrindas/5.jpg ikoną) — pašalinta pagal ClickUp "2.0 Baldas. Pagrindas.
          Be pagrindo. Panaikinti apatinę lentyną. Ir leisti uždėti duris iki
          grindų": abu šonai jau ir taip siekia grindis (leftFloorReach/
          rightFloorReach), dugno visai neturi būti, o durys turi kabėti žemyn
          iki visualBaseHeight (dabar 1,5cm) vietoj senojo 10cm tarpo viršaus. */}
      {basisType !== 5 && basisType !== 7 && basisType !== 8 && (() => {
        // "Baldo plokštes niekada negali persikirsti viena su kita" — kai šis kraštas
        // REALIAI siekia grindis (leftFloorReach/rightFloorReach), reali šoninė siena
        // (žr. žemiau "Left wall"/"Right wall") tęsiasi ištisai per VISĄ šią Y juostą
        // (kurioje stovi šis "Bottom wall"), tad pilno pločio "Bottom wall" fiziškai
        // PERSIDENGDAVO su jos tūriu prie krašto (ne tik prisiliesdavo). Right pusėje
        // tikrinama TIK tada, kai reali "Right wall" IŠ TIKRŲJŲ piešiama šiame
        // kabinete (isLastInGroup) — kitaip tai būtų vidinė jungtis su KITU kabinetu,
        // kurio geometriją čia neteisinga spėlioti. "Left wall" visada piešiama, tad
        // leftFloorReach čia tikrinamas be papildomos sąlygos.
        const bottomInsetL = leftFloorReach ? safeWallThickness : 0;
        const bottomInsetR = (rightFloorReach && (isLastInGroup || cabinets.length === 1)) ? safeWallThickness : 0;
        const bottomWidth = Math.max(0.001, cabWidth - bottomInsetL - bottomInsetR);
        const bottomX = bottomInsetL + bottomWidth / 2;
        // Apačia stovi karkaso plokštumoje — TAS PATS gylis (safeDepth), kaip Kairė/
        // Dešinė sienelės ir Viršus. Jos ir šonų bendrą -safeWallThickness poslinkį nuo
        // fasado (vietos durelėms) jau automatiškai atlieka CabinetPanel'io bordersDoor
        // mechanizmas (žr. carcassPanelToggleProps aukščiau — bottom/left/right visada
        // bordersDoor=true), tad čia JOKIO papildomo rankinio recesso nereikia — ankstesnis
        // bandymas rankomis papildomai įgilinti ŠITĄ plokštę (kad "sutaptų" su UnifiedBase.jsx
        // Pagrindo cokolio juosta) buvo klaidingas: cokolio juosta turi būti įgilinta VIENU
        // safeWallThickness GILIAU už Apačią (žr. UnifiedBase.jsx FRONT_RECESS), o ne tame
        // pačiame gylyje — vartotojas: "pagdindo cokolis yra visada 2cm labiau ilygilintas
        // nuo pagdindo plokstes... kitu gyliu negali buti".
        return (
          <CabinetPanel position={[bottomX, visualBaseHeight + safeWallThickness / 2, safeDepth / 2]} args={[bottomWidth, safeWallThickness, safeDepth]} color={currentColor} texture={plateTexture} activeStep={hoverActiveStep} castShadow receiveShadow showEdges={showDoorEdges} {...carcassPanelToggleProps('bottom')} />
        );
      })()}
      {/* Top wall — praleidžiama, kai Stogelis aktyvus (hideTopPanel): UnifiedTopPanel
          (FurnitureGroup.jsx) jau piešia VIENĄ dengiančią plokštę per visą grupę tokiu
          atveju, tad šis atskiras kiekvieno cabinet savo plokštės piešimas sukurtų
          antrą, nereikalingą plokštę toje pačioje vietoje. */}
      {!hideTopPanel && (
        <CabinetPanel position={[cabWidth / 2, cabHeight - safeWallThickness / 2, safeDepth / 2]} args={[cabWidth, safeWallThickness, safeDepth]} color={currentColor} texture={plateTexture} activeStep={hoverActiveStep} castShadow receiveShadow showEdges={showDoorEdges} {...carcassPanelToggleProps('top')} />
      )}
      {/* Left wall (extends to cover taller neighbor) */}
      <CabinetPanel position={[safeWallThickness / 2, leftWallCenterY, safeDepth / 2]} args={[safeWallThickness, leftWallHeight, safeDepth]} color={currentColor} texture={plateTexture} activeStep={hoverActiveStep} castShadow receiveShadow showEdges={showDoorEdges} {...carcassPanelToggleProps('left')} />
      {/* Right wall — only last cabinet in group */}
      {(isLastInGroup || cabinets.length === 1) && (
        <CabinetPanel position={[cabWidth - safeWallThickness / 2, rightWallCenterY, safeDepth / 2]} args={[safeWallThickness, rightWallHeight, safeDepth]} color={currentColor} texture={plateTexture} activeStep={hoverActiveStep} castShadow receiveShadow showEdges={showDoorEdges} {...carcassPanelToggleProps('right')} />
      )}

      {/* Recursive section tree — skip for pure shelf cabinets (shelves present, no split structure).
          The transparent click-box inside RecursiveSection causes depth-buffer artifacts for
          open-back (backWall:false) shelf units when a shelf board is deleted.
          EXCEPTION: onDemoteAtPanel (Išorė "paversti į vidinę") clears cabinet.structure.split
          back to a bare leaf, but leaves the real content in cabinet.interiorStructure — the
          ghost-divider "paversti į išorinę pertvarą" promote UI lives inside RecursiveSection's
          leaf branch (see ~2579 "Ghost inner structure in Išorė"). Without hasInteriorStructureContent,
          a cabinet that ALSO has legacy default shelves (buildDefaultCabinetSet) fell straight
          back into the pure-shelf branch below, hiding that ghost-divider entirely — a
          just-demoted divider became permanently unclickable and impossible to convert back to
          outer (vartotojas: "isorine karkasine plokste paverciant i vidine, nera galimybes
          paspausti ant jos ir ja vel paversti isorine"). */}
      {!lowDetail && !(cabinet.shelves?.length > 0 && !cabinet.structure?.split && !(cabinet.interiorStructure && Object.keys(cabinet.interiorStructure).length > 0)) && <group position={[safeWallThickness, contentFloorY + bottomPanelThickness, 0]}>
        <RecursiveSection
          key={cabinet.structureVersion || 0}
          node={cabinet.structure || {}}
          width={(isLastInGroup || cabinets.length === 1) ? cabWidth - safeWallThickness * 2 : cabWidth - safeWallThickness}
          height={cabHeight - contentFloorY - safeWallThickness - bottomPanelThickness - topDrop}
          depth={safeDepth}
          x={0} y={0}
          path="0"
          wallThickness={safeWallThickness}
          // "Vidinių pertvarų lentynų gylis nesumažėja" (žr. RecursiveSection
          // forceBordersDoor/cabinetHasAnyDoor komentarą) — ši, IŠORINIO (ne isInner)
          // medžio šaknis, anksčiau NEPERDUODAVO forceBordersDoor, tad tiesiogiai
          // sekcijoje esantys komponentai (renderInternalSectionByOpt effDepth, per
          // renderInternalComponent) žinojo TIK apie savo PAČIOS sekcijos dureles
          // (hasDoorAssigned), ne apie "bet kur spintoje yra durų" taisyklę, kurią
          // pertvaros/karkaso plokštės JAU teisingai taiko. Mišriame baldo (dalis
          // sekcijų su durimis, dalis — atvirų) atveju tai leisdavo atviros sekcijos
          // lentynai likti pilname gylyje, kyšant ~0,8cm pro jau įsigilinusią
          // (dėl cabinetHasAnyDoor) šoninę sienelę/pertvarą.
          forceBordersDoor={cabinetHasAnyDoor}
          // basisType 7/8: šio grupės local Y=0 atitinka pasaulio Y=visualBaseHeight (nes
          // bottomPanelThickness=0 šiems tipams, žr. aukščiau). Grupės-lygio plokštė ir
          // IŠORINIAI šonai dabar siekia pasaulio Y=0 (žr. leftFloorReach/rightFloorReach).
          // rootFloorExtend = kiek žemyn (local koordinatėmis) turi pratęsti ŠAKNINĘ
          // vertikalią pertvarą, kad jos apačia sutaptų su TA PAČIA riba, kaip ir
          // bottomOffsetFor(true) aukščiau naudoja IŠORINIAMS šonams — plokštės VIRŠUS
          // (safeWallThickness), be persidengimo (žr. bottomOffsetFor komentarą
          // aukščiau — persidengimo variantas vizualiai "užlipo ant" plokštės,
          // vartotojas paprašė tiesiog atsiremti, be persidengimo).
          rootFloorExtend={(basisType === 7 || basisType === 8) ? Math.max(0, visualBaseHeight - safeWallThickness) : 0}
          currentColor={currentColor}
          measureMode={measureMode}
          onShelfClick={(path) => {
            if (!furnitureActive) {
              if (onSelect) onSelect(cabinet.furnitureId);
            } else {
              onShelfClick(cabinet.furnitureId, index, path);
            }
          }}
          isSelected={(path) => selectedShelf && selectedShelf.shelfIndex === path && selectedShelf.cabinetIndex === index}
          activeStep={hoverActiveStep}
          splitSectionMode={splitSectionMode}
          onHorizontalSplit={(path) => onHorizontalSplit(cabinet.furnitureId, index, path)}
          onVerticalSplit={(path) => onVerticalSplit(cabinet.furnitureId, index, path)}
          renderInternalComponent={renderInternalSection}
          sectionOptions={sectionOptions}
          sectionKeyPrefix={`${cabinet.furnitureId}-${index}`}
          onSectionResize={(splitPath, newRatio) => {
            if (onSectionRatioChange) onSectionRatioChange(cabinet.furnitureId, index, splitPath, newRatio);
          }}
          onSectionDelete={(splitPath) => {
            if (onSectionDelete) onSectionDelete(cabinet.furnitureId, index, splitPath);
          }}
          onRootDimensionChange={(dim, valueMeters) => {
            if (dim === 'H') {
              if (onCabinetHeightChange) onCabinetHeightChange(valueMeters, cabinet.uniqueId);
            } else if (onCabinetWidthChange) {
              onCabinetWidthChange(cabinet.furnitureId, cabinet.uniqueId, valueMeters);
            }
          }}
          onLeafSectionDelete={onLeafSectionDelete ? (leafPath) => onLeafSectionDelete(cabinet.furnitureId, index, leafPath) : null}
          onNarySectionResize={onNarySectionResize ? (splitPath, newSizes) => onNarySectionResize(cabinet.furnitureId, index, splitPath, newSizes) : null}
          selectedDividerKey={selectedDividerKey}
          onDividerSelect={onDividerSelect}
          onOpenOuterDialog={onOpenOuterDialog}
          onOpenInnerDialog={onOpenInnerDialog}
          onRemoveDoor={(leafPath) => {
            const key = `${cabinet.furnitureId}-${index}-${leafPath}`;
            const cabMerged = (mergedSections || []).filter(s => s.furnitureId === cabinet.furnitureId && s.cabinetIndex === index);
            const mergedEntry = cabMerged.find(s => s.sectionIndex === leafPath || s.targetSectionIndex === leafPath);
            if (mergedEntry) {
              if (onRemoveDoors) onRemoveDoors(cabinet.furnitureId, index, mergedEntry.sectionIndex, mergedEntry.groupId);
            } else if (doorColors?.[key]) {
              if (onRemoveDoors) onRemoveDoors(cabinet.furnitureId, index, leafPath, `solo-${key}`);
            }
          }}
          mergedSectionsForCab={(mergedSections || []).filter(s => s.cabinetIndex === index && s.furnitureId === cabinet.furnitureId)}
          onMergeSection={(thisPath, thatPath) => {
            if (onCreateDoors) onCreateDoors(index, thisPath, index, thatPath);
          }}
          onUnmergeSection={(thisPath, groupId) => {
            if (onDetachSection) onDetachSection(cabinet.furnitureId, index, thisPath, groupId);
          }}
          selectedSectionPath={selectedShelf && selectedShelf.cabinetIndex === index ? selectedShelf.shelfIndex : null}
          mergeMode={mergeMode}
          onToggleMergeMode={onToggleMergeMode}
          doorColors={doorColors}
          doorGroupPlusPaths={doorGroupPlusPaths}
          doorGroupMinusPaths={doorGroupMinusPaths}
          interiorStructure={cabinet.interiorStructure || {}}
          isInnerSelected={(outerPath, innerPath) => {
            if (!selectedShelf || selectedShelf.cabinetIndex !== index || selectedShelf.furnitureId !== cabinet.furnitureId) return false;
            return selectedShelf.shelfIndex === `INNER__${outerPath}__${innerPath}`;
          }}
          onInnerShelfClick={(outerPath, innerPath) => onShelfClick(cabinet.furnitureId, index, `INNER__${outerPath}__${innerPath}`)}
          onInteriorHSplit={onInteriorHSplit ? (outerPath, innerPath) => onInteriorHSplit(cabinet.furnitureId, index, outerPath, innerPath) : null}
          onInteriorVSplit={onInteriorVSplit ? (outerPath, innerPath) => onInteriorVSplit(cabinet.furnitureId, index, outerPath, innerPath) : null}
          onInteriorSectionResize={onInteriorSectionResize ? (outerPath, innerPath, ratio) => onInteriorSectionResize(cabinet.furnitureId, index, outerPath, innerPath, ratio) : null}
          onInteriorSectionDelete={onInteriorSectionDelete ? (outerPath, innerSplitPath) => onInteriorSectionDelete(cabinet.furnitureId, index, outerPath, innerSplitPath) : null}
          onInteriorLeafSectionDelete={onInteriorLeafSectionDelete ? (outerPath, innerPath) => onInteriorLeafSectionDelete(cabinet.furnitureId, index, outerPath, innerPath) : null}
          onInteriorNarySectionResize={onInteriorNarySectionResize ? (outerPath, innerPath, sizes) => onInteriorNarySectionResize(cabinet.furnitureId, index, outerPath, innerPath, sizes) : null}
          onPromoteAtPanel={onPromoteAtPanel ? (outerPath, innerPathParts) => onPromoteAtPanel(cabinet.furnitureId, index, outerPath, innerPathParts) : null}
          onDemoteAtPanel={onDemoteAtPanel ? (outerPath) => onDemoteAtPanel(cabinet.furnitureId, index, outerPath) : null}
          panelFrontOffsets={panelFrontOffsets}
          onTogglePanelFront={onTogglePanelFront ? (outerPath) => onTogglePanelFront(cabinet.furnitureId, index, outerPath) : null}
          selectedGhostInner={selectedGhostInner}
          onGhostInnerSelect={setSelectedGhostInner}
          createInnerRenderFn={createInnerRenderFn}
          onClearSectionAtPath={onSectionOptionSelect ? (outerPath, innerPath) => {
            const sectionIndexStr = `INNER__${outerPath}__${innerPath}`;
            onSectionOptionSelect(cabinet.furnitureId, index, sectionIndexStr, 0);
          } : null}
          showEdges={showDoorEdges}
        />
      </group>}

      {/* Back wall — matches the karkaso plokštė decor like the other four structural
          panels, instead of being hardcoded white (it's the most visible surface of
          all when the furniture has no doors yet, so leaving it plain white was a
          big part of the "always looks grey/plain" complaint).
          Uses contentFloorY (not the shared carcassHeight — that stays untouched
          since it also drives the side-wall/drag-handle formulas, which already
          correctly reach the floor via their own -visualBaseHeight cancellation) so
          basisType 5 ("Be pagrindo") reaches the TRUE floor here too — žr.
          /admin/bugs "Nugarėlė pakilusi nuo grindų". */}
      {backWall && (() => {
        const backWallHeight = Math.max(0.001, cabHeight - contentFloorY);
        const backWallCenterY = contentFloorY + backWallHeight / 2;
        return (
          <>
            <Box position={[cabWidth / 2, backWallCenterY, safeWallThickness / 2]} args={[cabWidth, backWallHeight, safeWallThickness]}>
              <meshStandardMaterial color={plateTexture ? '#ffffff' : 'white'} map={plateTexture || undefined} roughness={0.34} metalness={0} envMapIntensity={0} />
            </Box>
            {showDoorEdges && <PanelEdges args={[cabWidth, backWallHeight, safeWallThickness]} position={[cabWidth / 2, backWallCenterY, safeWallThickness / 2]} />}
          </>
        );
      })()}

      {/* Horizontal shelves — drag to resize. Available in Baldas and Vidus steps. */}
      {(activeStep === 'Baldas' || activeStep === 'Vidus') && !lowDetail && <ShelfDragOverlay
        shelves={sortedShelvesForScroll}
        shelfSections={shelfSections}
        cabWidth={cabWidth}
        safeDepth={safeDepth}
        safeWallThickness={safeWallThickness}
        currentColor={currentColor}
        activeStep={activeStep}
        onShelfHeightChange={onShelfHeightChange ? (idx, h) => onShelfHeightChange(cabinet.furnitureId, index, idx, h) : null}
        onShelfDelete={onShelfDelete ? (idx) => onShelfDelete(cabinet.furnitureId, index, idx) : null}
        visualBaseHeight={visualBaseHeight}
        hasRightWall={isLastInGroup || cabinets.length === 1}
        selectedIdx={selectedShelfIdx}
        onSelectShelf={onSelectShelf}
        showEdges={showDoorEdges}
      />}

      {/* Ghost shelf plates — visible in Išorė for visual reference only, lighter color, non-interactive */}
      {activeStep === 'Išorė' && !lowDetail && sortedShelvesForScroll.map((shelf, i) => {
        const shelfCenterY = Number(shelf.position[1]);
        const ghostW = (isLastInGroup || cabinets.length === 1)
          ? cabWidth - safeWallThickness * 2
          : cabWidth - safeWallThickness;
        const ghostDepth = safeDepth - safeWallThickness;
        const ghostArgs = [ghostW, safeWallThickness, ghostDepth];
        const ghostPos = [safeWallThickness + ghostW / 2, shelfCenterY, safeWallThickness + ghostDepth / 2];
        return (
          <React.Fragment key={`ghost-shelf-${i}`}>
            <Box args={ghostArgs} position={ghostPos}>
              <meshStandardMaterial color="#b0bec5" transparent opacity={0.55} />
            </Box>
            {showDoorEdges && <PanelEdges args={ghostArgs} position={ghostPos} />}
          </React.Fragment>
        );
      })}

      {/* Drag handles — Baldas step, active furniture only (matches "Baldo matmenys ir
          laisvumo tarpeliai rodomi tik pažymėto baldo" — dimensions/handles for the
          selected furniture piece only, not every piece in the room). */}
      {activeStep === 'Baldas' && measureMode?.furniture && furnitureActive && (
        <>
          {/* Drag handle at top edge for stepped-wardrobe height drag */}
          <CabinetTopDragHandle
            cabWidth={cabWidth}
            cabHeight={cabHeight}
            safeDepth={safeDepth}
            onHeightChange={(newH) => { if (onCabinetHeightChange) onCabinetHeightChange(newH, cabinet.uniqueId); }}
          />
          {/* Drag handle on right wall for width resize */}
          {(isLastInGroup || cabinets.length === 1) && (
            <CabinetRightDragHandle
              cabWidth={cabWidth}
              cabHeight={cabHeight}
              safeDepth={safeDepth}
              safeWallThickness={safeWallThickness}
              visualBaseHeight={visualBaseHeight}
              onWidthChange={(newW) => { if (onCabinetWidthChange) onCabinetWidthChange(cabinet.furnitureId, cabinet.uniqueId, newW + carcassOffsetR); }}
            />
          )}
        </>
      )}

      {/* Drag handle on left wall to resize this + previous cabinet — Baldas and Išorė */}
      {(activeStep === 'Baldas' || activeStep === 'Išorė') && !isFirstInGroup && prevCabinet && onInterCabinetResize && (
        <InterCabinetDragHandle
          prevCabinet={prevCabinet}
          cabinet={cabinet}
          cabHeight={cabHeight}
          safeDepth={safeDepth}
          safeWallThickness={safeWallThickness}
          visualBaseHeight={visualBaseHeight}
          onInterCabinetResize={onInterCabinetResize}
        />
      )}

      {/* Doors — shown in all steps except Vidus, controlled by showDoors toggle in Išorė */}
      {activeStep !== 'Vidus' && (activeStep !== 'Išorė' || showDoors) && doorColors && (() => {
        const fid = cabinet.furnitureId;
        const innerW = cabWidth - safeWallThickness * 2;
        // innerH TYČIA lieka topDrop-sutrumpintas (žr. identišką komentarą prie
        // doorGroupPlusPaths aukščiau) — apibrėžia BOUNDS/ratio erdvę, kurioje tikrai
        // stovi medžio sekcijos (ratio-splits/proporcijos turi likti suderintos su
        // RecursiveSection šaknies height). Durų aukštis ties tikru viršumi dabar
        // pratęsiamas ATSKIRAI per bExtendT/uExtendT (žr. žemiau) — ne čia.
        const innerH = cabHeight - visualBaseHeight - safeWallThickness * 2 - topDrop;
        // s.sectionIndex !== s.targetSectionIndex atmeta SENUS, savireferentinius
        // "sujungimo" įrašus (targetSectionIndex === sectionIndex) — anksčiau
        // handleDoorSelection (Layout.jsx) tokį įrašą sukurdavo KIEKVIENAM durelių
        // priskyrimui, net vienai, niekada nesujungtai sekcijai, nukreipdamas ją į
        // "tik skaitymui" sujungtos grupės piešimo kelią žemiau. Šis filtras leidžia
        // TOKIOMS (jau išsaugotoms) sekcijoms grįžti į įprastą, redaguojamą "solo"
        // durelių kelią (žr. Object.entries(doorColors).forEach toliau), be jokio
        // poreikio keisti likusią šio bloko logiką.
        const cabMerged = (mergedSections || []).filter(s => s.furnitureId === fid && s.cabinetIndex === index && s.sectionIndex !== s.targetSectionIndex);
        const mergedPathSet = new Set([...cabMerged.map(s => s.sectionIndex), ...cabMerged.map(s => s.targetSectionIndex)]);
        const groupIds = [...new Set(cabMerged.map(s => s.groupId))];
        const doors = [];

        // ATSTATYTA (2-as bandymas, vartotojo patvirtinta žinant riziką) — žr. pilną
        // komentarą prie uExtendL/R/B/T žemiau: tikros IŠORINĖS karkaso plokštės
        // (bottom/top/left/right) ištraukimas į fasadą DABAR VĖL siaurina durelių
        // plotį/aukštį ta kryptimi, skaitant TĄ PATĮ (sinchronizuotą) panelFrontOffsets
        // raktą, kurį CabinetPanel'io carcassPanelToggleProps naudoja — bendra tarp
        // uExtendL/R/B/T (sujungtų durelių keliui) ir bExtendL/R/B/T (solo keliui).
        const outerPanelFacade = (id) => panelFrontOffsets?.[`${fid}-${index}-panel-${id}`] === 'facade';

        groupIds.forEach(gid => {
          // Collect all paths in this merge group
          const groupEntries = cabMerged.filter(s => s.groupId === gid);
          if (!groupEntries.length) return;
          const allPaths = new Set();
          groupEntries.forEach(s => { allPaths.add(s.sectionIndex); allPaths.add(s.targetSectionIndex); });

          // Union bounding box across all sections in the group
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const p of allPaths) {
            const b = getSectionBounds(cabinet.structure, p, innerW, innerH, safeWallThickness);
            if (!b) continue;
            if (b.x < minX) minX = b.x;
            if (b.y < minY) minY = b.y;
            if (b.x + b.width > maxX) maxX = b.x + b.width;
            if (b.y + b.height > maxY) maxY = b.y + b.height;
          }
          if (!isFinite(minX)) return;
          const unionBounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };

          const primaryPath = groupEntries[0].sectionIndex;
          const doorKey = `${fid}-${index}-${primaryPath}`;
          const doorOption = doorColors[doorKey] || 0;
          if (!doorOption) return;
          // Extend door to cover outer cabinet walls and adjacent dividers (half-wall on inner edges).
          // Falšjuosta/falšfasadas/papildomas šonas is now accounted for by narrowing the
          // CARCASS itself (cabWidth/carcassOffsetL — see top of component), so the door
          // needs only the normal wall-thickness extend on every edge, same as an edge
          // with no side element at all — no separate pull-back here anymore.
          const uAtLeft = unionBounds.x < 0.001;
          const uAtRight = unionBounds.x + unionBounds.width > innerW - 0.001;
          const uAtBottom = unionBounds.y < 0.001;
          const uAtTop = unionBounds.y + unionBounds.height > innerH - 0.001;
          // Ant VIDINIŲ kraštų (ribojasi su kitu lapu, ne su karkaso siena) anksčiau čia
          // VISADA buvo fiksuotas safeWallThickness/2, net kai besiribojantis daliklis
          // pats pažymėtas "Ištraukta į priekį" — tada daliklis tampa matomas TIK per PUSĘ
          // savo pločio (iki savo paties vidurio), nes durelės vis tiek dengia antrą pusę
          // (vartotojas: "dureles gretimo skyriaus atsiremtu i ta plokste o ne butu iki
          // vidurio"). Žemiau (solo durelių kelias, žr. bExtendL/R/B/T) ŠIS pataisymas jau
          // seniai padarytas per innerDoorFacadeExtend (ClickUp "3.0 Išorė... case 2" —
          // "Ištraukti į priekį" veikia, bet durelės viską uždengia), tiesiog niekada
          // nebuvo perkeltas į ŠITĄ (sujungtų/grupės) durelių kelią. Sujungta grupė gali
          // apimti kelis lapus — konkretaus krašto (kairė/dešinė/apačia/viršus) kaimynas
          // visada priklauso TAM lapui iš allPaths, kurio savos ribos TIKSLIAI sutampa su
          // unionBounds tuo kraštu, ne bet kuriam atsitiktiniam grupės nariui.
          const findBoundaryPath = (matchesEdge) => {
            for (const p of allPaths) {
              const b = getSectionBounds(cabinet.structure, p, innerW, innerH, safeWallThickness);
              if (b && matchesEdge(b)) return p;
            }
            return primaryPath;
          };
          // ClickUp "3.0 Išorė. Durelių matmenys... Durelės turi būti platesnės ir
          // aukštesnės" + "...'Ištraukus į priekį'" — PATAISYTA (2 raundas, rastas
          // vartotojo antru screenshot'u su matoma dviguba linija prie VIDINIO daliklio):
          // TRUE outer (bAtLeft/R/B/T) kraštas DABAR grąžina TĄ PATĮ wallThickness, kurį
          // jau grąžina innerDoorFacadeExtend savo `!found` šakoje (žr. jos komentarą —
          // "PILNAS wallThickness, ne pusė") — anksčiau čia grąžindavo 0 (facade atveju
          // -wallThickness), o trūkstamas "pilnas uždengimas" buvo bandomas pridėti ATSKIRU,
          // BESĄLYGIŠKU +safeWallThickness*2 priedu prie viso bDoorW/bDoorH (žr. žemiau) —
          // kas teisingai veikė TIK kai ABU kraštai buvo outer (vienas neskaidytas baldas),
          // bet PER DAUG pridėdavo bet kuriam VIDINIAM (daliklio) kraštui, kuris jau pats
          // teisingai gauna savo wallThickness/2 iš innerDoorFacadeExtend — durelė tada
          // persidengdavo su kaimynine per papildomą, nepageidaujamą wallThickness. Dabar
          // kiekvienas kraštas savarankiškai neša TEISINGĄ savo reikšmę (outer=wallThickness/
          // 0 priklausomai nuo facade, inner=wallThickness/2/0), tad jokio papildomo
          // bendro priedo prie bDoorW/bDoorH daugiau NEBEREIKIA.
          const uExtendL = uAtLeft ? (outerPanelFacade('left') ? 0 : safeWallThickness)
            : innerDoorFacadeExtend(cabinet.structure, findBoundaryPath(b => Math.abs(b.x - unionBounds.x) < 0.001), 'v', 'prev', innerW, innerH, safeWallThickness, `${fid}-${index}`, panelFrontOffsets);
          const uExtendR = uAtRight ? (outerPanelFacade('right') ? 0 : safeWallThickness)
            : innerDoorFacadeExtend(cabinet.structure, findBoundaryPath(b => Math.abs((b.x + b.width) - (unionBounds.x + unionBounds.width)) < 0.001), 'v', 'next', innerW, innerH, safeWallThickness, `${fid}-${index}`, panelFrontOffsets);
          const uExtendB = uAtBottom ? (outerPanelFacade('bottom') ? 0 : safeWallThickness)
            : innerDoorFacadeExtend(cabinet.structure, findBoundaryPath(b => Math.abs(b.y - unionBounds.y) < 0.001), 'h', 'next', innerW, innerH, safeWallThickness, `${fid}-${index}`, panelFrontOffsets);
          // + topDrop (0 visiems tipams, išskyrus 5/6/7) — ClickUp "Stogelis
          // (nuleistas)... Durys iki pat viršaus": TIKRAS viršutinis kraštas (uAtTop)
          // dabar papildomai pratęsiamas per topDrop, kad durelės siektų TIKRĄ baldo
          // viršų (kaip šonai), o ne sustotų ties nuleisto stogelio/vidinės
          // struktūros riba, palikdamos matomą plyšį/"ausis" virš savęs.
          const uExtendT = uAtTop ? (outerPanelFacade('top') ? 0 : safeWallThickness) + topDrop
            : innerDoorFacadeExtend(cabinet.structure, findBoundaryPath(b => Math.abs((b.y + b.height) - (unionBounds.y + unionBounds.height)) < 0.001), 'h', 'prev', innerW, innerH, safeWallThickness, `${fid}-${index}`, panelFrontOffsets);
          // Apsauga: jei falšfasado/papildomo šono plotis (senų, prieš SideElementDialog
          // ribojimą išsaugotų duomenų atveju) būtų didesnis už pačią sekciją, durelės
          // negali nukristi iki 0/neigiamo pločio — geometrijos ribos vietoje sulūžusios Box.
          const uDoorW = Math.max(0.05, unionBounds.width + uExtendL + uExtendR);
          const uDoorH = Math.max(0.05, unionBounds.height + uExtendB + uExtendT);
          const absX = safeWallThickness + unionBounds.x + unionBounds.width / 2 + (uExtendR - uExtendL) / 2;
          const absY = visualBaseHeight + safeWallThickness + unionBounds.y + unionBounds.height / 2 + (uExtendT - uExtendB) / 2;
          // "Griovelio šešėlis" (GROOVE_SHADOW, OuterDoors.jsx) simuliuoja tarpą tarp dviejų
          // NEPRIKLAUSOMŲ durelių grupių, besiribojančių per ĮGILINTĄ (nematomą, durelių
          // dengiamą) daliklį — jis TYČIA stovi truputį PRIEŠ durelių paviršių (žr.
          // GROOVE_SHADOW_Z_OFFSET). Kai besiribojantis daliklis PATS pažymėtas "Ištraukta
          // į priekį" (tas pats patikrinimas, kaip aukščiau — ties VIDINIU kraštu
          // innerDoorFacadeExtend grąžina TIKSLIAI 0 TIK isFacade atveju, kitaip
          // safeWallThickness/2, niekada 0, tad extend≈0 čia reiškia TIK isFacade), jis jau
          // TIKRAS, matomas, savo paties z gylyje (su savo polygonOffset apsauga) —
          // simuliuotas šešėlis tada stovėtų priešais TIKRĄ, jau matomą plokštę, kaip
          // nereikalinga tamsi juosta (vartotojas: "jos turi atsiremti i istraukta plokste,
          // ir kaip gali buti seselis, jeigu ir dureles ir plokste yra 60cm gylio?").
          // edgeGap=0 TUO PAČIU metu (a) nebepiešia šio šešėlio ir (b) leidžia matomam durelių
          // paviršiui siekti tiksliai iki daliklio krašto, be jokio (vos 0,75mm) atitraukimo.
          const uEdgeGap = {
            left: (uAtLeft || uExtendL <= FACADE_EXTEND_EPS) ? 0 : DOOR_EDGE_GAP,
            right: (uAtRight || uExtendR <= FACADE_EXTEND_EPS) ? 0 : DOOR_EDGE_GAP,
            bottom: (uAtBottom || uExtendB <= FACADE_EXTEND_EPS) ? 0 : DOOR_EDGE_GAP,
            top: (uAtTop || uExtendT <= FACADE_EXTEND_EPS) ? 0 : DOOR_EDGE_GAP,
          };
          doors.push(
            <OuterDoors
              key={gid} groupId={gid} posX={absX} doorCenterY={absY}
              doorHeight={uDoorH} doorOption={doorOption}
              handleType={doorHandles ? (doorHandles[doorKey] || 0) : 0}
              cabinet={{ ...cabinet, width: uDoorW, depth: safeDepth }}
              wallThickness={safeWallThickness}
              onClick={(e) => { e.stopPropagation(); if (activeStep === 'Baldas') { if (onSelect) onSelect(cabinet.furnitureId); } else onShelfClick(cabinet.furnitureId, index, primaryPath); }}
              isSelected={activeStep !== 'Baldas' && selectedShelf && selectedShelf.shelfIndex === primaryPath && selectedShelf.cabinetIndex === index}
              // Vartotojas: "3D vaizde visi esantys baldo elementai turi buti tos pacios
              // plokstes, tos pacios spalvos be galimybes keisti" — durelės PRIVALO visada
              // rodyti TĄ PAČIĄ plokštę kaip karkasas (cabinet.plateMaterial/plateMaterial,
              // ta pati formulė kaip activeMat CabinetPanel'e aukščiau), nepriklausomai nuo
              // bet kokio senesnio/liekamo cabinet.doorPlateMaterial įrašo — žr. ir
              // PlatesFillingsAndHandles.jsx bei Plates.jsx, kur pačios UI galimybės
              // pasirinkti kitą durų/pusės plokštę taip pat pašalintos.
              selectedPlate={cabinet.plateMaterial || plateMaterial}
              fallbackColor={currentColor}
              activeStep={activeStep}
              showEdges={showDoorEdges}
              edgeGap={uEdgeGap}
            />
          );
          // Group number ("1.1", "2.1", ...) — matches the row in the "Plokštės, filingai
          // ir rankenėlės" side menu so it's clear which door is which.
          if (activeStep === 'Išorė' && doorGroupLabels?.[gid]) {
            doors.push(
              <group key={`gl-${gid}`} position={[absX - uDoorW / 2 + 0.06, absY + uDoorH / 2 - 0.06, safeDepth + 0.03]}>
                <Html center zIndexRange={[175, 0]} style={{ pointerEvents: 'none' }}>
                  <div style={{ color: '#000', fontSize: 9, fontWeight: 400, whiteSpace: 'nowrap' }}>{doorGroupLabels[gid].text}</div>
                </Html>
              </group>
            );
          }
          // Facade width/height badge — centered on the facade itself (both axes),
          // not offset above/beside it.
          if (activeStep === 'Išorė' && (measureMode?.facadeWidths || measureMode?.facadeHeights)) {
            doors.push(
              <group key={`fdim-${gid}`} position={[absX, absY, safeDepth + 0.02]}>
                <Html center zIndexRange={[175, 0]}>
                  {measureMode?.facadeWidths
                    ? <ClickInput value={uDoorW} label="P" editable={false} bgColor="#ffffff" textColor="#374151" />
                    : <ClickInput value={uDoorH} label="A" editable={false} bgColor="#111827" textColor="#ffffff" />}
                </Html>
              </group>
            );
          }
        });

        Object.entries(doorColors).forEach(([key, doorOption]) => {
          if (!doorOption) return;
          const prefix = `${fid}-${index}-`;
          if (!key.startsWith(prefix)) return;
          const path = key.slice(prefix.length);
          if (mergedPathSet.has(path)) return;
          const bounds = getSectionBounds(cabinet.structure, path, innerW, innerH, safeWallThickness);
          if (!bounds) return;
          // Extend door to cover outer cabinet walls and adjacent dividers (half-wall on inner edges)
          const bAtLeft = bounds.x < 0.001;
          const bAtRight = bounds.x + bounds.width > innerW - 0.001;
          const bAtBottom = bounds.y < 0.001;
          const bAtTop = bounds.y + bounds.height > innerH - 0.001;
          // See the analogous uExtendL/uExtendR comment above (outerPanelFacade) — true
          // outer edges now return the SAME wallThickness/0 pair innerDoorFacadeExtend's
          // own `!found` branch documents (full coverage when not facade, 0 when the
          // panel itself is flush), read from the SAME panelFrontOffsets key
          // CabinetPanel's own isFacade/depthDelta uses — no separate/stale flag
          // involved, so the old asymmetric-gap bug can't recur here.
          // On INNER edges (bordering another leaf, not the cabinet wall), the extend
          // doubles from half a wallThickness to a full wallThickness when the bordering
          // divider itself is pulled to facade — otherwise the divider would protrude
          // forward but still sit flush against both doors, invisible between them (see
          // ClickUp case 2: "Vidinės karkaso plokštės - Abi besiribojančios durelės
          // sumažėja per 0,9cm, o plokštė išlenda į priekį ir matoma").
          const innerSectionKeyPrefix = `${fid}-${index}`;
          const bExtendL = bAtLeft ? (outerPanelFacade('left') ? 0 : safeWallThickness)
            : innerDoorFacadeExtend(cabinet.structure, path, 'v', 'prev', innerW, innerH, safeWallThickness, innerSectionKeyPrefix, panelFrontOffsets);
          const bExtendR = bAtRight ? (outerPanelFacade('right') ? 0 : safeWallThickness)
            : innerDoorFacadeExtend(cabinet.structure, path, 'v', 'next', innerW, innerH, safeWallThickness, innerSectionKeyPrefix, panelFrontOffsets);
          const bExtendB = bAtBottom ? (outerPanelFacade('bottom') ? 0 : safeWallThickness)
            : innerDoorFacadeExtend(cabinet.structure, path, 'h', 'next', innerW, innerH, safeWallThickness, innerSectionKeyPrefix, panelFrontOffsets);
          // + topDrop — žr. analogišką uExtendT komentarą aukščiau.
          const bExtendT = bAtTop ? (outerPanelFacade('top') ? 0 : safeWallThickness) + topDrop
            : innerDoorFacadeExtend(cabinet.structure, path, 'h', 'prev', innerW, innerH, safeWallThickness, innerSectionKeyPrefix, panelFrontOffsets);
          // Ta pati apsauga kaip uDoorW/uDoorH aukščiau.
          const bDoorW = Math.max(0.05, bounds.width + bExtendL + bExtendR);
          const bDoorH = Math.max(0.05, bounds.height + bExtendB + bExtendT);
          const absX = safeWallThickness + bounds.x + bounds.width / 2 + (bExtendR - bExtendL) / 2;
          const absY = visualBaseHeight + safeWallThickness + bounds.y + bounds.height / 2 + (bExtendT - bExtendB) / 2;
          // Žr. analogišką uEdgeGap komentarą aukščiau — tas pats "nerodyti simuliuoto
          // griovelio šešėlio, kai besiribojantis daliklis pats jau matomas facade gylyje" atvejis.
          const bEdgeGap = {
            left: (bAtLeft || bExtendL <= FACADE_EXTEND_EPS) ? 0 : DOOR_EDGE_GAP,
            right: (bAtRight || bExtendR <= FACADE_EXTEND_EPS) ? 0 : DOOR_EDGE_GAP,
            bottom: (bAtBottom || bExtendB <= FACADE_EXTEND_EPS) ? 0 : DOOR_EDGE_GAP,
            top: (bAtTop || bExtendT <= FACADE_EXTEND_EPS) ? 0 : DOOR_EDGE_GAP,
          };
          doors.push(
            <OuterDoors
              key={`solo-${key}`} groupId={`solo-${key}`} posX={absX} doorCenterY={absY}
              doorHeight={bDoorH} doorOption={doorOption}
              handleType={doorHandles ? (doorHandles[key] || 0) : 0}
              cabinet={{ ...cabinet, width: bDoorW, depth: safeDepth }}
              wallThickness={safeWallThickness}
              onClick={(e) => { e.stopPropagation(); if (activeStep === 'Baldas') { if (onSelect) onSelect(cabinet.furnitureId); } else onShelfClick(cabinet.furnitureId, index, path); }}
              isSelected={activeStep !== 'Baldas' && selectedShelf && selectedShelf.shelfIndex === path && selectedShelf.cabinetIndex === index}
              selectedPlate={cabinet.plateMaterial || plateMaterial}
              fallbackColor={currentColor}
              activeStep={activeStep}
              showEdges={showDoorEdges}
              edgeGap={bEdgeGap}
            />
          );
          // Group number ("1.1", "2.1", ...) for solo (never-merged) doors too — same synthetic
          // key format doorGroupLabels/PlatesFillingsAndHandles use for a solo door's own group.
          if (activeStep === 'Išorė' && doorGroupLabels?.[key]) {
            doors.push(
              <group key={`solo-gl-${key}`} position={[absX - bDoorW / 2 + 0.06, absY + bDoorH / 2 - 0.06, safeDepth + 0.03]}>
                <Html center zIndexRange={[175, 0]} style={{ pointerEvents: 'none' }}>
                  <div style={{ color: '#000', fontSize: 9, fontWeight: 400, whiteSpace: 'nowrap' }}>{doorGroupLabels[key].text}</div>
                </Html>
              </group>
            );
          }
          if (activeStep === 'Išorė' && measureMode?.facadeWidths) {
            doors.push(
              <group key={`solo-fw-${key}`} position={[absX, absY, safeDepth + 0.02]}>
                <Html center zIndexRange={[175, 0]}>
                  <ClickInput
                    value={bDoorW} label="P" editable bgColor="#ffffff" textColor="#374151"
                    onCommit={(newValMeters) => commitFacadeSectionResize(
                      cabinet.structure, path, 'v', bDoorW, newValMeters, innerW, innerH, safeWallThickness,
                      (ancestorPath, ratio) => onSectionRatioChange && onSectionRatioChange(cabinet.furnitureId, index, ancestorPath, ratio),
                      (ancestorPath, sizes) => onNarySectionResize && onNarySectionResize(cabinet.furnitureId, index, ancestorPath, sizes),
                    )}
                  />
                </Html>
              </group>
            );
          }
          if (activeStep === 'Išorė' && measureMode?.facadeHeights) {
            doors.push(
              <group key={`solo-fh-${key}`} position={[absX, absY, safeDepth + 0.02]}>
                <Html center zIndexRange={[175, 0]}>
                  <ClickInput
                    value={bDoorH} label="A" editable bgColor="#111827" textColor="#ffffff"
                    onCommit={(newValMeters) => commitFacadeSectionResize(
                      cabinet.structure, path, 'h', bDoorH, newValMeters, innerW, innerH, safeWallThickness,
                      (ancestorPath, ratio) => onSectionRatioChange && onSectionRatioChange(cabinet.furnitureId, index, ancestorPath, ratio),
                      (ancestorPath, sizes) => onNarySectionResize && onNarySectionResize(cabinet.furnitureId, index, ancestorPath, sizes),
                    )}
                  />
                </Html>
              </group>
            );
          }
        });

        return doors;
      })()}
    </group>
  );
};

// OverlayBtn/overlayWrapStyle also reused by UnifiedBase.jsx — same "Ištraukti į
// priekį (fasadų plokštuma)" / "Grąžinti į karkaso plokštumą" toggle button now
// applies to Pagrindas front trim boards too (žr. UnifiedBase.jsx type 1/2/3/4).
export { PanelEdges, OverlayBtn, overlayWrapStyle };
export default SingleCabinet3D;
